import { useState, useEffect, useCallback, useMemo, useRef, memo } from "react";
import { ADS_MOBILE } from "../components/AdShortcode";
import SuperDealsManager from "../components/SuperDealsManager";
import LatestUpdatesManager from "../components/LatestUpdatesManager";
import HomeBannerManager from "../components/HomeBannerManager";
import SEO from "../components/SEO";
import seoData from "../components/seoData";
import { API_BASE_PATH, fetchPosts } from "../api/posts";
import {
  readBookmarkedPostIds,
  removeBookmarkedPost,
  upsertBookmarkedPost,
  writeBookmarkedPostIds,
} from "../bookmarks";
import { getPostImageData } from "../utils/postMedia";
import yesevaFont from "/assets/fonts/YesevaOne-Regular-DOzKZiG_.ttf";

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const WA_BASE = "https://wa.me/918438506813";
const WA_ENQUIRY_BASE = "https://wa.me/919962610113";
const SITE_URL = "https://travel.rethinkways.com";
const IMAGE_BASE = "/assets/images/tour-packages/tour-packages/";
const WP_TAGS_API = `${API_BASE_PATH}/tags`;
const DEFAULT_TERMS = "Prices may vary based on travel dates & availability";
const DEFAULT_INCLUDES = ["Food", "Transfer", "Stay", "Site Seeing"];
const DEFAULT_CITIES = ["Bangalore", "Mumbai", "Kerala", "Delhi", "Chennai", "Pune", "Kolkata", "Hyderabad", "Ahmedabad"];
const DEFAULT_ORIGIN_CITY = "Chennai";
const SEO_META_UPDATED_EVENT = "trw-tour-seo-meta-updated";
const TOUR_PACKAGES_UPDATED_EVENT = "trw-tour-packages-updated";
const TOUR_PACKAGES_LISTING_PATH = "/tour-packages";
const SEO_TITLE_LIMIT = 50;
const SEO_DESCRIPTION_LIMIT = 150;
const TOUR_FORM_DRAFTS_STORAGE_KEY = "trw-tour-form-drafts-v1";
const TOUR_PACKAGES_LISTING_ALIASES = new Set([
  TOUR_PACKAGES_LISTING_PATH,
  "/travel-tour-packages",
]);
const DESTINATION_DROPDOWN_ADD_VALUE = "__trw_add_destination__";
const SECTIONS = [
  { code: "international", title: "International Getaways" },
  { code: "india",         title: "India Getaways" },
  { code: "europe",        title: "Europe Getaways" },
];

// ─── STORAGE KEYS ─────────────────────────────────────────────────────────────
const STORAGE = {
  TOUR_BOOKMARKS:  "trw-tour-package-bookmarks-v1",
  BLOG_BOOKMARKS:  "trw-destination-blog-bookmarks-v1",
  ORIGIN_CITY:     "trw-origin-city-v1",
  DEST_PAGES:      "trw-destination-pages-v1",
  CUSTOM_PACKAGES: "trw-custom-tour-packages-v1",
  SEED_PACKAGE_OVERRIDES: "trw-seed-tour-package-overrides-v1",
  DELETED_SEED_PACKAGES: "trw-deleted-seed-tour-package-ids-v1",
  TOUR_SEO_META: "trw-tour-seo-meta-v1",
};

const MAX_AVIF_UPLOAD_SIZE = 1 * 1024 * 1024;
const DEFAULT_WA_NUMBER = WA_BASE.replace("https://wa.me/", "");
const TOUR_PACKAGES_BASE_CANONICAL = "https://travel.rethinkways.com/tour-packages";
const BUILD_ID = typeof __TRW_BUILD_ID__ !== "undefined" ? __TRW_BUILD_ID__ : "";

const withAssetCacheBust = (url) => {
  const raw = String(url || "").trim();
  if (!raw) return raw;
  if (!BUILD_ID) return raw;
  // Cache-bust only local Vite-public assets.
  if (!raw.startsWith("/assets/") && !raw.startsWith("assets/")) return raw;
  const joiner = raw.includes("?") ? "&" : "?";
  return `${raw}${joiner}v=${encodeURIComponent(BUILD_ID)}`;
};

const YesevaHeadingStyle = { fontFamily: '"TRW Yeseva One", "Yeseva One", Georgia, serif' };
const YesevaFontCss = `
  @font-face {
    font-family: "TRW Yeseva One";
    src: url("${yesevaFont}") format("truetype");
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }
`;

let yesevaFontInjected = false;
const destinationTagRequestCache = new Map();
const destinationArticlesCache = new Map();
const destinationArticlesDataCache = new Map();
const injectYesevaFont = () => {
  if (yesevaFontInjected || typeof document === "undefined") return;
  const style = document.createElement("style");
  style.id = "trw-tour-packages-yeseva-font";
  style.textContent = YesevaFontCss;
  document.head.appendChild(style);
  yesevaFontInjected = true;
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const slugify = (v = "") =>
  String(v).trim().toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const displayDestinationName = (name) => (name === "Jammu and Kashmir" ? "Kashmir" : name);
const DEFAULT_VIEW_DETAILS_ENABLED_SLUGS = new Set(["andaman"]);
const getDefaultShowViewDetails = (value = "") => DEFAULT_VIEW_DETAILS_ENABLED_SLUGS.has(slugify(value || ""));

const showViewDetailsForDestination = (name) => {
  const normalized = displayDestinationName(name);
  const normalizedSlug = slugify(normalized || "");
  if (!normalizedSlug) return false;
  const matchedPackage = getAllMiddlePackages().find((pkg) => (
    slugify(pkg.destinationSlug || "") === normalizedSlug
    || slugify(displayDestinationName(pkg.destination || "")) === normalizedSlug
  ));
  return typeof matchedPackage?.showViewDetails === "boolean"
    ? matchedPackage.showViewDetails
    : getDefaultShowViewDetails(normalizedSlug);
};

const getMiddlePackageWhatsappMessage = (dest) =>
  `Hi Travel Rethink Ways, I want to plan the ${dest} package.`;

const getDetailPackageWhatsappMessage = (title) =>
  `Hi, I'm interested in the ${title || "travel"} package. Please share the full details.`;

const buildWhatsAppLink = (message, number = DEFAULT_WA_NUMBER) => {
  const text = String(message || "").trim();
  const normalizedNumber = String(number || "").replace(/\D/g, "") || DEFAULT_WA_NUMBER;
  const base = `https://wa.me/${normalizedNumber}`;
  return text ? `${base}?text=${encodeURIComponent(text)}` : base;
};

const extractWhatsAppMessage = (value, fallback = "") => {
  const raw = String(value || "").trim();
  if (!raw) return fallback;
  try {
    const parsed = new URL(raw, SITE_URL);
    const text = parsed.searchParams.get("text");
    return text ? text.trim() : fallback;
  } catch {
    const [, textPart = ""] = raw.split("?text=");
    if (!textPart) return fallback;
    try {
      return decodeURIComponent(textPart).trim() || fallback;
    } catch {
      return textPart.trim() || fallback;
    }
  }
};

const extractWhatsAppNumber = (value, fallback = DEFAULT_WA_NUMBER) => {
  const raw = String(value || "").trim();
  if (!raw) return fallback;
  const match = raw.match(/wa\.me\/(\d+)/i);
  return match?.[1] || fallback;
};

const waLink = (dest) => buildWhatsAppLink(getMiddlePackageWhatsappMessage(dest));

const readTourSeoMetaOverrides = () => readLS(STORAGE.TOUR_SEO_META, {});
const readObjectLS = (key, fallback = {}) => {
  try {
    const value = localStorage.getItem(key);
    const parsed = value ? JSON.parse(value) : fallback;
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
};
const readTourFormDrafts = () => readObjectLS(TOUR_FORM_DRAFTS_STORAGE_KEY, {});
const writeTourFormDrafts = (value) => writeLS(TOUR_FORM_DRAFTS_STORAGE_KEY, value);
const writeTourSeoMetaOverrides = (value) => {
  writeLS(STORAGE.TOUR_SEO_META, value);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(SEO_META_UPDATED_EVENT));
    window.dispatchEvent(new CustomEvent(TOUR_PACKAGES_UPDATED_EVENT));
  }
  saveTourPackagesToRemote();
};

const getFallbackDestinationSeo = (destinationName, slug) => {
  const label = displayDestinationName(destinationName || slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()));
  return {
    title: `${label} Tour Packages & Holidays | Travel Rethink Ways`,
    description: `Explore ${label} tour packages, holiday escapes, curated adventures, and travel experiences with Travel Rethink Ways.`,
    canonical: `${SITE_URL}/${encodeURIComponent(slug)}-tour-packages`,
  };
};

const getBaseSiteSeoEditorEntries = () => {
  const webStoryTitle = seoData.webStory?.title || "Travel Web Stories & Visual Stories | Travel Rethink Ways";
  const webStoryDescription = seoData.webStory?.description || "Explore visual travel stories, destination highlights, and quick travel inspiration from Travel Rethink Ways.";
  const webStoryCanonical = seoData.webStory?.canonical || "https://travel.rethinkways.com/visual-stories";

  return [
    { key: "site:home", label: "Home", title: seoData.home.title, description: seoData.home.description, canonical: seoData.home.canonical },
    { key: "site:superDeals", label: "Super Deals", title: seoData.superDeals.title, description: seoData.superDeals.description, canonical: seoData.superDeals.canonical },
    { key: "site:about", label: "About Us", title: seoData.about.title, description: seoData.about.description, canonical: seoData.about.canonical },
    { key: "site:contact", label: "Contact Us", title: seoData.contact.title, description: seoData.contact.description, canonical: seoData.contact.canonical },
    { key: "site:privacyPolicy", label: "Privacy Policy", title: seoData.privacyPolicy.title, description: seoData.privacyPolicy.description, canonical: seoData.privacyPolicy.canonical },
    { key: "site:termsConditions", label: "Terms & Conditions", title: seoData.termsConditions.title, description: seoData.termsConditions.description, canonical: seoData.termsConditions.canonical },
    { key: "site:disclaimer", label: "Disclaimer", title: seoData.disclaimer.title, description: seoData.disclaimer.description, canonical: seoData.disclaimer.canonical },
    { key: "site:affiliate", label: "Affiliate", title: seoData.affiliate.title, description: seoData.affiliate.description, canonical: seoData.affiliate.canonical },
    { key: "site:latestUpdates", label: "Latest Updates", title: seoData.latestUpdates.title, description: seoData.latestUpdates.description, canonical: seoData.latestUpdates.canonical },
    { key: "site:webStory", label: "Web Story", title: webStoryTitle, description: webStoryDescription, canonical: webStoryCanonical },
    { key: "site:wishlist", label: "Wishlist", title: seoData.wishlist.title, description: seoData.wishlist.description, canonical: seoData.wishlist.canonical },
  ];
};

const DESTINATION_ROUTE_SUFFIX = "-tour-packages";
const DESTINATION_ROUTE_PATTERNS = [
  new RegExp(`^${TOUR_PACKAGES_LISTING_PATH}/([a-z0-9-]+)${DESTINATION_ROUTE_SUFFIX}/?$`, "i"),
  new RegExp(`^/([a-z0-9-]+)${DESTINATION_ROUTE_SUFFIX}/?$`, "i"),
];

const normalizeRoutePathname = (pathname = "") => {
  const rawPath = String(pathname || "").trim() || "/";
  const baseUrl = typeof import.meta !== "undefined" ? (import.meta.env.BASE_URL || "/") : "/";
  const normalizedBase = baseUrl === "/" ? "/" : `/${String(baseUrl).replace(/^\/+|\/+$/g, "")}/`;
  if (normalizedBase !== "/" && rawPath.startsWith(normalizedBase)) {
    const trimmed = rawPath.slice(normalizedBase.length - 1);
    return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  }
  return rawPath;
};

const isLocalHostLike = () => {
  if (typeof window === "undefined") return false;
  const host = String(window.location.hostname || "").toLowerCase();
  return host === "localhost" || host === "127.0.0.1";
};

const getBaseRoutePrefix = () => {
  const baseUrl = typeof import.meta !== "undefined" ? (import.meta.env.BASE_URL || "/") : "/";
  if (baseUrl === "/") return "";
  return `/${String(baseUrl).replace(/^\/+|\/+$/g, "")}`;
};

const getPublicRoutePrefix = () => (isLocalHostLike() ? getBaseRoutePrefix() : "");

const isTourPackagesListingPath = (pathname = "") => {
  const normalizedPath = normalizeRoutePathname(pathname).replace(/\/+$/, "") || "/";
  return TOUR_PACKAGES_LISTING_ALIASES.has(normalizedPath);
};

const buildDestinationRoutePath = (slug) =>
  `${getPublicRoutePrefix()}/${encodeURIComponent(slug)}${DESTINATION_ROUTE_SUFFIX}`;
const tourLink = (slug, fromCity = "") => {
  const path = buildDestinationRoutePath(slug);
  const normalizedFrom = slugify(fromCity || "");
  return normalizedFrom ? `${path}?from=${encodeURIComponent(normalizedFrom)}` : path;
};

const getDestinationSlugFromPathname = (pathname = "") => {
  if (isTourPackagesListingPath(pathname)) return "";
  const normalizedPath = normalizeRoutePathname(pathname);
  for (const pattern of DESTINATION_ROUTE_PATTERNS) {
    const match = normalizedPath.match(pattern);
    if (match?.[1]) return slugify(decodeURIComponent(match[1]));
  }
  return "";
};

const formatINR = (n) =>
  Number.isFinite(+n) ? new Intl.NumberFormat("en-IN").format(+n) : "0";

const imgUrl = (name) =>
  name
    ? withAssetCacheBust(
        name.startsWith("/") || name.startsWith("http")
          ? name
          : `${IMAGE_BASE}Travel-Rethinkways-${name.replace(/\s+/g, "-")}.avif`
      )
    : "";

const normalizeManagedTourImageUrl = (value = "") => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const marker = "/assets/images/tour-packages/";
  const markerIndex = raw.indexOf(marker);
  if (markerIndex >= 0) {
    return raw.slice(markerIndex);
  }
  return withAssetCacheBust(raw);
};

const resolveTourImageSrc = (value = "") => {
  const normalized = normalizeManagedTourImageUrl(value);
  if (!normalized) return "";
  return normalized.startsWith("/")
    ? `${import.meta.env.BASE_URL}${normalized.slice(1)}`
    : normalized;
};

const stripHtml = (value = "") =>
  String(value || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

const formatBlogDate = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const getPostTermsByTaxonomy = (post, taxonomy) => {
  const termGroups = post?._embedded?.["wp:term"];
  if (!Array.isArray(termGroups)) return [];
  return termGroups.flat().filter((term) => term?.taxonomy === taxonomy);
};

const findDestinationTag = async (tagLabel) => {
  const normalizedSlug = slugify(tagLabel);
  if (!normalizedSlug) return null;
  if (destinationTagRequestCache.has(normalizedSlug)) {
    return destinationTagRequestCache.get(normalizedSlug);
  }

  const request = (async () => {
    const exactResponse = await fetch(
      `${WP_TAGS_API}?slug=${encodeURIComponent(normalizedSlug)}&_fields=id,name,slug`,
      { credentials: "same-origin" }
    );
    if (exactResponse.ok) {
      const exactTags = await exactResponse.json();
      if (Array.isArray(exactTags) && exactTags[0]) return exactTags[0];
    }

    const searchResponse = await fetch(
      `${WP_TAGS_API}?search=${encodeURIComponent(tagLabel)}&_fields=id,name,slug&per_page=20`,
      { credentials: "same-origin" }
    );
    if (!searchResponse.ok) {
      throw new Error(`Tag request failed with status ${searchResponse.status}`);
    }

    const searchTags = await searchResponse.json();
    if (!Array.isArray(searchTags) || !searchTags.length) return null;

    return (
      searchTags.find((tag) => String(tag?.slug || "").toLowerCase() === normalizedSlug) ||
      searchTags.find((tag) => slugify(tag?.name || "") === normalizedSlug) ||
      searchTags[0]
    );
  })();

  destinationTagRequestCache.set(normalizedSlug, request);
  try {
    return await request;
  } catch (error) {
    destinationTagRequestCache.delete(normalizedSlug);
    throw error;
  }
};

const mapWpPostToDestinationArticle = (post, destinationTitle) => {
  const image = getPostImageData(post);
  const category = getPostTermsByTaxonomy(post, "category")[0];

  return {
    id: String(post?.id || post?.slug || post?.link || Math.random()),
    title: stripHtml(post?.title?.rendered || destinationTitle),
    excerpt: stripHtml(post?.excerpt?.rendered || ""),
    imageUrl: image.src,
    imageAlt: image.alt || stripHtml(post?.title?.rendered || destinationTitle),
    link: post?.link || "#",
    tag: category?.name || "Travel Story",
    category: category?.name || destinationTitle,
    dateText: formatBlogDate(post?.date),
  };
};

const fetchDestinationArticles = async (title, tagLabels = []) => {
  const normalizedLabels = Array.from(
    new Set(
      tagLabels
        .map((label) => String(label || "").trim())
        .filter(Boolean)
    )
  );

  if (!normalizedLabels.length) return [];
  const cacheKey = `${slugify(title)}::${normalizedLabels.map((label) => slugify(label)).join("|")}`;
  if (destinationArticlesDataCache.has(cacheKey)) {
    return destinationArticlesDataCache.get(cacheKey);
  }
  if (destinationArticlesCache.has(cacheKey)) {
    return destinationArticlesCache.get(cacheKey);
  }

  const request = (async () => {
    const resolvedTags = await Promise.all(normalizedLabels.map((label) => findDestinationTag(label)));
    const uniqueTags = resolvedTags.filter((tag, index, arr) => tag?.id && arr.findIndex((entry) => entry?.id === tag.id) === index);
    if (!uniqueTags.length) return [];

    const articleMap = new Map();

    for (const tag of uniqueTags) {
      const posts = await fetchPosts({
        tags: tag.id,
        per_page: 9,
        orderby: "date",
        order: "desc",
      });

      posts.forEach((post) => {
        const mapped = mapWpPostToDestinationArticle(post, title);
        articleMap.set(String(mapped.id), mapped);
      });
    }

    const nextArticles = Array.from(articleMap.values()).slice(0, 9);
    destinationArticlesDataCache.set(cacheKey, nextArticles);
    return nextArticles;
  })();

  destinationArticlesCache.set(cacheKey, request);
  try {
    return await request;
  } catch (error) {
    destinationArticlesCache.delete(cacheKey);
    destinationArticlesDataCache.delete(cacheKey);
    throw error;
  }
};

// ─── INDIA IMAGE MAP ──────────────────────────────────────────────────────────
const INDIA_IMG = {
  "Andaman":                                    `${IMAGE_BASE}Travel-Rethinkways-Andaman.avif`,
  "Jammu and Kashmir":                          `${IMAGE_BASE}Travel-Rethinkways-Kashmir.avif`,
  "Kashmir":                                    `${IMAGE_BASE}Travel-Rethinkways-Kashmir.avif`,
  "Goa":                                        `${IMAGE_BASE}Travel-Rethinkways-Goa.avif`,
  "Himachal Pradesh":                           `${IMAGE_BASE}Travel-Rethinkways-Himachal-Pradesh.avif`,
  "Kerala":                                     `${IMAGE_BASE}Travel-Rethinkways-Kerala.avif`,
  "Rajasthan":                                  `${IMAGE_BASE}Travel-Rethinkways-Rajasthan.avif`,
  "Uttarakhand":                                `${IMAGE_BASE}Travel-Rethinkways-Uttarakhand.avif`,
  "Ladakh":                                     `${IMAGE_BASE}Travel-Rethinkways-Ladakh.avif`,
  "Tamil Nadu":                                 `${IMAGE_BASE}Travel-Rethinkways-Tamil-Nadu.avif`,
  "Karnataka":                                  `${IMAGE_BASE}Travel-Rethinkways-Karnataka.avif`,
  "Maharashtra":                                `${IMAGE_BASE}Travel-Rethinkways-Maharashtra.avif`,
  "Uttar Pradesh":                              `${IMAGE_BASE}Travel-Rethinkways-Uttar-Pradesh.avif`,
  "Delhi":                                      `${IMAGE_BASE}Travel-Rethinkways-Delhi.avif`,
  "West Bengal":                                `${IMAGE_BASE}Travel-Rethinkways-West-Bengal.avif`,
  "Sikkim":                                     `${IMAGE_BASE}Travel-Rethinkways-Sikkim.avif`,
  "Meghalaya":                                  `${IMAGE_BASE}Travel-Rethinkways-Meghalaya.avif`,
  "Gujarat":                                    `${IMAGE_BASE}Travel-Rethinkways-Gujarat.avif`,
  "Punjab":                                     `${IMAGE_BASE}Travel-Rethinkways-Punjab.avif`,
  "Telangana":                                  `${IMAGE_BASE}Travel-Rethinkways-Telangana.avif`,
  "Arunachal Pradesh":                          `${IMAGE_BASE}Travel-Rethinkways-Arunachal-Pradesh.avif`,
  "Assam":                                      `${IMAGE_BASE}Travel-Rethinkways-Assam.avif`,
  "Nagaland":                                   `${IMAGE_BASE}Travel-Rethinkways-Nagaland.avif`,
  "Manipur":                                    `${IMAGE_BASE}Travel-Rethinkways-Manipur.avif`,
  "Mizoram":                                    `${IMAGE_BASE}Travel-Rethinkways-Mizoram.avif`,
  "Tripura":                                    `${IMAGE_BASE}Travel-Rethinkways-Tripura.avif`,
  "Chhattisgarh":                               `${IMAGE_BASE}Travel-Rethinkways-Chhattisgarh.avif`,
  "Jharkhand":                                  `${IMAGE_BASE}Travel-Rethinkways-Jharkhand.avif`,
  "Odisha":                                     `${IMAGE_BASE}Travel-Rethinkways-Odisha.avif`,
  "Madhya Pradesh":                             `${IMAGE_BASE}Travel-Rethinkways-Madhya-Pradesh.avif`,
  "Puducherry":                                 `${IMAGE_BASE}Travel-Rethinkways-Puducherry.avif`,
  "Chandigarh":                                 `${IMAGE_BASE}Travel-Rethinkways-Chandigarh.avif`,
  "Lakshadweep":                                `${IMAGE_BASE}Travel-Rethinkways-Lakshadweep.avif`,
  "Dadra and Nagar Haveli and Daman and Diu":   `${IMAGE_BASE}Travel-Rethinkways-Dadra-and-Nagar-Haveli-and-Daman-and-Diu.avif`,
  "Andhra Pradesh":                             `${IMAGE_BASE}Travel-Rethinkways-Andhra-Pradesh.avif`,
  "Bihar":                                      `${IMAGE_BASE}Travel-Rethinkways-Bihar.avif`,
  "Haryana":                                    `${IMAGE_BASE}Travel-Rethinkways-Haryana.avif`,
};

// ─── INDIA ROWS ───────────────────────────────────────────────────────────────
const INDIA_ROWS = [
  ["Goa",               "Sun, Sand & Vibes",             1,  9999,  "Goa Tour Packages: Beaches, Nightlife & Holiday Deals",               "Discover Goa tour packages with beaches, nightlife, water sports & heritage sites."],
  ["Himachal Pradesh",  "Land of Snow & Hills",          1, 12999,  "Himachal Tour Packages: Snow, Hills & Scenic Trips",                  "Explore Himachal tour packages with snow mountains, hill stations & scenic valleys."],
  ["Kashmir", "Paradise on Earth",             1, 14999,  "Kashmir Tour Packages: Srinagar, Gulmarg & More",                     "Book Kashmir tour packages with Srinagar, Gulmarg, Pahalgam, Sonmarg & more."],
  ["Kerala",            "God's Green Canvas",            1, 11999,  "Kerala Tour Packages: Backwaters, Hills & Beaches",                   "Explore Kerala tour packages with backwaters, Munnar hills, beaches & wildlife."],
  ["Rajasthan",         "Desert & Royal Trails",         1, 20999,  "Rajasthan Tour Packages: Palaces, Desert & Heritage",                 "Discover Rajasthan tour packages with Jaipur, Udaipur, Jaisalmer & Jodhpur."],
  ["Uttarakhand",       "Land of Gods & Hills",          1, 12499,  "Uttarakhand Tour Packages: Hills, Temples & Adventure",               "Book Uttarakhand tour packages with Rishikesh, Nainital, Auli & Kedarnath."],
  ["Andaman",           "Island Life Starts Here",       1, 14999,  "Andaman Tour Packages: Islands, Beaches & Coral Reefs",               "Explore Andaman tour packages with Havelock, Neil Island & Port Blair."],
  ["Ladakh",            "Land of High Passes",           1, 18999,  "Ladakh Tour Packages: Leh, Pangong & Road Trips",                     "Explore Ladakh tour packages with Pangong Lake, Nubra Valley & Leh."],
  ["Tamil Nadu",        "Culture Crafted Forever",       1,  9499,  "Tamil Nadu Tour Packages: Temples, Hills & Heritage",                 "Discover Tamil Nadu tour packages with temples, hill stations & heritage sites."],
  ["Karnataka",         "Culture, Coffee & Coasts",      1, 10999,  "Karnataka Tour Packages: Coorg, Hampi & More",                        "Explore Karnataka tour packages with Coorg, Hampi, Mysore & beaches."],
  ["Maharashtra",       "From Mumbai to Mountains",      1, 10499,  "Maharashtra Tour Packages: Mumbai, Lonavola & More",                  "Book Maharashtra tour packages with Mumbai, Lonavola, Mahabaleshwar & Aurangabad."],
  ["Uttar Pradesh",     "Land of Timeless Heritage",     1, 12999,  "Uttar Pradesh Tour Packages: Agra, Varanasi & More",                  "Explore Uttar Pradesh tour packages with Taj Mahal, Varanasi & Ayodhya."],
  ["Delhi",             "Heart of Incredible India",     1, 14999,  "Delhi Tour Packages: Heritage, Markets & City Life",                  "Explore Delhi tour packages with Red Fort, India Gate & markets."],
  ["West Bengal",       "Soul of Eastern India",         1, 11999,  "West Bengal Tour Packages: Darjeeling, Sundarbans",                   "Discover West Bengal tour packages with Darjeeling hills, Sundarbans & Kolkata heritage."],
  ["Sikkim",            "Hidden Himalayan Gem",          1, 13999,  "Sikkim Tour Packages: Gangtok, Tsomgo Lake & More",                   "Explore Sikkim tour packages with Gangtok, Tsomgo Lake & Nathula Pass."],
  ["Meghalaya",         "Abode of Clouds",               1, 14499,  "Meghalaya Tour Packages: Shillong, Cherrapunji & More",               "Book Meghalaya tour packages with Shillong, Cherrapunji & Dawki."],
  ["Gujarat",           "Land of Legends & Lions",       1, 11499,  "Gujarat Tour Packages: Rann of Kutch, Gir & More",                    "Explore Gujarat tour packages with Rann of Kutch, Gir National Park & temples."],
  ["Punjab",            "Spirit of True India",          1, 15999,  "Punjab Tour Packages: Amritsar, Wagah & More",                        "Discover Punjab tour packages with Amritsar, Golden Temple & Wagah Border."],
  ["Telangana",         "Land of Nawabs & Culture",      1,  9499,  "Telangana Tour Packages: Hyderabad, Charminar & More",                "Explore Telangana tour packages with Hyderabad, Charminar & Golconda Fort."],
  ["Arunachal Pradesh", "Land of Rising Sun",            1, 16999,  "Arunachal Tour Packages: Tawang, Valleys & More",                    "Discover Arunachal tour packages with Tawang, Ziro Valley & monasteries."],
  ["Assam",             "Tea Gardens & Wildlife",        1, 12999,  "Assam Tour Packages: Kaziranga, Tea Gardens & More",                  "Explore Assam tour packages with Kaziranga, tea gardens & Brahmaputra river."],
  ["Nagaland",          "Land of Tribal Heritage",       1, 14999,  "Nagaland Tour Packages: Kohima, Culture & Hills",                     "Discover Nagaland tour packages with Kohima, tribal villages & festivals."],
  ["Manipur",           "Land of Floating Lakes",        1, 14499,  "Manipur Tour Packages: Loktak Lake & Scenic Beauty",                  "Explore Manipur tour packages with Loktak Lake, Imphal & culture."],
  ["Mizoram",           "Land of Blue Mountains",        1, 15499,  "Mizoram Tour Packages: Aizawl, Hills & Nature Trails",                "Discover Mizoram tour packages with Aizawl, hills & valleys."],
  ["Tripura",           "Palaces & Scenic Hills",        1, 13999,  "Tripura Tour Packages: Palaces, Temples & Nature",                    "Explore Tripura tour packages with Ujjayanta Palace, temples & hills."],
  ["Chhattisgarh",      "India's Hidden Wilderness",     1, 10999,  "Chhattisgarh Tour Packages: Waterfalls, Forests & More",              "Discover Chhattisgarh tour packages with waterfalls, caves & forests."],
  ["Jharkhand",         "Land of Forests & Falls",       1, 10499,  "Jharkhand Tour Packages: Waterfalls, Hills & Nature",                 "Explore Jharkhand tour packages with waterfalls, hills & wildlife."],
  ["Odisha",            "Heritage by the Coast",         1, 11999,  "Odisha Tour Packages: Puri, Konark & Chilika Lake",                   "Discover Odisha tour packages with Puri, Konark Temple & Chilika Lake."],
  ["Madhya Pradesh",    "Wildlife & Heritage Hub",       1, 11499,  "Madhya Pradesh Tour Packages: Wildlife & Heritage",                   "Explore Madhya Pradesh tour packages with Khajuraho, national parks & heritage sites."],
  ["Puducherry",        "Little France of India",        1,  8999,  "Puducherry Tour Packages: Beaches, Cafes & Culture",                  "Explore Puducherry tour packages with French streets, beaches & Auroville."],
  ["Chandigarh",        "Modern India's Pride",          1, 17999,  "Chandigarh Tour Packages: Gardens, Lakes & City Life",                "Discover Chandigarh tour packages with Rock Garden, Sukhna Lake & parks."],
  ["Lakshadweep",       "India's Hidden Islands",        1, 29999,  "Lakshadweep Tour Packages: Islands, Coral & Beaches",                 "Explore Lakshadweep tour packages with coral islands, lagoons & beaches."],
  ["Dadra and Nagar Haveli and Daman and Diu", "Peace by the Arabian Sea", 1, 10999, "Daman & Diu Tour Packages: Beaches & Heritage Sites", "Discover Daman & Diu tour packages with beaches, forts & churches."],
  ["Andhra Pradesh",    "Where Faith Meets Nature",      1,  9499,  "Andhra Pradesh Tour Packages: Temples, Hills & Coast",                "Explore Andhra Pradesh tour packages with Tirupati, Araku Valley & beaches."],
  ["Bihar",             "Land of Ancient Wisdom",        1, 14999,  "Bihar Tour Packages: Bodh Gaya, Nalanda & More",                      "Discover Bihar tour packages with Bodh Gaya, Nalanda & Rajgir."],
  ["Haryana",           "Heritage Near the Capital",     1, 17999,  "Haryana Tour Packages: Heritage, Lakes & Culture",                    "Explore Haryana tour packages with heritage sites, lakes & rural experiences."],
];

// ─── INTERNATIONAL ROWS ───────────────────────────────────────────────────────
const INTL_ROWS = [
  ["Bali",              "Island of Gods Awaits",           9,  39999],
  ["Maldives",          "Luxury Island Escape",            5,  49999],
  ["Thailand",          "Fun, Beaches & Nightlife",       11,  24999],
  ["Dubai (UAE)",       "Desert to Skyscrapers",          21,  45999],
  ["Singapore",         "Where Fun Never Ends",            6,  69999],
  ["Malaysia",          "Where Asia Comes Alive",         10,  39999],
  ["Vietnam",           "Scenic Asia Unfolds",            14,  44999],
  ["Sri Lanka",         "Beaches, Hills & Heritage",      11,  34999],
  ["Philippines",       "7000 Islands of Beauty",         22,  49999],
  ["Oman",              "Desert Beauty of Arabia",        23,  49999],
  ["Qatar",             "Arabian Elegance Unfolds",       24,  49999],
  ["Mauritius",         "Island Luxury Redefined",        23,  69999],
  ["Seychelles",        "Untouched Island Paradise",      25,  89999],
  ["Cambodia",          "Culture Beyond Time",            19,  44999],
  ["Laos",              "Hidden Gem of Asia",             18,  49999],
  ["Kenya",             "Into the African Wild",          21,  89999],
  ["Tanzania",          "Wild Africa Unleashed",          21,  99999],
  ["Japan",             "Culture & Cherry Blossom",       24,  99999],
  ["South Korea",       "K Culture Comes Alive",          21,  89999],
  ["Turkey",            "Timeless Turkish Trails",        23,  79999],
  ["Nepal",             "Gateway to the Himalayas",       24,  39999],
  ["Bhutan",            "Land of Happiness Awaits",       24,  49999],
  ["Egypt",             "Land of Ancient Wonders",        23,  89999],
  ["China",             "Explore Chinese Heritage",       24, 109999],
  ["Hong Kong",         "City of Lights & Skyline",       24,  89999],
  ["Jordan",            "Wonders of the Middle East",     26,  79999],
  ["Uzbekistan",        "Silk Road Wonders",              17,  69999],
  ["Kazakhstan",        "Hidden Eurasian Gem",            19,  59999],
  ["Fiji",              "Luxury in Blue Waters",          21,  99999],
  ["Australia",         "Beaches, Cities & Nature",       24, 149999],
  ["New Zealand",       "Land of Epic Landscapes",        23, 169999],
  ["USA",               "From Cities to Wonders",         22, 179999],
  ["Canada",            "Nature & Cities Combined",       24, 159999],
  ["South Africa",      "Adventure in Every Mile",        23, 129999],
];

// ─── EUROPE ROWS ──────────────────────────────────────────────────────────────
const EUROPE_ROWS = [
  ["Europe Multi-Country", "Expertly Designed Routes",   165000],
  ["France",               "Romance & Culture",           145000],
  ["Switzerland",          "Alpine Beauty Escape",        155000],
  ["Italy",                "Art, Food & Romance",         140000],
  ["United Kingdom",       "Royal & Urban Escape",        160000],
  ["Greece",               "Islands & Blue Bliss",        150000],
  ["Netherlands",          "Canals & City Vibes",         135000],
  ["Spain",                "Culture & Coastal Life",      145000],
  ["Austria",              "Music & Mountain Charm",      140000],
  ["Germany",              "History & Modern Mix",        135000],
  ["Turkey",               "East Meets West Magic",       120000],
  ["Norway",               "Fjords & Scenic Beauty",      200000],
  ["Iceland",              "Fire, Ice & Wonders",         210000],
  ["Finland",              "Snow & Northern Lights",      210000],
  ["Ireland",              "Green Lands & Castles",       155000],
  ["Portugal",             "Coastal Charm & Heritage",    130000],
  ["Czech Republic",       "Fairytale City Charm",        120000],
  ["Hungary",              "Thermal Baths & Views",       115000],
  ["Croatia",              "Adriatic Coastal Gems",       130000],
  ["Sweden",               "Nordic Design & Nature",      190000],
  ["Denmark",              "Hygge & Harbor Charm",        185000],
  ["Poland",               "Heritage & Old Towns",        110000],
  ["Slovakia",             "Castles & Nature Trails",     110000],
  ["Slovenia",             "Lakes & Alpine Beauty",       120000],
  ["Romania",              "Medieval Charm & Legends",    115000],
  ["Bulgaria",             "History & Coastal Beauty",    110000],
  ["Estonia",              "Medieval Meets Digital",      120000],
  ["Latvia",               "Baltic Charm & Old Towns",    115000],
  ["Lithuania",            "Heritage & Baltic Beauty",    115000],
  ["Malta",                "Island History Escape",       130000],
  ["Montenegro",           "Coastal Hidden Gem",          120000],
  ["Belgium",              "Medieval Meets Modern",       135000],
  ["Luxembourg",           "Castles & Scenic Views",      140000],
  ["Liechtenstein",        "Alpine Luxury Escape",        145000],
  ["San Marino",           "Hilltop Views & Heritage",    125000],
  ["Vatican City",         "Faith & Timeless Art",        135000],
  ["Georgia",              "Culture, Peaks & Wine",       110000],
  ["Armenia",              "Timeless Culture & Peaks",    105000],
  ["Azerbaijan",           "Modern Meets Old Charm",      110000],
  ["Serbia",               "Nightlife & Culture Mix",     110000],
  ["Albania",              "Raw Riviera Beauty",          110000],
  ["North Macedonia",      "Lakes & Quiet Beauty",        105000],
  ["Belarus",              "Soviet Charm & Nature",       110000],
  ["Russia",               "Grand Cities & Heritage",     160000],
];

// ─── BUILD PACKAGE ITEMS ──────────────────────────────────────────────────────
const buildIndia = () => INDIA_ROWS.map(([dest, tagline, pkgCount, startsFrom, seoTitle, seoDesc]) => ({
  id:              `seed-india-${slugify(dest)}`,
  sectionCode:     "india",
  destination:     dest,
  destinationSlug: slugify(dest),
  tagline,
  packageCount:    pkgCount,
  startsFrom,
  priceUnit:       "Per Adult",
  imageUrl:        INDIA_IMG[dest] || imgUrl(dest),
  showShare:       true,
  planLink:        waLink(dest),
  tourLink:        tourLink(slugify(dest)),
  seoTitle,
  seoDescription:  seoDesc,
}));

const buildIntl = () => INTL_ROWS.map(([dest, tagline, pkgCount, startsFrom]) => ({
  id:              `seed-international-${slugify(dest)}`,
  sectionCode:     "international",
  destination:     dest,
  destinationSlug: slugify(dest),
  tagline,
  packageCount:    pkgCount,
  startsFrom,
  priceUnit:       "Per Adult",
  imageUrl:        imgUrl(dest),
  showShare:       true,
  planLink:        waLink(dest),
  tourLink:        tourLink(slugify(dest)),
}));

const buildEurope = () => EUROPE_ROWS.map(([dest, tagline, startsFrom]) => ({
  id:              `seed-europe-${slugify(dest)}`,
  sectionCode:     "europe",
  destination:     dest,
  destinationSlug: slugify(dest),
  tagline,
  packageCount:    1,
  startsFrom,
  priceUnit:       "Per Adult",
  imageUrl:        imgUrl(dest),
  showShare:       true,
  planLink:        waLink(dest),
  tourLink:        tourLink(slugify(dest)),
}));

export const ALL_SEED_PACKAGES = [...buildIndia(), ...buildIntl(), ...buildEurope()];

const makePkg = (o) => ({
  id:          o.id,
  title:       o.title,
  subtitle:    o.subtitle || "",
  nights:      o.nights,
  days:        o.days,
  startsFrom:  o.startsFrom,
  perAdult:    "Per Adult",
  imageUrl:    o.imageUrl || INDIA_IMG[o.dest] || imgUrl(o.dest),
  includes:    [...DEFAULT_INCLUDES],
  routes:      o.route,
  highlights:  o.highlights,
  planLink:    `${WA_BASE}?text=${encodeURIComponent(`Hi, I'm interested in the ${o.title} package for ${o.dest}. Please share the full details.`)}`,
  tourLink:    tourLink(o.slug),
  terms:       DEFAULT_TERMS,
});

const ANDAMAN_PKGS = [
  { id:"andaman-starter",       title:"Andaman Starter",        subtitle:"Your First Island Escape",         dest:"Andaman & Nicobar", slug:"andaman", nights:3, days:4, startsFrom:14999, route:"2N Port Blair | 1N Havelock",                  imageUrl:`${IMAGE_BASE}Travel-Rethinkways-Andaman.avif`, highlights:["Cellular Jail Tour","Light & Sound Show","Radhanagar Beach","Elephant Beach"] },
  { id:"andaman-pearl-escape",  title:"Andaman Pearl Escape",   subtitle:"Timeless Island Beauty",           dest:"Andaman & Nicobar", slug:"andaman", nights:4, days:5, startsFrom:22999, route:"2N Port Blair | 1N Havelock | 1N Neil Island",  imageUrl:`${IMAGE_BASE}Travel-Rethinkways-andaman-480w.jpg`, highlights:["Light & Sound Show","Radhanagar Beach","Ross Island","Chidiya Tapu Island"] },
  { id:"andaman-blue-break",    title:"Andaman Blue Break",     subtitle:"Break Free. Breathe Blue.",        dest:"Andaman & Nicobar", slug:"andaman", nights:5, days:6, startsFrom:26999, route:"2N Port Blair | 1N Ross Island | 2N Havelock",  imageUrl:`${IMAGE_BASE}Travel-Rethinkways-Andaman-Cellular-Jail.avif`, highlights:["Corbyn's Cove Beach","Colonial Buildings","Radhanagar Beach","Elephant Beach"] },
  { id:"andaman-moonlit-luxe",  title:"Andaman Moonlit Luxe",   subtitle:"Romance Under Island Skies",       dest:"Andaman & Nicobar", slug:"andaman", nights:6, days:7, startsFrom:29999, route:"5N Port Blair | 1N Havelock",                  imageUrl:`${IMAGE_BASE}Travel-Rethinkways-Andaman-Limestone-Caves.avif`, highlights:["Cellular Jail","Coral Island","Limestone Caves","Chidiya Tapu Sunset"] },
  { id:"andaman-leisure-quest", title:"Andaman Leisure Quest",  subtitle:"Discovery Meets Pure Relaxation",  dest:"Andaman & Nicobar", slug:"andaman", nights:7, days:8, startsFrom:34999, route:"3N Port Blair | 2N Havelock | 2N Neil Island", imageUrl:`${IMAGE_BASE}Travel-Rethinkways-Andaman-Radhanagar-Beach.avif`, highlights:["Dolphin Watch Tour","Radhanagar Beach","Sound & Light Show","Snorkeling"] },
  { id:"andaman-grand-escape",  title:"Andaman Grand Escape",   subtitle:"The Ultimate Andaman Experience",  dest:"Andaman & Nicobar", slug:"andaman", nights:8, days:9, startsFrom:39999, route:"4N Port Blair | 3N Havelock | 1N Neil Island", imageUrl:`${IMAGE_BASE}Travel-Rethinkways-Andaman-Ross-Island.avif`, highlights:["Sunset Cruise","Island Hopping","Watersports Activity","Limestone Caves"] },
].map(makePkg);

const KASHMIR_PKGS = [
  { id:"kashmir-royale",          title:"Kashmir Royale",          subtitle:"Elegance Meets the Valley",        dest:"Kashmir", slug:"kashmir", nights:3, days:4, startsFrom:12999, route:"3N Srinagar",                                                               highlights:["Dal Lake Shikara Ride","Traditional Houseboat Stay","Gulmarg Full-Day Trip","Sonmarg Full-Day Trip"] },
  { id:"kashmir-alpine-circuit",  title:"Kashmir Alpine Circuit",  subtitle:"Kashmir's Most Scenic Journey",    dest:"Kashmir", slug:"kashmir", nights:3, days:4, startsFrom:15999, route:"1N Srinagar | 1N Pahalgam | 1N Gulmarg",                                      highlights:["Dal Lake Shikara Ride","Mughal Gardens Visit","Gulmarg Gondola Ride","Saffron Fields Exploration"] },
  { id:"kashmir-expedition",      title:"Kashmir Expedition",      subtitle:"Not Just Travel. An Expedition",   dest:"Kashmir", slug:"kashmir", nights:4, days:5, startsFrom:19999, route:"2N Srinagar | 1N Pahalgam | 1N Sonmarg",                                      highlights:["Dal Lake Shikara Ride","Traditional Houseboat Stay","Gondola Ride","Pony Ride"] },
  { id:"kashmir-nature-trails",   title:"Kashmir Nature Trails",   subtitle:"Where Nature Isn't Seen, Felt",    dest:"Kashmir", slug:"kashmir", nights:5, days:6, startsFrom:29999, route:"1N Srinagar | 2N Lolab Valley | 2N Srinagar",                                 highlights:["Dal Lake Shikara Ride","Lolab Valley Exploration","Gulmarg Gondola Ride","Mughal Gardens Visit"] },
  { id:"kashmir-grand-escape",    title:"Kashmir Grand Escape",    subtitle:"The Complete Kashmir Experience",  dest:"Kashmir", slug:"kashmir", nights:6, days:7, startsFrom:34999, route:"1N Pahalgam | 1N Gulmarg | 3N Srinagar | 1N Sonmarg",                          highlights:["Gulmarg Skiing Experience","Dal Lake Houseboat Stay","Shikara Ride","Sonmarg Sightseeing"] },
  { id:"kashmir-untamed-valleys", title:"Kashmir Untamed Valleys", subtitle:"Beyond the Usual. Into the Wild",  dest:"Kashmir", slug:"kashmir", nights:6, days:7, startsFrom:34999, route:"3N Srinagar | 2N Gurez Valley | 1N Srinagar",                                 highlights:["Dal Lake Houseboat Stay","Dal Lake Shikara Ride","Cable Car Experience","Gurez Valley Exploration"] },
  { id:"kashmir-sacred-trail",    title:"Kashmir Sacred Trail",    subtitle:"Soulful Journey Through Kashmir",  dest:"Kashmir", slug:"kashmir", nights:7, days:8, startsFrom:39999, route:"2N Katra | 2N Srinagar | 1N Gulmarg | 1N Pahalgam",                            highlights:["Vaishno Devi Shrine Visit","Dal Lake Shikara Ride","Houseboat Stay","Mughal Gardens Visit"] },
  { id:"kashmir-trans-himalaya",  title:"Kashmir Trans-Himalaya",  subtitle:"Beyond Valleys, Into Himalayas",   dest:"Kashmir", slug:"kashmir", nights:8, days:9, startsFrom:49999, route:"1N Srinagar | 1N Kargil | 4N Leh | 1N Nubra Valley | 1N Pangong",             highlights:["Dal Lake Shikara Ride","Monasteries Tour","Nubra Camel Safari","Pangong Lakeside Camp"] },
].map(makePkg);

const RAJASTHAN_PKGS = [
  { id:"raj-complete",  title:"Rajasthan Complete",  subtitle:"Rajasthan Regal Journey",  dest:"Rajasthan", slug:"rajasthan", nights:14, days:15, startsFrom:65999, route:"2N Delhi | 2N Agra | 2N Jaipur | 1N Mandawa | 1N Bikaner | 2N Jaisalmer | 1N Jodhpur | 2N Udaipur | 1N Pushkar",       highlights:["Majestic Taj Mahal","Royal Rajasthan Forts","Desert Safari","Lake Pichola Boat Ride"] },
  { id:"raj-imperial",  title:"Rajasthan Imperial",  subtitle:"Rajasthan Imperial Trail", dest:"Rajasthan", slug:"rajasthan", nights:10, days:11, startsFrom:49999, route:"2N Udaipur | 1N Mt. Abu | 1N Jodhpur | 2N Jaisalmer | 1N Bikaner | 2N Jaipur | 1N Agra",                               highlights:["Royal Lake of Udaipur","Hill Escapes of Mt. Abu","Blue City Grandeur","Majestic Taj Mahal"] },
  { id:"raj-cultural",  title:"Rajasthan Cultural",  subtitle:"Rajasthan Imperial Tour",  dest:"Rajasthan", slug:"rajasthan", nights:8,  days:9,  startsFrom:41999, route:"2N Jaipur | 1N Ranthambore National Park | 2N Udaipur | 1N Jodhpur | 2N Jaisalmer",                                   highlights:["Pink City Heritage","Wildlife Safari","Royal Lakes of Udaipur","Desert Nights"] },
  { id:"raj-circuit",   title:"Rajasthan Circuit",   subtitle:"Rajasthan Royal Circuit",  dest:"Rajasthan", slug:"rajasthan", nights:7,  days:8,  startsFrom:44999, route:"1N Delhi | 1N Agra | 2N Jaipur | 1N Jodhpur | 2N Udaipur",                                                            highlights:["Timeless Taj Mahal","Pink City Royalty","Blue City Grandeur","Royal Lakes of Udaipur"] },
  { id:"raj-heritage",  title:"Rajasthan Heritage",  subtitle:"Rajasthan Classic Circuit",dest:"Rajasthan", slug:"rajasthan", nights:7,  days:8,  startsFrom:37999, route:"2N Jaipur | 1N Jaisalmer | 2N Jodhpur | 2N Udaipur",                                                                 highlights:["Saheliyon Ki Bari Tour","Lake Pichola Boat Ride","Jaipur Fort","Jaisalmer Desert Experience"] },
  { id:"raj-wildlife",  title:"Rajasthan Wildlife",  subtitle:"Rajasthan Wildlife",       dest:"Rajasthan", slug:"rajasthan", nights:6,  days:7,  startsFrom:35999, route:"2N Delhi | 1N Sariska | 2N Ranthambore | 1N Bharatpur",                                                              highlights:["Delhi City Tour","Sariska Tiger Reserve","Bird Watching","Ranthambore National Park"] },
  { id:"raj-royal",     title:"Rajasthan Royal",     subtitle:"Rajasthan Desert Escape",  dest:"Rajasthan", slug:"rajasthan", nights:5,  days:6,  startsFrom:29999, route:"2N Jaipur | 1N Jodhpur | 2N Jaisalmer",                                                                              highlights:["Jaipur City Tour","Mehrangarh Fort","Camel Safari","Heritage Havelis"] },
  { id:"raj-elegant",   title:"Rajasthan Elegant",   subtitle:"Rajasthan Elegant",        dest:"Rajasthan", slug:"rajasthan", nights:4,  days:5,  startsFrom:26999, route:"2N Jaipur | 2N Udaipur",                                                                                              highlights:["Elephant Ride","City Palace","Traditional Rajasthani Dinner","Lake Pichola Ride"] },
  { id:"raj-classic",   title:"Rajasthan Classic",   subtitle:"Rajasthan Short Break",    dest:"Rajasthan", slug:"rajasthan", nights:4,  days:5,  startsFrom:25999, route:"1N Udaipur | 1N Mount Abu | 1N Jodhpur | 1N Jaisalmer",                                                              highlights:["City Palace Udaipur","Dilwara Temples","Mehrangarh Fort","Camel Ride"] },
].map(makePkg);

const KERALA_PKGS = [
  { id:"kerala-unfolded",     title:"Kerala Unfolded",      subtitle:"Kerala Unfolded",   dest:"Kerala", slug:"kerala", nights:10, days:11, startsFrom:59999, route:"2N Kochi | 2N Munnar | 2N Thekkady | 2N Kumarakom | 2N Kovalam",              highlights:["Houseboat Cruise","Spice Plantation Tour","Boat Safari","Eravikulam"] },
  { id:"kerala-signature",    title:"Kerala Signature",     subtitle:"Kerala Signature",  dest:"Kerala", slug:"kerala", nights:8,  days:9,  startsFrom:47999, route:"1N Kochi | 2N Munnar | 1N Thekkady | 1N Alleppey | 2N Kovalam | 1N Poovar",   highlights:["Kochi Sightseeing","Houseboat Cruise","Spice Plantation Tour","Kanyakumari Excursion"] },
  { id:"kerala-essence-plus", title:"Kerala Essence Plus",  subtitle:"Kerala Balance",    dest:"Kerala", slug:"kerala", nights:7,  days:8,  startsFrom:42999, route:"1N Kochi | 2N Munnar | 1N Thekkady | 1N Alleppey | 2N Kovalam",              highlights:["Periyar Wildlife Sanctuary","Munnar Sightseeing","Kovalam Beach","Spice Tour"] },
  { id:"kerala-panorama",     title:"Kerala Panorama",      subtitle:"Kerala Serenity",   dest:"Kerala", slug:"kerala", nights:6,  days:7,  startsFrom:37999, route:"2N Kochi | 2N Munnar | 1N Thekkady | 1N Kumarakom",                          highlights:["Elephant Ride","Houseboat Experience","Cheeyappara Waterfalls","Periyar Wildlife"] },
  { id:"kerala-highlights",   title:"Kerala Highlights",    subtitle:"Kerala Panorama",   dest:"Kerala", slug:"kerala", nights:6,  days:7,  startsFrom:35999, route:"1N Kochi | 1N Munnar | 1N Thekkady | 1N Alleppey | 1N Kovalam | 1N Trivandrum", highlights:["Cheeyappara Waterfalls","Elephant Ride","Houseboat Stay","Kovalam Beach"] },
  { id:"kerala-hill-retreat", title:"Kerala Hill Retreat",  subtitle:"Kerala Highlands",  dest:"Kerala", slug:"kerala", nights:5,  days:6,  startsFrom:31999, route:"1N Kochi | 2N Vagamon | 2N Thekkady",                                        highlights:["Kochi Sightseeing","Tree House Stay","Jeep Off-Road Trip","Periyar Wildlife Sanctuary"] },
  { id:"kerala-countryside",  title:"Kerala Countryside",   subtitle:"Kerala Countryside",dest:"Kerala", slug:"kerala", nights:5,  days:6,  startsFrom:29999, route:"2N Munnar | 1N Vagamon | 1N Kottayam | 1N Alleppey",                        highlights:["Houseboat","Jeep Safari","Tea Plantation","Scenic Waterfalls"] },
  { id:"kerala-divinity",     title:"Kerala Divinity",      subtitle:"Sacred Kerala",     dest:"Kerala", slug:"kerala", nights:4,  days:5,  startsFrom:23999, route:"1N Thrissur | 1N Guruvayur | 1N Kochi | 1N Trivandrum",                     highlights:["Boat Ride","Famous Dance Drama","Chettuva Backwaters","Trivandrum Sightseeing"] },
  { id:"kerala-snapshot",     title:"Kerala Snapshot",      subtitle:"Kerala Snapshot",   dest:"Kerala", slug:"kerala", nights:3,  days:4,  startsFrom:17999, route:"1N Munnar | 1N Thekkady | 1N Kochi",                                        highlights:["Tea Gardens","Bamboo Rafting","Periyar Wildlife Sanctuary","Kochi City Tour"] },
].map(makePkg);

const TAMILNADU_PKGS = [
  { id:"tamilnadu-timeless",      title:"Tamil Nadu Timeless",      subtitle:"Divine Dravidian",   dest:"Tamil Nadu", slug:"tamilnadu", nights:15, days:16, startsFrom:93999, route:"2N Trichy | 2N Madurai | 1N Kanyakumari | 1N Rameshwaram | 2N Tanjore | 2N Kumbakonam | 1N Pondicherry | 2N Chennai | 1N Tirupati | 1N Thiruvannamalai", highlights:["Tanjore Sightseeings","Pamban Bridge","Temple Visits","Pondicherry Sightseeings"] },
  { id:"tamilnadu-cultural",      title:"Tamil Nadu Cultural",      subtitle:"Cultural Tamil Nadu", dest:"Tamil Nadu", slug:"tamilnadu", nights:8,  days:9,  startsFrom:50999, route:"1N Chennai | 1N Kanchipuram | 1N Mahabalipuram | 1N Pondicherry | 1N Kumbakonam | 1N Thanjavur | 1N Trichy | 1N Madurai", highlights:["Vedanthangal Bird Sanctuary","Shore Temple","Pondicherry Beach","Temple Visit"] },
  { id:"tamilnadu-legacy",        title:"Tamil Nadu Legacy",        subtitle:"Faith & Forest",     dest:"Tamil Nadu", slug:"tamilnadu", nights:7,  days:8,  startsFrom:44999, route:"0N Chennai | 1N Kumbakonam | 1N Thanjavur | 1N Rameshwaram | 1N Kanyakumari | 1N Kodaikanal | 2N Ooty", highlights:["Boating in Mudaliar Kuppam","Sphatik Shivaling Darshan","Vivekananda Rock Memorial","Ooty Sightseeing"] },
  { id:"tamilnadu-escape",        title:"Tamil Nadu Escape",        subtitle:"Tamil Nadu Tides",   dest:"Tamil Nadu", slug:"tamilnadu", nights:5,  days:6,  startsFrom:31999, route:"2N Kodaikanal | 1N Madurai | 1N Rameshwaram | 1N Kanyakumari", highlights:["Boat Ride","Padmanabhapuram Palace","Kodaikanal Lake","Scenic Beaches"] },
  { id:"tamilnadu-twin-hills",    title:"Tamil Nadu Twin Hills",    subtitle:"Tamil Nadu Twin Hills", dest:"Tamil Nadu", slug:"tamilnadu", nights:4,  days:5,  startsFrom:25999, route:"0N Coimbatore | 2N Ooty | 2N Kodaikanal", highlights:["Candlelight Dinner","Ooty Botanical Gardens","Doddabetta","Kodaikanal Sightseeing"] },
  { id:"tamilnadu-temple-trail",  title:"Tamil Nadu Temple Trail",  subtitle:"Tamil Nadu Faith Trail", dest:"Tamil Nadu", slug:"tamilnadu", nights:3,  days:4,  startsFrom:18999, route:"1N Madurai | 1N Rameswaram | 1N Kanyakumari", highlights:["Hanuman Temple","Pamban Bridge","Vivekananda Rock","Sunrise Point"] },
].map(makePkg);

const BALI_PKGS = [
  {
    id: "bali-exotic-island",
    title: "Bali Exotic Island",
    subtitle: "Explore Bali's hidden gems",
    dest: "Bali",
    slug: "bali",
    nights: 6,
    days: 7,
    startsFrom: 69999,
    imageUrl: "/assets/images/tour-packages/tour-packages/travel-rethinkways-bali-nusa-penida-20260527120113.avif",
    route: "2N Kuta | 2N Nusa Penida | 2N Gili Trawangan",
    highlights: ["Waterbom Bali", "Nusa Penida Sightseeing", "Gili Island Chill Vibe", "Speedboat Transfers"],
  },
  {
    id: "bali-cultural-beach",
    title: "Bali Cultural Beach",
    subtitle: "Temples, beaches & beyond",
    dest: "Bali",
    slug: "bali",
    nights: 6,
    days: 7,
    startsFrom: 64999,
    imageUrl: "/assets/images/tour-packages/tour-packages/travel-rethinkways-bali-monkey-forest-20260527120256.avif",
    route: "3N Ubud | 3N Seminyak",
    highlights: ["Nusa Penida Tour", "Monkey Forest Visit", "Gili Islands Day Trip", "Culture + Beach Mix"],
  },
  {
    id: "bali-beach-culture",
    title: "Bali Beach & Culture",
    subtitle: "Beaches with cultural charm",
    dest: "Bali",
    slug: "bali",
    nights: 5,
    days: 6,
    startsFrom: 56999,
    imageUrl: "/assets/images/tour-packages/tour-packages/travel-rethinkways-bali-uluwatu-sunset-tour-20260527120521.avif",
    route: "3N Kuta | 2N Nusa Dua",
    highlights: ["Kecak Fire Dance Show", "Tanah Lot Sunset Visit", "Uluwatu Temple Tour", "Beach Activities"],
  },
  {
    id: "bali-nature-adventure",
    title: "Bali Nature Adventure",
    subtitle: "Nature, thrill & scenic views",
    dest: "Bali",
    slug: "bali",
    nights: 5,
    days: 6,
    startsFrom: 59999,
    imageUrl: "/assets/images/tour-packages/tour-packages/travel-rethinkways-bali-white-water-rafting-20260527120604.avif",
    route: "2N Seminyak | 1N Nusa Penida | 2N Ubud",
    highlights: ["Angel's Billabong Visit", "White Water Rafting", "Nusa Penida Snorkeling", "Ubud Cultural Tour"],
  },
  {
    id: "bali-penida-escape",
    title: "Bali Penida Escape",
    subtitle: "Discover island views & blue waters",
    dest: "Bali",
    slug: "bali",
    nights: 5,
    days: 6,
    startsFrom: 45999,
    imageUrl: "/assets/images/tour-packages/tour-packages/travel-rethinkways-bali-tembeling-beach-20260527121900.avif",
    route: "3N Nusa Penida | 2N Sanur",
    highlights: ["Tembeling Beach Visit", "Angel's Billabong Stop", "Diamond Beach Views"],
  },
  {
    id: "bali-thrill-culture",
    title: "Bali Thrill & Culture",
    subtitle: "Action meets tradition",
    dest: "Bali",
    slug: "bali",
    nights: 5,
    days: 6,
    startsFrom: 55999,
    imageUrl: "/assets/images/tour-packages/tour-packages/travel-rethinkways-bali-reverse-bungy-20260527121948.avif",
    route: "2N Kuta | 2N Gianyar | 1N Ubud",
    highlights: ["Reverse Bungy Experience", "Ubud Sightseeing", "Kuta Beach Day", "Gianyar Sightseeing"],
  },
  {
    id: "bali-complete-island",
    title: "Bali Complete Island",
    subtitle: "Best of Bali in one go",
    dest: "Bali",
    slug: "bali",
    nights: 5,
    days: 6,
    startsFrom: 49999,
    imageUrl: "/assets/images/tour-packages/tour-packages/travel-rethinkways-bali-monkey-forest-20260527122102.avif",
    route: "1N Kuta | 1N Jimbaran | 1N Ubud | 1N Bedugul | 1N Gianyar",
    highlights: ["Ubud Monkey Forest", "Banjar Hot Springs", "North Bali Exploration", "Beach Life"],
  },
  {
    id: "bali-scenic-short",
    title: "Bali Scenic Short",
    subtitle: "Short trip, big experiences",
    dest: "Bali",
    slug: "bali",
    nights: 4,
    days: 5,
    startsFrom: 48999,
    imageUrl: "/assets/images/tour-packages/tour-packages/travel-rethinkways-bali-hot-spring-20260527122236.avif",
    route: "2N Kuta | 2N Ubud",
    highlights: ["Watersports Package", "Uluwatu Sunset Tour", "Kintamani Day Trip", "Cultural Ubud Vibes"],
  },
  {
    id: "bali-elite-escape",
    title: "Bali Elite Escape",
    subtitle: "Luxury stay & experiences",
    dest: "Bali",
    slug: "bali",
    nights: 4,
    days: 5,
    startsFrom: 39999,
    imageUrl: "/assets/images/tour-packages/tour-packages/travel-rethinkways-bali-private-pool-villa-20260527122344.avif",
    route: "3N Kuta | 1N Seminyak",
    highlights: ["Bali Swing Experience", "Private Pool Villa Stay", "Floating Breakfast", "Ulun Danu Temple"],
  },
].map(makePkg);

const MALDIVES_PKGS = [
  {
    id: "maldives-budget-escape",
    title: "Maldives Budget Escape",
    subtitle: "Affordable island getaway",
    dest: "Maldives",
    slug: "maldives",
    nights: 3,
    days: 4,
    startsFrom: 39999,
    imageUrl: "/assets/images/tour-packages/tour-packages/travel-rethinkways-maldives--maafushi-island-20260527135920.avif",
    route: "3N Maafushi",
    highlights: ["Stay in Maafushi Island", "Shark Bay Day Trip", "Coral Garden Snorkeling", "Gulhi Island Visit"],
  },
  {
    id: "maldives-bliss-escape",
    title: "Maldives Bliss Escape",
    subtitle: "Pure island bliss awaits",
    dest: "Maldives",
    slug: "maldives",
    nights: 3,
    days: 4,
    startsFrom: 99999,
    imageUrl: "/assets/images/tour-packages/tour-packages/travel-rethinkways-maldives-shark-snorkeling-20260527140430.avif",
    route: "2N Beach Villa | 1N Ocean Villa",
    highlights: ["Shark Snorkeling & Marine Life", "Relaxing Spa & Wellness", "Beachfront Dining Experience", "Private Terrace & Ocean Views"],
  },
  {
    id: "maldives-ocean-retreat",
    title: "Maldives Ocean Retreat",
    subtitle: "Beach & water villa combo",
    dest: "Maldives",
    slug: "maldives",
    nights: 4,
    days: 5,
    startsFrom: 134999,
    imageUrl: "/assets/images/tour-packages/tour-packages/travel-rethinkways-maldives-watersports-20260527140509.avif",
    route: "2N Beach Villa | 2N Ocean Villa",
    highlights: ["Direct Ocean Access Stay", "Sunset & Beach Moments", "Seamless Island Transfer", "Exciting Watersports Included"],
  },
  {
    id: "maldives-island-combo",
    title: "Maldives Island Combo",
    subtitle: "Relax by the endless ocean",
    dest: "Maldives",
    slug: "maldives",
    nights: 4,
    days: 5,
    startsFrom: 110999,
    imageUrl: "/assets/images/tour-packages/tour-packages/travel-rethinkways-maldives-overwater-villa-20260527135756.avif",
    route: "2N Maafushi Hotel | 2N Water Villa",
    highlights: ["Overwater Villa Experience", "Snorkel Coral Gardens", "Dolphin Spotting Tour", "Local Island Stay"],
  },
  {
    id: "maldives-smart-escape",
    title: "Maldives Smart Escape",
    subtitle: "Smart value island getaway",
    dest: "Maldives",
    slug: "maldives",
    nights: 4,
    days: 5,
    startsFrom: 45999,
    imageUrl: "/assets/images/tour-packages/tour-packages/travel-rethinkways-maldives-beachfront-dining-20260527135813.avif",
    route: "4N Maafushi",
    highlights: ["Marine Adventures", "Beachfront Dining", "Thrilling Watersports", "Private Picnic"],
  },
].map(makePkg);

const SINGAPORE_PKGS = [
  { id:"singapore-grand-escape",  title:"Singapore Grand Escape",  subtitle:"Iconic sights & experiences",    dest:"Singapore", slug:"singapore", nights:7, days:8, startsFrom:105000, route:"7N Singapore",                          highlights:["Giant Wheel Experience","Dolphin Lagoon Visit","Universal Studios Fun","Night Safari Experience"] },
  { id:"singapore-city-explorer", title:"Singapore City Explorer", subtitle:"Discover the Lion City",         dest:"Singapore", slug:"singapore", nights:6, days:7, startsFrom:99999,  route:"6N Singapore",                          highlights:["Wildlife Night Safari","Theme Park Experience","Sentosa Island Fun","Marina Bay Highlights"] },
  { id:"singapore-sea-and-city",  title:"Singapore Sea & City",    subtitle:"City life meets cruise vibes",   dest:"Singapore", slug:"singapore", nights:6, days:7, startsFrom:115000, route:"4N Singapore City | 2N Singapore Cruise", highlights:["Dual Experience Getaway","Luxury Cruise Experience","Live Shows & Dining Onboard","Iconic City Attractions"] },
  { id:"singapore-classic-trip",  title:"Singapore Classic Trip",  subtitle:"Timeless city experiences",      dest:"Singapore", slug:"singapore", nights:5, days:6, startsFrom:89999,  route:"5N Singapore",                          highlights:["Wildlife Night Safari","Marina Bay Attractions","Sentosa Island Fun","Light & Sound Show"] },
  { id:"singapore-thrill-escape", title:"Singapore Thrill Escape", subtitle:"Adventure meets city fun",       dest:"Singapore", slug:"singapore", nights:4, days:5, startsFrom:79999,  route:"4N Singapore",                          highlights:["Iconic Singapore Flyer Ride","Garden Rhapsody Light Show","Half-Day City Exploration","Sentosa Island Experiences"] },
  { id:"singapore-express-trip",  title:"Singapore Express Trip",  subtitle:"Quick trip, big experiences",    dest:"Singapore", slug:"singapore", nights:3, days:4, startsFrom:69999,  route:"3N Singapore",                          highlights:["Wildlife & Night Safari","Zoo & Nature Encounters","Cable Car Sky Views","Garden Rhapsody Show"] },
].map(makePkg);

const MALAYSIA_IMAGE = `${IMAGE_BASE}Travel-Rethinkways-Malaysia.avif`;

const MALAYSIA_PKGS = [
  {
    id: "malaysia-kl-city-trip",
    title: "Malaysia KL City Trip",
    subtitle: "KL highlights in one trip",
    dest: "Malaysia",
    slug: "malaysia",
    nights: 3,
    days: 4,
    startsFrom: 34999,
    imageUrl: MALAYSIA_IMAGE,
    route: "3N Kuala Lumpur",
    highlights: ["Sunway Lagoon Theme Park", "Butterfly Park Visit", "City Sightseeing Tour", "Family-Friendly Getaway"],
  },
  {
    id: "malaysia-island-escape",
    title: "Malaysia Island Escape",
    subtitle: "Coastal escape in Malaysia",
    dest: "Malaysia",
    slug: "malaysia",
    nights: 3,
    days: 4,
    startsFrom: 39999,
    imageUrl: MALAYSIA_IMAGE,
    route: "3N Langkawi",
    highlights: ["Langkawi Cable Car Ride", "Island Hopping Experience", "Sky Bridge Views", "Scenic Beaches & Islands"],
  },
  {
    id: "malaysia-hills-and-city",
    title: "Malaysia Hills & City",
    subtitle: "KL & Genting in one trip",
    dest: "Malaysia",
    slug: "malaysia",
    nights: 4,
    days: 5,
    startsFrom: 49999,
    imageUrl: MALAYSIA_IMAGE,
    route: "2N Genting Highlands | 2N Kuala Lumpur",
    highlights: ["Genting Highlands Excursion", "Indoor Theme Park Fun", "Kuala Lumpur City Tour", "KL Tower City Views"],
  },
  {
    id: "malaysia-kl-hill-trip",
    title: "Malaysia KL Hill Trip",
    subtitle: "From towers to tea hills",
    dest: "Malaysia",
    slug: "malaysia",
    nights: 4,
    days: 5,
    startsFrom: 52999,
    imageUrl: MALAYSIA_IMAGE,
    route: "3N Kuala Lumpur | 1N Cameron Highlands",
    highlights: ["Cameron Highlands Tour", "Batu Caves Visit", "Petronas Twin Towers", "KL Bird Park Visit"],
  },
  {
    id: "malaysia-dual-escape",
    title: "Malaysia Dual Escape",
    subtitle: "City & island in one trip",
    dest: "Malaysia",
    slug: "malaysia",
    nights: 4,
    days: 5,
    startsFrom: 54999,
    imageUrl: MALAYSIA_IMAGE,
    route: "2N Kuala Lumpur | 2N Langkawi",
    highlights: ["Langkawi Island Hopping", "Langkawi Sightseeing Tour", "Sunway Lagoon Theme Park", "Batu Caves Visit"],
  },
  {
    id: "malaysia-trio-escape",
    title: "Malaysia Trio Escape",
    subtitle: "City, hills & island combo",
    dest: "Malaysia",
    slug: "malaysia",
    nights: 5,
    days: 6,
    startsFrom: 62999,
    imageUrl: MALAYSIA_IMAGE,
    route: "2N Kuala Lumpur | 1N Cameron Highlands | 2N Pangkor Island",
    highlights: ["Kuala Lumpur Exploration", "Boh Tea Plantation Visit", "Pangkor Island Experience", "Scenic Highland Views"],
  },
  {
    id: "malaysia-fun-explorer",
    title: "Malaysia Fun Explorer",
    subtitle: "Fun, sights & experiences",
    dest: "Malaysia",
    slug: "malaysia",
    nights: 5,
    days: 6,
    startsFrom: 64999,
    imageUrl: MALAYSIA_IMAGE,
    route: "2N Kuala Lumpur | 1N Genting Highlands | 2N Langkawi",
    highlights: ["Cable Car Ride Experience", "Underwater World Visit", "Langkawi Island Tour", "Petronas Twin Towers"],
  },
  {
    id: "malaysia-wild-borneo",
    title: "Malaysia Wild Borneo",
    subtitle: "Wildlife & jungle adventure",
    dest: "Malaysia",
    slug: "malaysia",
    nights: 6,
    days: 7,
    startsFrom: 89999,
    imageUrl: MALAYSIA_IMAGE,
    route: "2N Kota Kinabalu | 1N Sabah | 1N Sandakan | 2N Kota Kinabalu",
    highlights: ["Sunset Dinner Cruise", "Kinabalu National Park", "Wildlife River Cruise", "Cultural Village Visit"],
  },
  {
    id: "malaysia-scenic-trio",
    title: "Malaysia Scenic Trio",
    subtitle: "Trio of top destinations",
    dest: "Malaysia",
    slug: "malaysia",
    nights: 6,
    days: 7,
    startsFrom: 72999,
    imageUrl: MALAYSIA_IMAGE,
    route: "2N Penang | 2N Langkawi | 2N Kuala Lumpur",
    highlights: ["Penang Heritage Tour", "Langkawi Island Experience", "Kuala Lumpur City Tour", "Cultural Exploration"],
  },
  {
    id: "malaysia-hills-coast",
    title: "Malaysia Hills & Coast",
    subtitle: "Highlands to seaside trip",
    dest: "Malaysia",
    slug: "malaysia",
    nights: 8,
    days: 9,
    startsFrom: 84999,
    imageUrl: MALAYSIA_IMAGE,
    route: "3N Kuala Lumpur | 2N Port Dickson | 2N Cameron Highlands | 1N Kuala Lumpur",
    highlights: ["Kuala Lumpur City Tour", "Port Dickson Beach Stay", "Tea Plantation Visit", "Scenic Hill Station Views"],
  },
].map(makePkg);

const THAILAND_PKGS = [
  { id:"thailand-city-coast",     title:"Thailand City Coast",     subtitle:"From temples to beaches",          dest:"Thailand", slug:"thailand", nights:3, days:4, startsFrom:32999,  route:"1N Bangkok | 2N Hua Hin",                                       highlights:["Hua Hin City Tour","Bangkok City Highlights","Golden Buddha Temple","Marble Temple Visit"] },
  { id:"thailand-cultural-trip",  title:"Thailand Cultural Trip",  subtitle:"Authentic Thai experiences",       dest:"Thailand", slug:"thailand", nights:3, days:4, startsFrom:34999,  route:"3N Chiang Mai",                                                 highlights:["Alms Giving Ceremony","Water Festival Experience","Elephant Nature Park Visit","Cultural City Exploration"] },
  { id:"thailand-fun-pattaya",    title:"Thailand Fun Pattaya",    subtitle:"Beach, party & nightlife",         dest:"Thailand", slug:"thailand", nights:3, days:4, startsFrom:29999,  route:"3N Pattaya",                                                    highlights:["Coral Island Experience","Beach & Water Fun","Vibrant Nightlife Scenes","Optional Water Sports"] },
  { id:"thailand-beach-escape",   title:"Thailand Beach Escape",   subtitle:"Perfect beachside getaway",        dest:"Thailand", slug:"thailand", nights:3, days:4, startsFrom:34999,  route:"3N Phuket",                                                     highlights:["Island Hopping Tour","Beach & Water Activities","Scenic Coastal Views","Relaxing Beach Time"] },
  { id:"thailand-dual-fun-trip",  title:"Thailand Dual Fun Trip",  subtitle:"Two cities, double fun",           dest:"Thailand", slug:"thailand", nights:3, days:4, startsFrom:32999,  route:"2N Pattaya | 1N Bangkok",                                       highlights:["Coral Island Tour","Chao Phraya Dinner Cruise","Tiger Park Visit","Alcazar Show Experience"] },
  { id:"thailand-classic-combo",  title:"Thailand Classic Combo",  subtitle:"Iconic Thailand highlights",       dest:"Thailand", slug:"thailand", nights:5, days:6, startsFrom:44999,  route:"3N Pattaya | 2N Bangkok",                                       highlights:["Coral Island Experience","City & Temple Tours","Dinner Cruise Experience","Tiger Park Visit"] },
  { id:"thailand-island-duo",     title:"Thailand Island Duo",     subtitle:"Beach bliss in two spots",         dest:"Thailand", slug:"thailand", nights:5, days:6, startsFrom:54999,  route:"3N Phuket | 2N Krabi",                                          highlights:["Phi Phi Island Tour","James Bond Island Visit","Krabi 4 Island Tour","Phuket Fantasea Show"] },
  { id:"thailand-triple-escape",  title:"Thailand Triple Escape",  subtitle:"Explore three Thai gems",          dest:"Thailand", slug:"thailand", nights:6, days:7, startsFrom:69999,  route:"2N Pattaya | 2N Bangkok | 2N Phuket",                           highlights:["Coral Island Experience","James Bond Island Tour","Dream World Bangkok","Snow Town Experience"] },
  { id:"thailand-scenic-trip",    title:"Thailand Scenic Trip",    subtitle:"Explore Thailand's landscapes",    dest:"Thailand", slug:"thailand", nights:7, days:8, startsFrom:82999,  route:"2N Bangkok | 2N Phi Phi Islands | 1N Krabi | 2N Phuket",        highlights:["Phi Phi Island Tour","Krabi 4 Island Tour","Phuket Beach Leisure","Multi-Island Experience"] },
  { id:"thailand-grand-trio",     title:"Thailand Grand Trio",     subtitle:"Best of Thailand in one trip",     dest:"Thailand", slug:"thailand", nights:9, days:10, startsFrom:95999, route:"3N Bangkok | 3N Chiang Mai | 3N Phuket",                        highlights:["Ayutthaya Day Trip","Temple & Heritage Visits","Phi Phi Island Tour","Elephant Sanctuary Visit"] },
  { id:"thailand-mega-circuit",   title:"Thailand Mega Circuit",   subtitle:"Explore Thailand end-to-end",      dest:"Thailand", slug:"thailand", nights:9, days:10, startsFrom:109999,route:"3N Phuket | 2N Krabi | 2N Pattaya | 2N Bangkok",               highlights:["Phi Phi Island Tour","Coral Island Excursion","Alcazar Show Experience","Golden Buddha Temple"] },
].map(makePkg);

const VIETNAM_PKGS = [
  { id:"vietnam-capital-trip",    title:"Vietnam Capital Trip",    subtitle:"Explore Hanoi's highlights",       dest:"Vietnam", slug:"vietnam", nights:3,  days:4,  startsFrom:39999,  route:"3N Hanoi",                                                             highlights:["Old Quarter Exploration","City Sightseeing Tour","Cultural Experiences","Local Markets & Food"] },
  { id:"vietnam-danang-break",    title:"Vietnam Danang Break",    subtitle:"Relax by Danang beaches",          dest:"Vietnam", slug:"vietnam", nights:3,  days:4,  startsFrom:42999,  route:"3N Danang",                                                            highlights:["Beachside Relaxation","Ba Na Hills Excursion","Golden Bridge Visit","Scenic Coastal Views"] },
  { id:"vietnam-city-heritage",   title:"Vietnam City Heritage",   subtitle:"Explore historic Vietnam",         dest:"Vietnam", slug:"vietnam", nights:3,  days:4,  startsFrom:45999,  route:"2N Danang | 1N Hoi An",                                                highlights:["Dinner on Cruise","Ba Na Hills Visit","Hoi An Ancient Town","Scenic Coastal Views"] },
  { id:"vietnam-phu-quoc-trip",   title:"Vietnam Phu Quoc Trip",   subtitle:"Beachside luxury escape",          dest:"Vietnam", slug:"vietnam", nights:3,  days:4,  startsFrom:44999,  route:"3N Phu Quoc Island",                                                   highlights:["Vinpearl Safari Visit","Hon Thom Cable Car Ride","4 Island Hopping Tour","Beach & Island Vibes"] },
  { id:"vietnam-bay-escape",      title:"Vietnam Bay Escape",      subtitle:"Limestone views & waters",         dest:"Vietnam", slug:"vietnam", nights:4,  days:5,  startsFrom:52999,  route:"3N Hanoi | 1N Halong Bay",                                             highlights:["Halong Overnight Cruise","Scenic Limestone Views","Hanoi City Exploration","Cruise Dining Experience"] },
  { id:"vietnam-scenic-trio",     title:"Vietnam Scenic Trio",     subtitle:"Nature, city & cruise combo",      dest:"Vietnam", slug:"vietnam", nights:4,  days:5,  startsFrom:64999,  route:"2N Hanoi | 1N Sapa | 1N Ha Long",                                      highlights:["Muong Hoa Valley Trek","Halong Bay Cruise","Morning Tai Chi Session","Mountain & Valley Views"] },
  { id:"vietnam-city-combo",      title:"Vietnam City Combo",      subtitle:"Culture across cities",            dest:"Vietnam", slug:"vietnam", nights:4,  days:5,  startsFrom:69999,  route:"2N Ho Chi Minh City | 1N Hanoi | 1N Ha Long Bay",                      highlights:["Halong Bay Cruise","Cu Chi Tunnels Visit","Mekong Delta Tour","Culture & Cruise Combo"] },
  { id:"vietnam-island-bliss",    title:"Vietnam Island Bliss",    subtitle:"Pure island relaxation",           dest:"Vietnam", slug:"vietnam", nights:5,  days:6,  startsFrom:69999,  route:"5N Phu Quoc Island",                                                   highlights:["Private Island Hopping","Hon Thom Cable Car Ride","VinWonders Theme Park","Vinpearl Safari Visit"] },
  { id:"vietnam-eco-escape",      title:"Vietnam Eco Escape",      subtitle:"Into Vietnam's natural side",      dest:"Vietnam", slug:"vietnam", nights:5,  days:6,  startsFrom:74999,  route:"3N Hanoi | 1N Mai Chau | 1N Nam Cat Island",                           highlights:["Mai Chau Village Visit","Nam Cat Island Escape","Cycling & Hiking Tours","Kayaking Experience"] },
  { id:"vietnam-island-and-city", title:"Vietnam Island & City",   subtitle:"City vibes to beach calm",         dest:"Vietnam", slug:"vietnam", nights:6,  days:7,  startsFrom:79999,  route:"3N Phu Quoc Island | 2N Hanoi | 1N Halong Bay",                        highlights:["Halong Bay Cruise Stay","Beach & Island Relaxation","Hanoi City Exploration","Scenic Bay Experiences"] },
  { id:"vietnam-grand-trio",      title:"Vietnam Grand Trio",      subtitle:"Best of Vietnam in one trip",      dest:"Vietnam", slug:"vietnam", nights:6,  days:7,  startsFrom:82999,  route:"2N Hanoi | 2N Halong | 2N Ho Chi Minh",                                highlights:["Halong Luxury Cruise","Kayaking in Halong Bay","Cu Chi Tunnels Visit","Mekong Delta Experience"] },
  { id:"vietnam-city-circuit",    title:"Vietnam City Circuit",    subtitle:"Urban Vietnam experience",         dest:"Vietnam", slug:"vietnam", nights:8,  days:9,  startsFrom:94999,  route:"2N Danang | 1N Hanoi | 1N Halong Bay | 2N Hanoi | 2N Ho Chi Minh City", highlights:["Halong Bay Cruise","Multi-City Exploration","Cultural & Scenic Mix","Beach + City Experiences"] },
  { id:"vietnam-epic-explorer",   title:"Vietnam Epic Explorer",   subtitle:"Grand adventure across Vietnam",   dest:"Vietnam", slug:"vietnam", nights:9,  days:10, startsFrom:119999, route:"2N Hanoi | 1N Halong | 1N Sapa | 2N Danang | 3N Ho Chi Minh",          highlights:["Halong Bay Cruise Stay","Sapa Hill Experience","Multi-City Exploration","Culture + Nature Combo"] },
  { id:"vietnam-total-journey",   title:"Vietnam Total Journey",   subtitle:"From north to south Vietnam",      dest:"Vietnam", slug:"vietnam", nights:10, days:11, startsFrom:134999, route:"3N Hanoi | 1N Halong Bay | 2N Danang | 4N Phu Quoc Island",            highlights:["Halong Bay Cruise","Beach & Island Relaxation","Multi-City Exploration","Scenic & Cultural Mix"] },
].map(makePkg);

const SRILANKA_PKGS = [
  { id:"srilanka-mega-circuit",  title:"Sri Lanka Mega Circuit", subtitle:"Explore Sri Lanka end-to-end", dest:"Sri Lanka", slug:"sri-lanka", nights:12, days:13, startsFrom:129999, route:"1N Negombo | 1N Anuradhapura | 1N Jaffna | 2N Sigiriya | 2N Kandy | 1N Nuwara Eliya | 1N Mirissa | 2N Bentota | 1N Colombo", highlights:["10+ Destinations Covered","Ramayana Trail Experience","Sigiriya Rock Fortress","Pinnawala Elephant Visit"] },
  { id:"srilanka-scenic-tour",   title:"Sri Lanka Scenic Tour", subtitle:"Nature meets island charm", dest:"Sri Lanka", slug:"sri-lanka", nights:10, days:11, startsFrom:109999, route:"1N Colombo | 2N Habarana | 2N Kandy | 1N Nuwara Eliya | 1N Yala | 3N Bentota", highlights:["Colombo, Kandy & Bentota","Sigiriya Rock Fortress","Yala Wildlife Safari","Scenic Hill Country Stay"] },
  { id:"srilanka-nature-trip",   title:"Sri Lanka Nature Trip", subtitle:"Scenic island experiences", dest:"Sri Lanka", slug:"sri-lanka", nights:8, days:9, startsFrom:92999, route:"1N Colombo | 2N Dambulla | 1N Kandy | 1N Nuwara Eliya | 1N Bentota | 1N Yala National Park | 1N Galle", highlights:["Sigiriya Rock Fortress","Scenic Train Ride","Yala Safari Adventure","Pinnawala Elephant Visit"] },
  { id:"srilanka-hill-escape",   title:"Sri Lanka Hill Escape", subtitle:"Hills, beaches & culture", dest:"Sri Lanka", slug:"sri-lanka", nights:8, days:9, startsFrom:89999, route:"2N Sigiriya | 1N Kandy | 1N Nuwara Eliya | 1N Ella | 1N Tissamaharama | 1N Bentota | 1N Colombo", highlights:["Sigiriya to Colombo Route","Scenic Hill Country Stay","Ella Mountain Landscapes","Bentota Beach Relaxation"] },
  { id:"srilanka-wild-trail",    title:"Sri Lanka Wild Trail", subtitle:"Wildlife & coastal journey", dest:"Sri Lanka", slug:"sri-lanka", nights:7, days:8, startsFrom:82999, route:"2N Kandy | 1N Nuwara Eliya | 1N Yala National Park | 1N Galle | 1N Bentota | 1N Colombo", highlights:["Pinnawala Elephant Visit","Yala Safari Adventure","Galle Fort Exploration","Turtle Conservation Visit"] },
  { id:"srilanka-east-coast",    title:"Sri Lanka East Coast", subtitle:"Coastal adventure awaits", dest:"Sri Lanka", slug:"sri-lanka", nights:7, days:8, startsFrom:79999, route:"2N Trincomalee | 1N Pasikuda | 2N Arugam Bay | 2N Colombo", highlights:["East Coast Beach Circuit","Whale Watching Experience","Surfing in Arugam Bay","Relaxing Beach Resorts"] },
  { id:"lanka-classic-escape",   title:"Lanka Classic Escape", subtitle:"Explore the island classics", dest:"Sri Lanka", slug:"sri-lanka", nights:6, days:7, startsFrom:74999, route:"1N Nuwara Eliya | 1N Ella | 1N Sigiriya | 1N Kandy | 1N Bentota | 1N Colombo", highlights:["Ella & Nuwara Eliya Stay","Tea Factory Experience","Pinnawala Elephant Visit","Cultural Dance Show"] },
  { id:"lanka-leisure-trip",     title:"Lanka Leisure Trip", subtitle:"Leisure meets scenic beauty", dest:"Sri Lanka", slug:"sri-lanka", nights:5, days:6, startsFrom:64999, route:"1N Kandy | 2N Bentota | 1N Colombo | 1N Nuwara Eliya", highlights:["Temple of the Tooth Visit","Pinnawala Elephant Orphanage","Madu River Safari","Colombo City Exploration"] },
  { id:"srilanka-quick-trip",    title:"Sri Lanka Quick Trip", subtitle:"Island highlights in days", dest:"Sri Lanka", slug:"sri-lanka", nights:4, days:5, startsFrom:54999, route:"1N Kandy | 1N Galle | 1N Nuwara Eliya | 1N Colombo", highlights:["Whale Watching in Mirissa","Yala Jeep Safari","Cultural Dance Experience","Scenic Hill Country Stay"] },
  { id:"srilanka-mini-tour",     title:"Sri Lanka Mini Tour", subtitle:"Small trip, big memories", dest:"Sri Lanka", slug:"sri-lanka", nights:3, days:4, startsFrom:44999, route:"1N Kandy | 1N Bentota | 1N Colombo", highlights:["Kandy & Bentota Stay","Traditional Dance Show","Bentota Beach Relaxation","Colombo City Exploration"] },
  { id:"srilanka-city-escape",   title:"Sri Lanka City Escape", subtitle:"Urban meets island charm", dest:"Sri Lanka", slug:"sri-lanka", nights:3, days:4, startsFrom:42999, route:"1N Kandy | 1N Galle | 1N Colombo", highlights:["Coastal City Exploration","Cultural Sightseeing","Heritage Town Experience","Relaxing Island Journey"] },
].map(makePkg);

const DUBAI_PKGS = [
  { id:"dubai-ultimate-trip",    title:"Dubai Ultimate Trip",    subtitle:"Luxury & adventure escape",    dest:"Dubai", slug:"dubai", nights:7, days:8, startsFrom:129999, route:"7N Dubai",                highlights:["Dhow Marina Dinner Cruise","Deluxe Desert Safari","Atlantis Aquaventure Park","Abu Dhabi City Tour"] },
  { id:"dubai-city-yas-trip",    title:"Dubai City & Yas Trip",  subtitle:"Two cities, one luxury trip",  dest:"Dubai", slug:"dubai", nights:6, days:7, startsFrom:119999, route:"5N Dubai | 1N Abu Dhabi", highlights:["Marina Dhow Dinner Cruise","Desert Safari Experience","Atlantis Aquaventure Park","Yas Island Theme Parks"] },
  { id:"dubai-iconic-trip",      title:"Dubai Iconic Trip",      subtitle:"Best of Dubai in one trip",    dest:"Dubai", slug:"dubai", nights:5, days:6, startsFrom:99999,  route:"5N Dubai",                highlights:["Desert Safari Adventure","Burj Khalifa Visit","Marina Dhow Cruise","Museum of the Future"] },
  { id:"dubai-twin-city-tour",   title:"Dubai Twin City Tour",   subtitle:"Explore UAE's top cities",     dest:"Dubai", slug:"dubai", nights:5, days:6, startsFrom:109999, route:"3N Dubai | 2N Abu Dhabi", highlights:["Dhow Dinner Cruise","Desert Safari Experience","Ferrari World Visit","Yas Waterworld Fun"] },
  { id:"dubai-signature-trip",   title:"Dubai Signature Trip",   subtitle:"Premium Dubai highlights",     dest:"Dubai", slug:"dubai", nights:4, days:5, startsFrom:89999,  route:"4N Dubai",                highlights:["Marina Dinner Cruise","Burj Khalifa Visit","Atlantis Aquaventure","City Tour & Dubai Mall"] },
  { id:"dubai-city-break",       title:"Dubai City Break",       subtitle:"Short trip, big experiences",  dest:"Dubai", slug:"dubai", nights:3, days:4, startsFrom:89999,  route:"3N Dubai",                highlights:["Marina Dinner Cruise","Burj Khalifa Visit","Desert Safari Experience","Dubai Frame Visit"] },
].map(makePkg);

const DEST_PACKAGES = {
  andaman:   ANDAMAN_PKGS,
  kashmir:   KASHMIR_PKGS,
  rajasthan: RAJASTHAN_PKGS,
  kerala:    KERALA_PKGS,
  tamilnadu: TAMILNADU_PKGS,
  bali:      BALI_PKGS,
  maldives:  MALDIVES_PKGS,
  singapore: SINGAPORE_PKGS,
  malaysia:  MALAYSIA_PKGS,
  thailand:  THAILAND_PKGS,
  vietnam:   VIETNAM_PKGS,
  "sri-lanka": SRILANKA_PKGS,
  dubai:     DUBAI_PKGS,
};

// ─── DESTINATION PAGE DATA ────────────────────────────────────────────────────
const DEST_ATTRACTIONS = {
  goa: [
    { id:"goa1", title:"Baga Beach", description:"A lively beach popular for water sports and nightlife.", tag:"Beach", imageUrl:`${IMAGE_BASE}Travel-Rethinkways-Goa-Baga-Beach.avif` },
    { id:"goa2", title:"Calangute Beach", description:"The largest and most popular beach in North Goa.", tag:"Beach", imageUrl:`${IMAGE_BASE}Travel-Rethinkways-Goa-Calangute-Beach.avif` },
    { id:"goa3", title:"Anjuna Beach", description:"Known for its flea market, rocky shores and trance culture.", tag:"Beach", imageUrl:`${IMAGE_BASE}Travel-Rethinkways-Goa-Anjuna-Beach.avif` },
    { id:"goa4", title:"Dudhsagar Waterfalls", description:"A magnificent four-tiered waterfall on the Goa-Karnataka border.", tag:"Nature", imageUrl:`${IMAGE_BASE}Travel-Rethinkways-Goa-Dudhsagar-Waterfalls.avif` },
  ],
  bali: [
    { id: "attr-bali-kelingking-beach", title: "KelingKing Beach", description: "Iconic T-Rex shaped cliff with breathtaking ocean views, one of Bali’s most Instagrammable spots.", tag: "Attraction", imageUrl: `${IMAGE_BASE}travel-rethinkways-bali-fire-show-20260527114606.avif` },
    { id: "attr-bali-mount-batur", title: "Mount Batur", description: "An active volcano popular for sunrise treks with panoramic views above the clouds.", tag: "Attraction", imageUrl: `${IMAGE_BASE}travel-rethinkways-bali-mount-batur-20260527115616.avif` },
    { id: "attr-bali-tanah-lot-temple", title: "Tanah Lot Temple", description: "A stunning sea temple known for magical sunset views and cultural significance.", tag: "Attraction", imageUrl: `${IMAGE_BASE}travel-rethinkways-bali-tanah-lot-temple-20260527115701.avif` },
    { id: "attr-bali-tegallalang-rice-terraces", title: "Tegallalang Rice Terraces", description: "Beautiful green landscapes showcasing Bali’s traditional farming and scenic beauty.", tag: "Attraction", imageUrl: `${IMAGE_BASE}travel-rethinkways-bali-tegallalang-rice-terraces-20260527115729.avif` },
  ],
  maldives: [
    { id: "attr-maldives-male-city", title: "Male City", description: "Male City", tag: "Attraction", imageUrl: `${IMAGE_BASE}travel-rethinkways-maldives-male-city-20260527134511.avif` },
    { id: "attr-maldives-banana-reef", title: "Banana Reef", description: "Banana Reef", tag: "Attraction", imageUrl: `${IMAGE_BASE}travel-rethinkways-maldives-banana-reef-20260527134739.avif` },
    { id: "attr-maldives-alimatha-island", title: "Alimatha Island", description: "Alimatha Island", tag: "Attraction", imageUrl: `${IMAGE_BASE}travel-rethinkways-maldives--alimatha-island-20260527134812.avif` },
    { id: "attr-maldives-vaadhoo-island", title: "Vaadhoo Island", description: "Vaadhoo Island", tag: "Attraction", imageUrl: `${IMAGE_BASE}travel-rethinkways-maldives-vaadhoo-island-20260527134834.avif` },
  ],
  himachal: [
    { id:"him1", title:"Solang Valley", description:"A scenic valley near Manali known for skiing and adventure sports.", tag:"Adventure", imageUrl:`${IMAGE_BASE}Travel-Rethinkways-Himachal-Solang-Valley.avif` },
    { id:"him2", title:"Rohtang Pass", description:"A high mountain pass in the Himalayas with breathtaking views.", tag:"Nature", imageUrl:`${IMAGE_BASE}Travel-Rethinkways-Himachal-Rohtang-Pass.avif` },
    { id:"him3", title:"Mall Road, Shimla", description:"The main promenade of Shimla lined with shops and eateries.", tag:"Urban", imageUrl:`${IMAGE_BASE}Travel-Rethinkways-Himachal-Mall-Road-Shimla.avif` },
    { id:"him4", title:"Hadimba Temple, Manali", description:"An ancient cave temple surrounded by cedar forests in Manali.", tag:"Spiritual", imageUrl:`${IMAGE_BASE}Travel-Rethinkways-Himachal-Hadimba-Temple.avif` },
  ],
  andaman: [
    { id:"andr1", title:"Radhanagar Beach, Havelock", description:"One of Asia's best beaches with white sand and clear waters.", tag:"Beach",      imageUrl:`${IMAGE_BASE}Travel-Rethinkways-Andaman-Radhanagar-Beach.avif` },
    { id:"andr2", title:"Cellular Jail, Port Blair",   description:"A former British prison, now a national memorial.",          tag:"Historical",  imageUrl:`${IMAGE_BASE}Travel-Rethinkways-Andaman-Cellular-Jail.avif` },
    { id:"andr3", title:"Ross Island",                 description:"An abandoned British settlement with colonial ruins.",        tag:"Historical",  imageUrl:`${IMAGE_BASE}Travel-Rethinkways-Andaman-Ross-Island.avif` },
    { id:"andr4", title:"Baratang Limestone Caves",    description:"Unique mangrove forests and limestone formations.",           tag:"Nature",      imageUrl:`${IMAGE_BASE}Travel-Rethinkways-Andaman-Limestone-Caves.avif` },
  ],
  kashmir: [
    { id:"kas1", title:"Shikara Ride", description:"A traditional wooden boat ride on the serene Dal Lake.", tag:"Experience", imageUrl:`${IMAGE_BASE}Travel-Rethinkways-Kashmir-Shikara-Ride.avif` },
    { id:"kas2", title:"Gulmarg Gondola", description:"One of the world's highest cable cars with panoramic snow views.", tag:"Adventure", imageUrl:`${IMAGE_BASE}Travel-Rethinkways-Kashmir-Gulmarg-Gondola.avif` },
    { id:"kas3", title:"Betaab Valley", description:"A picturesque valley near Pahalgam with lush meadows and streams.", tag:"Nature", imageUrl:`${IMAGE_BASE}Travel-Rethinkways-Kashmir-Betaab-Valley.avif` },
    { id:"kas4", title:"Sonmarg", description:"The Meadow of Gold, a glacier-fed alpine valley in Kashmir.", tag:"Nature", imageUrl:`${IMAGE_BASE}Travel-Rethinkways-Kashmir-Sonamarg.avif` },
  ],
  kerala: [
    { id:"ker1", title:"Alleppey Backwaters", description:"Iconic network of canals, lakes and lagoons best explored by houseboat.", tag:"Nature", imageUrl:`${IMAGE_BASE}Travel-Rethinkways-Kerala-Alleppey-Backwaters.avif` },
    { id:"ker2", title:"Munnar Tea Gardens", description:"Rolling highlands blanketed with verdant tea plantations.", tag:"Nature", imageUrl:`${IMAGE_BASE}Travel-Rethinkways-Kerala-Munnar-Tea-Gardens.avif` },
    { id:"ker3", title:"Periyar Wildlife Sanctuary", description:"A protected reserve famous for elephants, tigers and boat safaris.", tag:"Wildlife", imageUrl:`${IMAGE_BASE}Travel-Rethinkways-Kerala-Periyar-Wildlife.avif` },
    { id:"ker4", title:"Athirappilly Waterfalls", description:"Kerala's largest waterfall, nicknamed the Niagara of India.", tag:"Nature", imageUrl:`${IMAGE_BASE}Travel-Rethinkways-Kerala-Athirappilly-Falls.avif` },
  ],
  tamilnadu: [
    { id:"tn1", title:"Meenakshi Amman Temple", description:"A magnificent Dravidian temple complex in the heart of Madurai.", tag:"Spiritual", imageUrl:`${IMAGE_BASE}Travel-Rethinkways-Tamilnadu-Meenakshi-Temple.avif` },
    { id:"tn2", title:"Rameswaram Ramanathaswamy Temple", description:"One of India's holiest shrines, famous for its long corridors.", tag:"Spiritual", imageUrl:`${IMAGE_BASE}Travel-Rethinkways-Tamilnadu-Rameswaram-Temple.avif` },
    { id:"tn3", title:"Ooty Botanical Gardens", description:"A century-old garden in the Nilgiri hills with exotic plant species.", tag:"Nature", imageUrl:`${IMAGE_BASE}Travel-Rethinkways-Tamilnadu-Ooty-Botanical-Gardens.avif` },
    { id:"tn4", title:"Kodaikanal Lake", description:"A star-shaped lake in a charming hill station ideal for boating.", tag:"Nature", imageUrl:`${IMAGE_BASE}Travel-Rethinkways-Tamilnadu-Kodaikanal-Lake.avif` },
  ],
  rajasthan: [
    { id:"raj1", title:"Hawa Mahal", description:"The iconic Palace of Winds with its ornate honeycomb facade in Jaipur.", tag:"Historical", imageUrl:`${IMAGE_BASE}Travel-Rethinkways-Rajasthan-Hawa-Mahal.avif` },
    { id:"raj2", title:"Amber Fort", description:"A majestic hilltop fort-palace complex near Jaipur.", tag:"Historical", imageUrl:`${IMAGE_BASE}Travel-Rethinkways-Rajasthan-Amber-Fort.avif` },
    { id:"raj3", title:"City Palace, Udaipur", description:"A grand palace complex on the banks of Lake Pichola.", tag:"Historical", imageUrl:`${IMAGE_BASE}Travel-Rethinkways-Rajasthan-City-Palace-Udaipur.avif` },
    { id:"raj4", title:"Lake Pichola", description:"A serene artificial lake in Udaipur with island palaces and boat rides.", tag:"Nature", imageUrl:`${IMAGE_BASE}Travel-Rethinkways-Rajasthan-Lake-Pichola.avif` },
  ],
};

const DEST_ARTICLE_TAGS = {
  andaman: ["andaman"],
  kashmir: ["kashmir", "jammu-and-kashmir"],
  rajasthan: ["rajasthan"],
  kerala: ["kerala"],
  bali: ["bali"],
};

const DEST_FAQS = {
  andaman: [
    { id:"af1", q:"What is the best time to visit the Andaman & Nicobar Islands?", points:["October to March: Best weather for sightseeing and water activities","April to May: Hot but suitable for beach holidays","June to September: Monsoon season with limited water activities"] },
    { id:"af2", q:"How can I reach the Andaman & Nicobar Islands?",                points:["By Air: Veer Savarkar International Airport, Port Blair","By Sea: Passenger ships from Chennai, Kolkata, and Visakhapatnam"] },
    { id:"af3", q:"How many days are ideal for an Andaman trip?",                  points:["5 to 7 days are ideal to comfortably explore the main islands and key attractions."] },
    { id:"af4", q:"What activities can tourists do in the Andaman?",                points:["Scuba diving","Snorkeling","Sea walking","Mangrove kayaking","Glass-bottom boat rides","Beach hopping"] },
    { id:"af5", q:"What food should I try in the Andaman?",                         points:["Fresh seafood","Coconut-based curries","Grilled fish","Prawns and crabs","Local island-style cuisine"] },
  ],
  kashmir: [
    { id:"kf1", q:"What is the best time to visit Kashmir?",   points:["March to May: Spring blooms, gardens in full colour","June to August: Pleasant summer, ideal for sightseeing","December to February: Snowfall & winter sports"] },
    { id:"kf2", q:"How can I reach Kashmir?",                   points:["By Air: Srinagar International Airport","By Train: Nearest railhead is Jammu Tawi","By Road: Well-connected via NH44 from Jammu"] },
    { id:"kf3", q:"What are the top places to visit?",          points:["Srinagar","Gulmarg","Pahalgam","Sonmarg","Dal Lake","Betaab Valley","Yusmarg"] },
    { id:"kf4", q:"Is Kashmir good for honeymoon trips?",       points:["Absolutely — Kashmir is one of India's top honeymoon destinations, offering romantic houseboats, snow experiences, scenic valleys, and luxury stays."] },
  ],
  bali: [
    { id:"bf1", q:"What is the best time to visit Bali?",  points:["April to October, during the dry season, is ideal for beaches, sightseeing, and outdoor activities."] },
    { id:"bf2", q:"Do Indians need a visa for Bali?",       points:["No, Indians get Visa on Arrival (VoA) in Bali for up to 30 days."] },
    { id:"bf3", q:"Is Bali good for honeymoon?",            points:["Yes, Bali is one of the most popular honeymoon destinations due to private villas, beaches, and romantic experiences."] },
    { id:"bf4", q:"What are the best places to visit?",     points:["Ubud","Kuta","Seminyak","Nusa Penida","Uluwatu","Tanah Lot Temple"] },
  ],
};

DEST_FAQS.kashmir = [
  { id:"kf1", q:"What is the best time to visit Kashmir?", points:["March to May: Spring blooms, gardens in full colour","June to August: Pleasant summer, ideal for sightseeing","December to February: Snowfall & winter sports"] },
  { id:"kf2", q:"How can I reach Kashmir?", points:["By Air: Srinagar International Airport (direct flights from major Indian cities)","By Train: Nearest railhead is Jammu Tawi","By Road: Well-connected via NH44 from Jammu"] },
  { id:"kf3", q:"How many days are ideal for a Kashmir trip?", points:["An ideal Kashmir trip requires 5 to 7 days to comfortably cover Srinagar, Gulmarg, Pahalgam, and nearby attractions."] },
  { id:"kf4", q:"What are the top places to visit in Kashmir?", points:["Srinagar","Gulmarg","Pahalgam","Sonmarg","Dal Lake","Betaab Valley","Yusmarg"] },
  { id:"kf5", q:"Is Kashmir good for honeymoon trips?", points:["Absolutely. Kashmir is one of India's top honeymoon destinations, offering romantic houseboats, snow experiences, scenic valleys, and luxury stays."] },
  { id:"kf6", q:"What activities can tourists do in Kashmir?", points:["Shikara ride on Dal Lake","Gondola ride in Gulmarg","Skiing & snowboarding (winter)","Trekking & nature walks","Garden tours & heritage sightseeing"] },
  { id:"kf7", q:"What food should I try in Kashmir?", points:["Rogan Josh","Yakhni","Dum Aloo","Kahwa tea","Kashmiri Wazwan (traditional feast)"] },
  { id:"kf8", q:"What are the famous festivals in Kashmir?", points:["Tulip Festival (Spring)","Eid celebrations","Navroz","Shikara Festival"] },
];

DEST_FAQS.andaman = [
  { id:"af1", q:"What is the best time to visit the Andaman & Nicobar Islands?", points:["October to March: Best weather for sightseeing and water activities.","April to May: Hot but suitable for beach holidays.","June to September: Monsoon season with limited water activities."] },
  { id:"af2", q:"How can I reach the Andaman & Nicobar Islands?", points:["By Air: Veer Savarkar International Airport, Port Blair","By Sea: Passenger ships from Chennai, Kolkata, and Visakhapatnam"] },
  { id:"af3", q:"How many days are ideal for an Andaman & Nicobar Islands trip?", points:["An ideal trip to the Andaman & Nicobar Islands requires 5 to 7 days to comfortably explore the main islands and attractions."] },
  { id:"af4", q:"What are the top places to visit in the Andaman & Nicobar Islands?", points:["Port Blair","Havelock Island (Swaraj Dweep)","Neil Island (Shaheed Dweep)","Ross Island","Cellular Jail","Baratang Island","North Bay Island"] },
  { id:"af5", q:"What activities can tourists do in the Andaman & Nicobar Islands?", points:["Scuba diving","Snorkeling","Sea walking","Mangrove kayaking","Glass-bottom boat rides","Beach hopping"] },
  { id:"af6", q:"Do Indian tourists need permits to visit the Andaman & Nicobar Islands?", points:["Indian tourists do not require any special permits, while foreign nationals need a Restricted Area Permit (RAP), which is usually issued on arrival."] },
  { id:"af7", q:"What food should I try in the Andaman & Nicobar Islands?", points:["Fresh seafood","Coconut-based curries","Grilled fish","Prawns and crabs","Local island-style cuisine"] },
  { id:"af8", q:"Which festivals are celebrated in the Andaman & Nicobar Islands?", points:["Island Tourism Festival","Subhash Mela","Durga Puja","Pongal","Local cultural events"] },
];

DEST_FAQS.bali = [
  { id:"bf1", q:"What is the best time to visit Bali?", points:["The best time to visit Bali is from April to October, during the dry season. This period offers pleasant weather, ideal for beaches, sightseeing, and outdoor activities."] },
  { id:"bf2", q:"Do Indians need a visa for Bali?", points:["No, Indians get Visa on Arrival (VoA) in Bali. You just need a valid passport, return ticket, and can stay up to 30 days."] },
  { id:"bf3", q:"How much does a Bali trip cost from India?", points:["A Bali trip from India typically costs between INR 50,000 to INR 1,20,000 per person, depending on hotel category, duration, and inclusions."] },
  { id:"bf4", q:"How many days are enough for Bali?", points:["A 5 to 7 days trip is ideal to explore major attractions like Ubud, Kuta, Nusa Penida, and nearby islands."] },
  { id:"bf5", q:"Is Bali good for honeymoon?", points:["Yes, Bali is one of the most popular honeymoon destinations due to its private villas, beaches, romantic dinners, and scenic landscapes."] },
  { id:"bf6", q:"What are the best places to visit in Bali?", points:["Ubud","Kuta","Seminyak","Nusa Penida","Uluwatu","Tanah Lot Temple"] },
  { id:"bf7", q:"What currency is used in Bali?", points:["The local currency is Indonesian Rupiah (IDR). Cards are widely accepted, but it's good to carry some cash."] },
  { id:"bf8", q:"Is Bali safe for tourists?", points:["Yes, Bali is generally safe for tourists, including solo travelers and couples. Basic precautions are recommended."] },
];

DEST_FAQS.singapore = [
  { id:"sf1", q:"Do Indians need a visa for Singapore in 2026?", points:["Yes, Indians need a Singapore tourist visa, which is usually processed within 3 to 5 working days through authorized agents."] },
  { id:"sf2", q:"How expensive is Singapore compared to Dubai or Thailand?", points:["More expensive than Thailand","Similar or slightly cheaper than Dubai","But offers clean, safe and premium travel experience"] },
  { id:"sf3", q:"How much money should I carry to Singapore?", points:["Carry around SGD 200 to 400 (INR 12K to 25K) for small expenses. Cards are widely accepted everywhere."] },
  { id:"sf4", q:"Is Singapore worth visiting for 3 to 4 days?", points:["Yes, 3 to 4 days is enough to cover:","Universal Studios","Sentosa Island","Gardens by the Bay","Marina Bay"] },
  { id:"sf5", q:"Which is better: Singapore or Malaysia trip?", points:["Singapore: modern, clean, attractions","Malaysia: cheaper, more diverse","Best option: Combine both"] },
  { id:"sf6", q:"What is the best area to stay in Singapore?", points:["Orchard Road: shopping","Marina Bay: premium and central","Little India: budget-friendly"] },
  { id:"sf7", q:"Is Singapore good for a honeymoon or family trip?", points:["Yes, Singapore is ideal for both:","Couples: skyline views, luxury stays","Families: Universal Studios, Zoo, Sentosa"] },
  { id:"sf8", q:"What are the must-do experiences in Singapore?", points:["Universal Studios Singapore","Night Safari","Gardens by the Bay light show","Sentosa Island","Marina Bay Sands SkyPark"] },
  { id:"sf9", q:"Can I explore Singapore without a tour package?", points:["Yes, but packages help with:","Attraction tickets","Transfers","Time-saving itinerary","Best for first-time travelers"] },
  { id:"sf10", q:"What is the best time to visit Singapore for deals?", points:["Feb to April: best weather","July to Sept: better deals and sales"] },
];

DEST_FAQS.vietnam = [
  { id:"vf1", q:"Is Vietnam visa on arrival or e-visa for Indians?", points:["Indians need a Vietnam e-visa, which is applied online and usually approved within 3 to 5 working days."] },
  { id:"vf2", q:"Which is better: North Vietnam or South Vietnam?", points:["North (Hanoi, Ha Long Bay, Sapa): nature, mountains, culture","South (Ho Chi Minh, Mekong): city life, history","Best option: Combine both for a complete trip"] },
  { id:"vf3", q:"How much cash should I carry to Vietnam?", points:["Carry around 3 to 5 million VND (INR 10K to 18K) for small expenses. Cards are accepted in cities but cash is preferred locally."] },
  { id:"vf4", q:"Is Vietnam cheaper than Thailand or Bali?", points:["Yes, Vietnam is generally:","Cheaper than Thailand","Similar or cheaper than Bali","Great value for hotels, food and tours"] },
  { id:"vf5", q:"Is Ha Long Bay cruise worth it?", points:["Absolutely. It's one of Vietnam's top experiences:","Overnight cruise","Kayaking","Limestone island views","Must-do for first-time visitors"] },
  { id:"vf6", q:"How many days are enough for Vietnam?", points:["A 5 to 8 days trip is ideal:","Hanoi + Ha Long Bay","Da Nang / Hoi An","Ho Chi Minh City"] },
  { id:"vf7", q:"Which is better: Da Nang or Phu Quoc?", points:["Da Nang: sightseeing + nearby Hoi An","Phu Quoc: beaches + island relaxation","Choose based on relax vs explore"] },
  { id:"vf8", q:"What is the best area to stay in Hanoi?", points:["Old Quarter: best for tourists, food, nightlife","French Quarter: quieter, premium stay"] },
  { id:"vf9", q:"Is Vietnam safe for Indian tourists?", points:["Yes, Vietnam is safe and tourist-friendly. Just follow basic precautions like avoiding scams and keeping valuables secure."] },
  { id:"vf10", q:"What are the must-do experiences in Vietnam?", points:["Ha Long Bay cruise","Ba Na Hills and Golden Bridge","Hoi An lantern streets","Mekong Delta tour","Sapa trekking"] },
];

DEST_FAQS.malaysia = [
  { id:"mf1", q:"Is Malaysia visa-free for Indians in 2026?", points:["Malaysia currently offers visa-free entry for Indians (limited period). Otherwise, you can apply for an eVisa online, which is quick and simple."] },
  { id:"mf2", q:"Which is better: Langkawi or Kuala Lumpur?", points:["Langkawi: beaches, island hopping, relaxation","Kuala Lumpur: city, shopping, attractions","Best choice: Combine both in one trip"] },
  { id:"mf3", q:"How much cash should I carry to Malaysia?", points:["You should carry around MYR 300 to 500 (INR 5K to 9K) in cash. Most places accept cards, but small shops prefer cash."] },
  { id:"mf4", q:"Is Malaysia cheaper than Thailand or Bali?", points:["Malaysia is generally:","Cheaper than Singapore","Similar to Thailand","Slightly costlier than Bali (in some areas)","But offers better infrastructure + comfort"] },
  { id:"mf5", q:"Is Langkawi worth visiting?", points:["Yes, Langkawi is one of the best island destinations in Southeast Asia, known for:","Sky Bridge","Cable car","Island hopping"] },
  { id:"mf6", q:"How many days are enough for Malaysia with Langkawi?", points:["A 5 to 6 days trip is perfect:","2 to 3 days Kuala Lumpur","2 to 3 days Langkawi"] },
  { id:"mf7", q:"What is the best area to stay in Kuala Lumpur?", points:["Best areas:","Bukit Bintang: shopping and nightlife","KLCC: near Petronas Towers"] },
  { id:"mf8", q:"Is Genting Highlands worth visiting?", points:["Yes, it's ideal for:","Theme parks","Cable car ride","Cooler weather","Great for families and short trips"] },
  { id:"mf9", q:"Can I do a Malaysia trip without a package?", points:["Yes, but packages help with:","Transfers","Tours","Cost savings","Packages are better for first-time travelers"] },
  { id:"mf10", q:"What are the must-do experiences in Malaysia?", points:["Langkawi cable car","Petronas Twin Towers","Batu Caves visit","Island hopping","Genting Highlands"] },
];

// ─── INLINE ADS ───────────────────────────────────────────────────────────────
const INLINE_ADS = [
  { id:"ad-gd",      href:`${WA_ENQUIRY_BASE}?text=${encodeURIComponent("Hi! I came across GD Brand Creative on Travel Rethink Ways and I'd like to enquire about your creative services.")}`, alt:"GD Brand Creative", label:"Creative Partner" },
  { id:"ad-t360",    href:`${WA_BASE}?text=${encodeURIComponent("Hello! We'd love to know more about The Tourist 360 Maldives Honeymoon Package.")}`,                                         alt:"Tourist 360",       label:"Maldives Honeymoon" },
  { id:"ad-trw",     href:`${WA_BASE}?text=${encodeURIComponent("Hi! I saw your travel ads on mobile. Please share more about your packages!")}`,                                              alt:"Travel RethinkWays", label:"Exclusive Deals" },
  { id:"ad-nalam",   href:`${WA_BASE}?text=${encodeURIComponent("Hello! We'd love to know more about Nalam Holidays Bali Honeymoon Package.")}`,                                               alt:"Nalam Holidays",    label:"Bali Honeymoon" },
];

// ─── LOCAL STORAGE HELPERS ────────────────────────────────────────────────────
const readLS = (key, fallback = []) => {
  try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fallback; } catch { return fallback; }
};
const writeLS = (key, val) => { try { localStorage.setItem(key, JSON.stringify(val)); } catch {} };
const dispatchTourPackagesUpdate = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(TOUR_PACKAGES_UPDATED_EVENT));
  }
};
const readOriginCity = () => readLS(STORAGE.ORIGIN_CITY, DEFAULT_ORIGIN_CITY);
const writeOriginCity = (value) => writeLS(STORAGE.ORIGIN_CITY, String(value || "").trim());

const readCustomMiddlePackages = () => readLS(STORAGE.CUSTOM_PACKAGES, []);
const readDestinationPageOverrides = () => readLS(STORAGE.DEST_PAGES, {});
const writeCustomMiddlePackages = (value) => {
  writeLS(STORAGE.CUSTOM_PACKAGES, value);
  dispatchTourPackagesUpdate();
  saveTourPackagesToRemote();
};
const writeDestinationPageOverrides = (value) => {
  writeLS(STORAGE.DEST_PAGES, value);
  dispatchTourPackagesUpdate();
  saveTourPackagesToRemote();
};
const readSeedPackageOverrides = () => readLS(STORAGE.SEED_PACKAGE_OVERRIDES, []);
const writeSeedPackageOverrides = (value) => {
  writeLS(STORAGE.SEED_PACKAGE_OVERRIDES, value);
  dispatchTourPackagesUpdate();
  saveTourPackagesToRemote();
};
const readDeletedSeedPackageIds = () => readLS(STORAGE.DELETED_SEED_PACKAGES, []);
const writeDeletedSeedPackageIds = (value) => {
  writeLS(STORAGE.DELETED_SEED_PACKAGES, value);
  dispatchTourPackagesUpdate();
  saveTourPackagesToRemote();
};

const parseTourPackagesBackupFromText = (text) => {
  const raw = String(text || "");
  const marker = "TRW_TOUR_PACKAGES_BACKUP";
  const markerIndex = raw.indexOf(marker);
  if (markerIndex === -1) throw new Error("TRW_TOUR_PACKAGES_BACKUP not found in file.");

  const eqIndex = raw.indexOf("=", markerIndex);
  if (eqIndex === -1) throw new Error("Invalid backup file (missing '=').");

  const startIndex = raw.indexOf("{", eqIndex);
  if (startIndex === -1) throw new Error("Invalid backup file (missing '{').");

  const endIndex = raw.lastIndexOf("};");
  if (endIndex === -1 || endIndex <= startIndex) throw new Error("Invalid backup file (missing closing '};').");

  const jsonText = raw.slice(startIndex, endIndex + 1);
  return JSON.parse(jsonText);
};

const normalizeTourPackagesBackupPayload = (payload) => {
  const tourPackages = payload?.tourPackages ?? payload?.liveData?.tourPackages ?? payload?.liveData ?? null;
  if (!tourPackages || typeof tourPackages !== "object") {
    throw new Error("Invalid backup payload (missing tourPackages).");
  }
  return {
    customPackages: Array.isArray(tourPackages.customPackages) ? tourPackages.customPackages : [],
    destinationPages: tourPackages.destinationPages && typeof tourPackages.destinationPages === "object" ? tourPackages.destinationPages : {},
    seedPackageOverrides: Array.isArray(tourPackages.seedPackageOverrides) ? tourPackages.seedPackageOverrides : [],
    deletedSeedPackageIds: Array.isArray(tourPackages.deletedSeedPackageIds) ? tourPackages.deletedSeedPackageIds : [],
    seoMetaOverrides: tourPackages.seoMetaOverrides && typeof tourPackages.seoMetaOverrides === "object" ? tourPackages.seoMetaOverrides : {},
  };
};

const getTourPackagesSyncUrls = () => {
  if (typeof window === "undefined") return [];
  const baseUrl = typeof import.meta !== "undefined" ? (import.meta.env.BASE_URL || "/") : "/";
  const normalizedBase = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
  const custom = [
    `${normalizedBase}live-data-sync.php`,
    ...(String(window.TRW_LIVE_DATA_SYNC_URLS || "")).split(","),
    ...(String(window.TRW_LIVE_DATA_SYNC_URL || "")).split(","),
    ...(String(window.TRW_SYNC_URL || "")).split(","),
  ].map((url) => url.trim()).filter(Boolean);
  const defaults = [
    "/wp-json/trw/v1/live-data",
    "/?rest_route=/trw/v1/live-data",
  ];
  return [...new Set([...custom, ...defaults])];
};

const getTourPackagesUploadUrls = () => (
  getTourPackagesSyncUrls().filter((url) => /live-data-sync\.php(?:\?|$)/i.test(String(url || "").trim()))
);

const getTourPackagesSyncAuthHeaders = () => {
  const headers = {};
  if (typeof window === "undefined") return headers;
  const nonce = String(window.TRW_SYNC_NONCE || window.wpApiSettings?.nonce || "").trim();
  if (nonce) headers["X-WP-Nonce"] = nonce;
  try {
    const token = String(window.TRW_SYNC_TOKEN || window.localStorage.getItem("trw-sync-token") || "").trim();
    if (token) headers["X-TRW-Sync-Token"] = token;
  } catch { /* ignored */ }
  return headers;
};

const getTourPackagesSyncHeaders = () => {
  return {
    "Content-Type": "application/json",
    ...getTourPackagesSyncAuthHeaders(),
  };
};

const buildTourPackagesSyncPayload = () => ({
  tourPackages: {
    customPackages: readCustomMiddlePackages(),
    destinationPages: readDestinationPageOverrides(),
    seedPackageOverrides: readSeedPackageOverrides(),
    deletedSeedPackageIds: readDeletedSeedPackageIds(),
    seoMetaOverrides: readTourSeoMetaOverrides(),
  },
});

const parseTourPackagesRemotePayload = (data) => {
  const outer = data?.data ?? data;
  const inner = outer?.tourPackages ?? outer;
  return {
    customPackages: Array.isArray(inner?.customPackages) ? inner.customPackages : [],
    destinationPages: inner?.destinationPages && typeof inner.destinationPages === "object" ? inner.destinationPages : {},
    seedPackageOverrides: Array.isArray(inner?.seedPackageOverrides) ? inner.seedPackageOverrides : [],
    deletedSeedPackageIds: Array.isArray(inner?.deletedSeedPackageIds) ? inner.deletedSeedPackageIds : [],
    seoMetaOverrides: inner?.seoMetaOverrides && typeof inner.seoMetaOverrides === "object" ? inner.seoMetaOverrides : {},
  };
};

const createDestinationArticleBookmarkPost = (article) => {
  const bookmarkId = String(article?.id || article?.link || article?.title || "");
  const safeTitle = String(article?.title || "").trim();
  const safeExcerpt = String(article?.excerpt || article?.tag || article?.category || "").trim();
  const safeImageUrl = String(article?.imageUrl || "").trim();
  const safeImageAlt = String(article?.imageAlt || safeTitle || "Travel article").trim();

  return {
    id: bookmarkId,
    slug: slugify(safeTitle || bookmarkId),
    link: String(article?.link || "#"),
    title: { rendered: safeTitle },
    excerpt: { rendered: safeExcerpt },
    content: {
      rendered: safeImageUrl
        ? `<img src="${safeImageUrl.replace(/"/g, "&quot;")}" alt="${safeImageAlt.replace(/"/g, "&quot;")}" />`
        : "",
    },
  };
};

let _tourPackagesHydrated = false;
let _tourPackagesSaving = false;
let _tourPackagesPendingSave = false;
let _tourPackagesPollingStarted = false;

async function hydrateTourPackagesFromRemote(force = false) {
  if (typeof window === "undefined") return false;
  if (_tourPackagesHydrated && !force) return false;
  _tourPackagesHydrated = true;
  const urls = getTourPackagesSyncUrls();
  for (const url of urls) {
    try {
      const response = await fetch(url, { method: "GET", credentials: "same-origin", cache: "no-store" });
      if (!response.ok) continue;
      const json = await response.json();
      const payload = parseTourPackagesRemotePayload(json);
      writeLS(STORAGE.CUSTOM_PACKAGES, payload.customPackages);
      writeLS(STORAGE.DEST_PAGES, payload.destinationPages);
      writeLS(STORAGE.SEED_PACKAGE_OVERRIDES, payload.seedPackageOverrides);
      writeLS(STORAGE.DELETED_SEED_PACKAGES, payload.deletedSeedPackageIds);
      writeLS(STORAGE.TOUR_SEO_META, payload.seoMetaOverrides);
      dispatchTourPackagesUpdate();
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent(SEO_META_UPDATED_EVENT));
      }
      return true;
    } catch { /* try next */ }
  }
  return false;
}

async function saveTourPackagesToRemote() {
  if (typeof window === "undefined") return false;
  const urls = getTourPackagesSyncUrls();
  if (!urls.length) return false;
  if (_tourPackagesSaving) {
    _tourPackagesPendingSave = true;
    return false;
  }
  _tourPackagesSaving = true;
  _tourPackagesPendingSave = false;
  try {
    for (const url of urls) {
      try {
        const response = await fetch(url, {
          method: "POST",
          credentials: "same-origin",
          headers: getTourPackagesSyncHeaders(),
          body: JSON.stringify(buildTourPackagesSyncPayload()),
        });
        if (!response.ok) continue;
        return true;
      } catch { /* try next */ }
    }
    return false;
  } finally {
    _tourPackagesSaving = false;
    if (_tourPackagesPendingSave) saveTourPackagesToRemote();
  }
}

async function uploadTourImageToRemote(file, existingImageUrl = "") {
  if (typeof window === "undefined" || !(file instanceof File)) return null;
  const urls = getTourPackagesUploadUrls();
  if (!urls.length) return null;

  for (const url of urls) {
    try {
      const formData = new FormData();
      formData.append("action", "uploadTourImage");
      formData.append("image", file);
      formData.append("existingImageUrl", String(existingImageUrl || "").trim());

      const response = await fetch(url, {
        method: "POST",
        credentials: "same-origin",
        headers: getTourPackagesSyncAuthHeaders(),
        body: formData,
      });
      if (!response.ok) continue;

      const json = await response.json();
      const uploadedImageUrl = normalizeManagedTourImageUrl(
        json?.data?.storedImageUrl || json?.data?.imageUrl || json?.storedImageUrl || json?.imageUrl || ""
      );
      if (uploadedImageUrl) return uploadedImageUrl;
    } catch { /* try next */ }
  }

  return null;
}

function startTourPackagesRemotePolling() {
  if (typeof window === "undefined" || _tourPackagesPollingStarted) return;
  _tourPackagesPollingStarted = true;
  window.setInterval(() => {
    hydrateTourPackagesFromRemote(true);
  }, 10000);
}

const downloadBackupJS = (filename, varName, data) => {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  const payload = JSON.stringify(data, null, 2);
  const content = `window.${varName} = ${payload};\n`;
  const blob = new Blob([content], { type: "application/javascript;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${filename}.js`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

async function fetchTourPackagesRemoteData() {
  if (typeof window === "undefined") return null;
  const urls = getTourPackagesSyncUrls();
  for (const url of urls) {
    try {
      const response = await fetch(url, { method: "GET", credentials: "same-origin", cache: "no-store" });
      if (!response.ok) continue;
      return await response.json();
    } catch { /* try next */ }
  }
  return null;
}

const normalizeDetailPackage = (pkg = {}) => ({
  id: String(pkg.id || `pkg-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`),
  title: String(pkg.title || "").trim(),
  subtitle: String(pkg.subtitle || "").trim(),
  nights: Number(pkg.nights) || 0,
  days: Number(pkg.days) || 0,
  startsFrom: Number(pkg.startsFrom) || 0,
  perAdult: String(pkg.perAdult || "Per Adult").trim(),
  imageUrl: normalizeManagedTourImageUrl(pkg.imageUrl),
  imageFileName: String(pkg.imageFileName || "").trim(),
  includes: Array.isArray(pkg.includes) && pkg.includes.length ? pkg.includes : [...DEFAULT_INCLUDES],
  routes: String(pkg.routes || pkg.route || "").trim(),
  highlights: Array.isArray(pkg.highlights)
    ? pkg.highlights.map((item) => String(item).trim()).filter(Boolean)
    : String(pkg.highlights || "").split("|").map((item) => item.trim()).filter(Boolean),
  planLink: String(pkg.planLink || WA_BASE).trim(),
  tourLink: String(pkg.tourLink || "#").trim(),
  terms: String(pkg.terms || DEFAULT_TERMS).trim(),
});

const normalizeAttraction = (item = {}) => ({
  id: String(item.id || `attr-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`),
  title: String(item.title || "").trim(),
  description: String(item.description || "").trim(),
  tag: String(item.tag || "Attraction").trim(),
  imageUrl: normalizeManagedTourImageUrl(item.imageUrl),
  imageFileName: String(item.imageFileName || "").trim(),
  link: String(item.link || "#").trim(),
});

const normalizeFaq = (item = {}) => ({
  id: String(item.id || `faq-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`),
  q: String(item.q || item.question || "").trim(),
  answer: String(item.answer || "").trim(),
  points: Array.isArray(item.points)
    ? item.points.map((point) => String(point).trim()).filter(Boolean)
    : String(item.points || "").split("\n").map((point) => point.trim()).filter(Boolean),
});

const resolveInternalTourLink = (slug = "") => {
  const normalizedSlug = slugify(slug || "");
  return normalizedSlug ? tourLink(normalizedSlug) : "#";
};

const normalizePackageCount = (value) => {
  const next = Number.parseInt(String(value ?? "").trim(), 10);
  return Number.isFinite(next) && next > 0 ? next : 1;
};

const formatPackageCountLabel = (value) => `${normalizePackageCount(value)}+`;

const normalizeMiddlePackage = (pkg = {}) => ({
  id: String(pkg.id || `custom-${Date.now()}`),
  sectionCode: String(pkg.sectionCode || "india").trim() || "india",
  destination: String(pkg.destination || "").trim(),
  destinationSlug: slugify(pkg.destinationSlug || pkg.destination || ""),
  tagline: String(pkg.tagline || "").trim(),
  packageCount: normalizePackageCount(pkg.packageCount),
  startsFrom: Number(pkg.startsFrom) || 0,
  priceUnit: String(pkg.priceUnit || "Per Adult").trim() || "Per Adult",
  imageUrl: String(pkg.imageUrl || "").trim(),
  imageFileName: String(pkg.imageFileName || "").trim(),
  showShare: pkg.showShare !== false,
  showViewDetails: typeof pkg.showViewDetails === "boolean"
    ? pkg.showViewDetails
    : getDefaultShowViewDetails(pkg.destinationSlug || pkg.destination || ""),
  planLink: String(pkg.planLink || WA_BASE).trim() || WA_BASE,
  tourLink: resolveInternalTourLink(pkg.destinationSlug || pkg.destination || ""),
  seoTitle: String(pkg.seoTitle || "").trim(),
  seoDescription: String(pkg.seoDescription || "").trim(),
});

const getAllMiddlePackages = () => {
  const overrideMap = new Map(
    readSeedPackageOverrides()
      .map(normalizeMiddlePackage)
      .map((pkg) => [pkg.id, pkg])
  );
  const deletedIds = new Set(readDeletedSeedPackageIds().map((id) => String(id)));
  const seedPackages = ALL_SEED_PACKAGES
    .map(normalizeMiddlePackage)
    .filter((pkg) => !deletedIds.has(pkg.id))
    .map((pkg) => overrideMap.get(pkg.id) || pkg);

  const customPackages = readCustomMiddlePackages().map(normalizeMiddlePackage);
  return [...seedPackages, ...customPackages];
};

const buildTourBookmarkCatalog = () => {
  const middlePackages = getAllMiddlePackages();
  const middleIds = new Set(middlePackages.map((pkg) => String(pkg.id)));
  const detailCards = Object.keys(DEST_PACKAGES || {}).flatMap((slug) => {
    const destinationData = getDestinationViewData(slug);
    const destinationTitle = destinationData.title || displayDestinationName(slug);
    return (destinationData.packages || []).map((pkg) => {
      const normalized = normalizeDetailPackage(pkg);
      return normalizeMiddlePackage({
        id: normalized.id,
        sectionCode: "india",
        destination: normalized.title || destinationTitle,
        destinationSlug: slug,
        tagline: normalized.subtitle || destinationTitle,
        packageCount: 1,
        startsFrom: normalized.startsFrom,
        priceUnit: normalized.perAdult || "Per Adult",
        imageUrl: normalized.imageUrl,
        imageFileName: normalized.imageFileName,
        showShare: true,
        showViewDetails: showViewDetailsForDestination(slug),
        planLink: normalized.planLink || WA_BASE,
        tourLink: resolveInternalTourLink(slug),
        seoTitle: "",
        seoDescription: "",
      });
    });
  });

  const uniqueDetailCards = detailCards.filter((pkg) => !middleIds.has(String(pkg.id)));
  return [...middlePackages, ...uniqueDetailCards];
};

export const getAllTourBookmarkPackages = () => buildTourBookmarkCatalog();

const normalizeDestinationMatchKey = (value = "") => (
  slugify(String(value || "").replace(/\([^)]*\)/g, " ").replace(/&/g, " and "))
);

const resolveMiddleDestinationSeed = (packages = [], slug = "", title = "") => {
  const targetKeys = new Set([
    slugify(slug || ""),
    normalizeDestinationMatchKey(slug || ""),
    slugify(title || ""),
    normalizeDestinationMatchKey(title || ""),
  ].filter(Boolean));

  return packages.find((pkg) => {
    const pkgKeys = [
      slugify(pkg.destinationSlug || ""),
      normalizeDestinationMatchKey(pkg.destinationSlug || ""),
      slugify(pkg.destination || ""),
      normalizeDestinationMatchKey(pkg.destination || ""),
    ].filter(Boolean);
    return pkgKeys.some((key) => targetKeys.has(key));
  }) || null;
};

const resolveCityNameFromSlug = (citySlug = "", cities = DEFAULT_CITIES) => {
  const normalizedSlug = slugify(citySlug || "");
  if (!normalizedSlug) return "";
  const matchedCity = (cities || []).find((city) => slugify(city) === normalizedSlug);
  if (matchedCity) return matchedCity;
  return String(citySlug || "")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();
};

const getDestinationViewData = (slug) => {
  const overrides = readDestinationPageOverrides();
  const override = overrides?.[slug] || {};
  const seed = getAllMiddlePackages().find((pkg) => pkg.destinationSlug === slug);
  const fallbackTitle = seed?.destination || slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  return {
    title: String(override.title || fallbackTitle).trim(),
    showViewDetails: typeof override.showViewDetails === "boolean"
      ? override.showViewDetails
      : (typeof seed?.showViewDetails === "boolean" ? seed.showViewDetails : showViewDetailsForDestination(seed?.destination || fallbackTitle)),
    articleTags: Array.isArray(override.articleTags) && override.articleTags.length
      ? override.articleTags.map((tag) => String(tag).trim()).filter(Boolean)
      : (DEST_ARTICLE_TAGS[slug] || [slug, fallbackTitle]),
    cityLinks: Array.isArray(override.cityLinks) && override.cityLinks.length
      ? override.cityLinks.map((city) => String(city).trim()).filter(Boolean)
      : [...DEFAULT_CITIES],
    packages: Array.isArray(override.packages)
      ? override.packages.map(normalizeDetailPackage)
      : (DEST_PACKAGES[slug] || []).map(normalizeDetailPackage),
    attractions: Array.isArray(override.attractions)
      ? override.attractions.map(normalizeAttraction)
      : (DEST_ATTRACTIONS[slug] || []).map(normalizeAttraction),
    faqs: Array.isArray(override.faqs)
      ? override.faqs.map(normalizeFaq)
      : (DEST_FAQS[slug] || []).map(normalizeFaq),
  };
};

const getSeoEditorDefaultEntries = (packages = []) => {
  const destinationMap = new Map();

  packages.forEach((pkg) => {
    const slug = slugify(pkg.destinationSlug || pkg.destination || "");
    if (!slug || destinationMap.has(slug)) return;
    const fallbackSeo = getFallbackDestinationSeo(pkg.destination, slug);
    destinationMap.set(slug, {
      key: `destination:${slug}`,
      label: `${displayDestinationName(pkg.destination || slug)} Destination Page`,
      title: String(pkg.seoTitle || "").trim() || fallbackSeo.title,
      description: String(pkg.seoDescription || "").trim() || fallbackSeo.description,
      canonical: fallbackSeo.canonical,
    });
  });

  return [
    ...getBaseSiteSeoEditorEntries(),
    {
      key: "listing:all",
      label: "Tour Packages - All",
      title: seoData.tourPackages.tags?.All?.title || seoData.tourPackages.title,
      description: seoData.tourPackages.tags?.All?.description || seoData.tourPackages.description,
      canonical: seoData.tourPackages.tags?.All?.canonical || seoData.tourPackages.canonical,
    },
    {
      key: "listing:india",
      label: "Tour Packages - India",
      title: seoData.tourPackages.tags?.india?.title || seoData.tourPackages.title,
      description: seoData.tourPackages.tags?.india?.description || seoData.tourPackages.description,
      canonical: seoData.tourPackages.tags?.india?.canonical || seoData.tourPackages.canonical,
    },
    {
      key: "listing:international",
      label: "Tour Packages - International",
      title: seoData.tourPackages.tags?.international?.title || seoData.tourPackages.title,
      description: seoData.tourPackages.tags?.international?.description || seoData.tourPackages.description,
      canonical: seoData.tourPackages.tags?.international?.canonical || seoData.tourPackages.canonical,
    },
    {
      key: "listing:europe",
      label: "Tour Packages - Europe",
      title: seoData.tourPackages.tags?.europe?.title || seoData.tourPackages.title,
      description: seoData.tourPackages.tags?.europe?.description || seoData.tourPackages.description,
      canonical: seoData.tourPackages.tags?.europe?.canonical || seoData.tourPackages.canonical,
    },
    ...Array.from(destinationMap.values()).sort((a, b) => a.label.localeCompare(b.label)),
  ];
};

const resolveTourPackagesSeo = ({ view = "", section = "", destination = "", packages = [], overrides = {} }) => {
  const defaultEntries = getSeoEditorDefaultEntries(packages);
  const defaultMap = new Map(defaultEntries.map((entry) => [entry.key, entry]));

  const normalizedSection = String(section || "").trim().toLowerCase();
  const destinationSlug = slugify(destination || "");
  const seoKey = view === "destination" && destinationSlug
    ? `destination:${destinationSlug}`
    : `listing:${normalizedSection || "all"}`;

  const fallback = defaultMap.get(seoKey) || defaultMap.get("listing:all");
  const override = overrides?.[seoKey] || {};

  return {
    key: seoKey,
    title: String(override.title || fallback?.title || seoData.tourPackages.title).trim(),
    description: String(override.description || fallback?.description || seoData.tourPackages.description).trim(),
    canonical: String(fallback?.canonical || seoData.tourPackages.canonical).trim(),
    image: seoData.tourPackages.image,
  };
};

// ─── HOOKS ────────────────────────────────────────────────────────────────────
function useBookmarks(storageKey) {
  const [ids, setIds] = useState(() => new Set(readLS(storageKey, [])));
  const toggle = useCallback((id) => {
    setIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      writeLS(storageKey, [...next]);
      return next;
    });
  }, [storageKey]);
  return [ids, toggle];
}

// ─── ICONS ────────────────────────────────────────────────────────────────────
const IconWA = ({ cls = "w-5 h-5" }) => (
  <svg viewBox="0 0 192 192" className={`${cls} fill-current`} aria-hidden>
    <path d="m60.359 160.867 2.894-5.256a6 6 0 0 0-4.284-.581l1.39 5.837ZM22 170l-5.837-1.39a6 6 0 0 0 7.227 7.227L22 170Zm9.133-38.359 5.837 1.39a6 6 0 0 0-.581-4.284l-5.256 2.894ZM96 176c44.183 0 80-35.817 80-80h-12c0 37.555-30.445 68-68 68v12Zm-38.535-9.877C68.9 172.42 82.04 176 96 176v-12c-11.884 0-23.04-3.043-32.747-8.389l-5.788 10.512Zm-34.075 9.714 38.358-9.133-2.78-11.674-38.358 9.133 2.78 11.674Zm1.906-45.585-9.133 38.358 11.674 2.78 9.133-38.359-11.674-2.779ZM16 96c0 13.959 3.58 27.1 9.877 38.535l10.512-5.788C31.043 119.039 28 107.884 28 96H16Zm80-80c-44.183 0-80 35.817-80 80h12c0-37.555 30.445-68 68-68V16Zm80 80c0-44.183-35.817-80-80-80v12c37.555 0 68 30.445 68 68h12Z"/>
    <path d="M103 130H76V96h27c9.389 0 17 7.611 17 17s-7.611 17-17 17Zm-2-34H76V62h25c9.389 0 17 7.611 17 17s-7.611 17-17 17Z" fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth="12"/>
  </svg>
);

const IconArrow = ({ cls = "w-4 h-4" }) => (
  <svg viewBox="0 0 24 24" className={cls} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M7 17 17 7"/><path d="M10 7h7v7"/>
  </svg>
);

const IconShare = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.317 3.222L3.854 8.71c-.305.1-.288.526.01.652l6.05 2.593a.4.4 0 0 0 .357-.04l5.932-4.238c.184-.13.41.096.27.28l-4.237 5.932a.4.4 0 0 0-.04.356l2.593 6.05c.127.3.553.316.652.01L20.778 3.682a.4.4 0 0 0-.46-.46Z"/>
  </svg>
);

const IconBookmark = ({ filled }) => (
  <svg viewBox="0 0 384 512" className="h-[14px] w-[14px]" fill="currentColor">
    {filled
      ? <path d="M64 0C28.7 0 0 28.7 0 64L0 480c0 11.5 6.2 22.2 16.2 27.8s22.3 5.5 32.2-.4L192 421.3 335.5 507.4c9.9 5.9 22.2 6.1 32.2.4S384 491.5 384 480l0-416c0-35.3-28.7-64-64-64L64 0z"/>
      : <path d="M0 64C0 28.7 28.7 0 64 0L320 0c35.3 0 64 28.7 64 64l0 417.1c0 25.6-28.5 40.8-49.8 26.6L192 412.8 49.8 507.7C28.5 521.9 0 506.6 0 481.1L0 64zM64 48c-8.8 0-16 7.2-16 16l0 387.2 117.4-78.2c16.1-10.7 37.1-10.7 53.2 0L336 451.2 336 64c0-8.8-7.2-16-16-16L64 48z"/>
    }
  </svg>
);

const IconCheck = () => (
  <svg className="w-4 h-4 text-[#d02525]" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
  </svg>
);

const INCLUDE_ICONS = {
  "Food":         "fa-solid fa-utensils",
  "Transfer":     "fa-solid fa-car-side",
  "Stay":         "fa-solid fa-hotel",
  "Site Seeing":  "fa-solid fa-satellite-dish",
};

// ─── SHARE POPUP ──────────────────────────────────────────────────────────────
const SharePopup = memo(({ shareUrl, onClose }) => {
  const [copied, setCopied] = useState(false);
  const url = shareUrl || (typeof window !== "undefined" ? window.location.href : "");
  const enc = encodeURIComponent(url);

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1600); } catch {}
  };

  const SOCIALS = [
    { label:"Facebook",  href:`https://www.facebook.com/sharer/sharer.php?u=${enc}`,          bg:"bg-[#1877F2]",   icon:"fa-brands fa-facebook-f" },
    { label:"X",         href:`https://twitter.com/intent/tweet?url=${enc}`,                   bg:"bg-black",       icon:"fa-brands fa-x-twitter" },
    { label:"WhatsApp",  href:`https://wa.me/?text=${encodeURIComponent(`Check this tour package: ${url}`)}`, bg:"bg-[#25D366]", icon:"fa-brands fa-whatsapp" },
    { label:"Telegram",  href:`https://t.me/share/url?url=${enc}`,                             bg:"bg-[#2AABEE]",   icon:"fa-brands fa-telegram" },
    { label:"LinkedIn",  href:`https://www.linkedin.com/sharing/share-offsite/?url=${enc}`,    bg:"bg-[#0A66C2]",   icon:"fa-brands fa-linkedin-in" },
  ];

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/55 px-4" onClick={onClose}>
      <div className="relative w-full max-w-[420px] rounded-[20px] bg-white px-6 pt-8 pb-6 shadow-[0_24px_50px_rgba(0,0,0,0.28)]"
        onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <button onClick={onClose} className="absolute top-4 right-4 inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#f1f2f4] text-xl" aria-label="Close">✕</button>
        <h3 className="text-center text-[30px] font-semibold text-[#151515]">Share with Friends</h3>
        <p className="mt-2 text-center text-[14px] text-[#6b7280]">Travel is better when shared</p>

        <div className="mt-6 flex h-[56px] items-center gap-3 rounded-[12px] bg-[#f4f5f7] px-3">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#e8ebef] text-[#6b7280]">
            <i className="fa-solid fa-link text-[14px]" />
          </span>
          <p className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-[14px] text-[#3f3f46]">{url}</p>
          <button onClick={copy} className="inline-flex h-[42px] w-[42px] items-center justify-center rounded-[10px] bg-white shadow" aria-label="Copy link">
            <i className={copied ? "fa-solid fa-check text-[18px] text-[#16a34a]" : "fa-regular fa-copy text-[18px]"} />
          </button>
        </div>

        <div className="mt-6 flex justify-between gap-2">
          {SOCIALS.map((s) => (
            <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer"
              className="inline-flex min-w-0 flex-col items-center gap-2 no-underline">
              <span className={`inline-flex h-12 w-12 items-center justify-center rounded-full text-white ${s.bg}`}>
                <i className={`${s.icon} text-[21px]`} />
              </span>
              <span className="text-center text-[11px] text-[#374151]">{s.label}</span>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
});

// ─── TOUR MIDDLE CARD (listing page) ─────────────────────────────────────────
export const TourMiddleCard = memo(({ item, darkMode = false, isBookmarked, onToggle, onShare, onForm }) => {
  const destName = displayDestinationName(item.destination);
  const canViewDetails = item.showViewDetails !== false;
  const countLabel = formatPackageCountLabel(item.packageCount);

  return (
    <article className={`trw-tour-card flex w-full flex-col gap-[15px] rounded-[20px] border p-[15px] transition-all duration-300 hover:shadow-[0_6px_20px_rgba(0,0,0,0.1)] ${darkMode ? "border-[#2b2b2b] bg-[#121212] text-white" : "border-[#eeeeee] bg-white text-black"}`}>
      {/* Image */}
      <div className="relative h-[200px] w-full overflow-hidden rounded-[12px]">
        {item.imageUrl
          ? <img src={resolveTourImageSrc(item.imageUrl)} alt={destName} className="h-full w-full rounded-[12px] object-cover" loading="lazy" decoding="async" />
          : <div className="h-full w-full rounded-[12px] bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center text-slate-400 text-sm">{destName}</div>
        }
        {/* Package count badge */}
        <div className={`trw-tour-card-badge absolute top-[10px] left-[10px] rounded-[50px] py-[6px] pr-[16px] pl-[45px] text-[14px] font-semibold ${darkMode ? "bg-[#1e1e1e] text-white" : "bg-white text-black"}`}>
          <span className="absolute left-0 top-1/2 inline-flex h-[36px] w-[36px] -translate-y-1/2 items-center justify-center rounded-full bg-red-600 text-[14px] font-semibold text-white">{countLabel}</span>
          Packages
        </div>
        {/* Action buttons */}
        <div className="absolute top-[12px] right-[12px] flex items-center gap-[8px]">
          <button type="button" onClick={onToggle}
            className={`trw-tour-bookmark-btn inline-flex h-[36px] w-[36px] items-center justify-center rounded-full border p-0 shadow-[0_3px_8px_rgba(0,0,0,0.15)] transition-all ${isBookmarked ? "trw-tour-bookmark-btn-active border-red-600 bg-red-600 text-white" : "border-red-600 bg-white text-red-600"}`}
            aria-label={`${isBookmarked ? "Remove bookmark for" : "Bookmark"} ${destName}`} aria-pressed={isBookmarked}>
            <IconBookmark filled={isBookmarked} />
          </button>
          {item.showShare && (
            <button type="button" onClick={onShare}
              className="trw-tour-share-btn inline-flex h-[36px] w-[36px] cursor-pointer items-center justify-center rounded-full bg-[#25D366] p-0 text-white shadow-[0_3px_8px_rgba(0,0,0,0.15)]"
              aria-label={`Share ${destName}`}>
              <IconShare />
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col gap-[10px]">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1 pr-2">
            <h3 className={`trw-tour-card-title m-0 text-[22px] leading-[1.2] font-semibold overflow-hidden [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] ${darkMode ? "text-white" : "text-black"}`}>{destName}</h3>
            <p className={`trw-tour-card-tagline mt-1 text-[14px] leading-[1.4] overflow-hidden [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] ${darkMode ? "text-[#b0b0b0]" : "text-[#555555]"}`}>{item.tagline}</p>
          </div>
          <div className={`flex min-w-[118px] shrink-0 justify-end text-right ${darkMode ? "text-white" : "text-black"}`}>
            <div>
              <p className="trw-tour-card-starts mb-0 text-[18px] leading-none font-semibold">Starts</p>
              <p className="trw-tour-card-price mt-1 mb-0 text-[22px] leading-none font-bold">INR {formatINR(item.startsFrom)}</p>
              <p className="mt-1 mb-0 text-[10px] leading-none text-red-600">{item.priceUnit || "Per Adult"}</p>
            </div>
          </div>
        </div>

        {/* CTAs */}
        <div className="mt-auto flex w-full items-center gap-[12px]">
          <div className={canViewDetails ? "w-1/2" : "w-full"}>
            <a href={item.planLink || "#"} target="_blank" rel="noopener noreferrer"
              className="inline-flex h-[44px] w-full items-center justify-center gap-[10px] rounded-[12px] bg-[#25D366] text-[14px] font-semibold text-white no-underline shadow-[0_3px_8px_rgba(0,0,0,0.15)] hover:bg-[#1ebf5b]">
              <IconWA cls="w-[18px] h-[18px]" />Plan Or Book Now
            </a>
          </div>
          {canViewDetails ? (
            <div className="w-1/2">
              <a href={item.tourLink || "#"} target="_self" rel="noopener noreferrer"
                className="inline-flex h-[44px] w-full items-center justify-center gap-[8px] rounded-[12px] bg-red-600 text-[14px] font-[600] text-white no-underline hover:bg-[#d40000]">
                View Details <IconArrow cls="w-[17px] h-[17px]" />
              </a>
            </div>
          ) : null}
        </div>
      </div>
    </article>
  );
});

// ─── DESTINATION DETAIL PACKAGE CARD ─────────────────────────────────────────
const DestPkgCard = memo(({ pkg, isBookmarked, onToggle, onShare, darkMode = false }) => {
  const canViewDetails = showViewDetailsForDestination(pkg.destination || pkg.title);
  const dur = `${pkg.nights} Nights ${pkg.days} Days`;
  const includes = Array.isArray(pkg.includes) && pkg.includes.length ? pkg.includes : DEFAULT_INCLUDES;
  const highlights = Array.isArray(pkg.highlights) ? pkg.highlights : [];

  return (
    <article className={`flex h-full flex-col gap-[12px] rounded-[20px] border p-[15px] ${darkMode ? "border-[#2b2b2b] bg-[#121212] text-white" : "border-[#eeeeee] bg-white text-black"}`}>
      <div className={`relative h-[200px] overflow-hidden rounded-[12px] ${darkMode ? "bg-[#1f2937]" : "bg-[#e5e7eb]"}`}>
        {pkg.imageUrl
          ? <img src={resolveTourImageSrc(pkg.imageUrl)} alt={pkg.title} className="h-full w-full object-cover" loading="lazy" />
          : <div className="h-full w-full flex items-center justify-center text-slate-400 text-sm">{pkg.title}</div>
        }
        <div className="absolute top-[10px] left-[10px] rounded-[50px] bg-red-600 px-[14px] py-[6px] text-[14px] font-semibold text-white">{dur}</div>
        <div className="absolute top-[10px] right-[10px] flex items-center gap-2">
          <button type="button" onClick={onToggle}
            className={`inline-flex h-[36px] w-[36px] items-center justify-center rounded-full border p-0 ${isBookmarked ? "border-red-600 bg-red-600 text-white" : darkMode ? "border-[#2b2b2b] bg-[#121212] text-white" : "border-[#9ca3af] bg-white text-black"}`}
            aria-label={isBookmarked ? "Remove bookmark" : "Add bookmark"} aria-pressed={isBookmarked}>
            <IconBookmark filled={isBookmarked} />
          </button>
          <button type="button" onClick={onShare}
            className="inline-flex h-[36px] w-[36px] cursor-pointer items-center justify-center rounded-full border border-[#25D366] bg-[#25D366] p-0 text-white"
            aria-label="Share package">
            <IconShare />
          </button>
        </div>
      </div>

      <div className="flex flex-1 flex-col">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1 pr-2">
            <h2 className={`text-[22px] leading-[1.2] font-semibold [font-family:Poppins,sans-serif] ${darkMode ? "text-white" : "text-black"}`}>{pkg.title}</h2>
            {pkg.subtitle && <p className={`mt-1 text-[14px] ${darkMode ? "text-[#b0b0b0]" : "text-[#555]"}`}>{pkg.subtitle}</p>}
          </div>
          <div className="min-w-[118px] shrink-0 text-right">
            <p className={`text-[18px] leading-none font-semibold ${darkMode ? "text-white" : "text-black"}`}>Starts</p>
            <p className={`mt-1 text-[22px] leading-none font-bold ${darkMode ? "text-white" : "text-black"}`}>INR {formatINR(pkg.startsFrom)}</p>
            <p className="mt-1 text-[10px] leading-none text-red-600">{pkg.perAdult || "Per Adult"}</p>
          </div>
        </div>

        {/* Includes */}
        <div className={`mt-3 flex flex-wrap items-center justify-between gap-2 px-3 py-2 ${darkMode ? "bg-gradient-to-t from-white/10 to-transparent" : "bg-gradient-to-t from-black/10 to-transparent"}`}>
          {includes.map((inc) => (
            <span key={inc} className={`inline-flex items-center gap-1.5 text-[13px] ${darkMode ? "text-white/80" : "text-[#1f2937]"}`}>
              <i className={INCLUDE_ICONS[inc] || "fa-solid fa-circle-check"} aria-hidden="true" />{inc}
            </span>
          ))}
        </div>

        {/* Routes + Highlights */}
        {(pkg.routes || highlights.length > 0) && (
          <div className="mt-3">
            {pkg.routes && (
              <p className="text-[14px] leading-[1.5]">
                <span className="mr-1 text-[15px] font-semibold text-red-600 underline">Routes:</span>
                <span className={darkMode ? "text-white/75" : "text-[#374151]"}>{pkg.routes}</span>
              </p>
            )}
            {highlights.length > 0 && (
              <p className={`text-[14px] leading-[1.5] ${pkg.routes ? "mt-2" : ""}`}>
                <span className="mr-1 text-[15px] font-semibold text-red-600 underline">Highlights:</span>
                <span className={darkMode ? "text-white/75" : "text-[#374151]"}>
                  {highlights.map((h, i) => (
                    <span key={`${pkg.id}-h-${i}`}>{h}{i < highlights.length - 1 && <span className="mx-[6px] text-[#9ca3af]">|</span>}</span>
                  ))}
                </span>
              </p>
            )}
          </div>
        )}

        {/* CTAs */}
        <div className="mt-auto pt-4 flex gap-3">
          <a href={pkg.planLink || "#"} target="_blank" rel="noopener noreferrer"
            className={`inline-flex h-[44px] ${canViewDetails ? "w-1/2" : "w-full"} items-center justify-center gap-2 rounded-[12px] bg-[#25D366] px-3 text-[14px] font-semibold text-white no-underline`}>
            <IconWA cls="w-[18px] h-[18px]" />Plan Or Book Now
          </a>
          {canViewDetails ? (
            <a href={pkg.tourLink || "#"} target="_self" rel="noopener noreferrer"
              className="inline-flex h-[44px] w-1/2 items-center justify-center gap-2 rounded-[12px] bg-red-600 px-3 text-[14px] font-[600] text-white no-underline">
              View Details <IconArrow cls="w-[16px] h-[16px]" />
            </a>
          ) : null}
        </div>

        {pkg.terms && <p className={`trw-inline-card-terms mt-3 text-center text-[10px] leading-[1.4] ${darkMode ? "text-white/55" : "text-[#4b5563]"}`}>{pkg.terms}</p>}
      </div>
    </article>
  );
});

// ─── ATTRACTION CARD ──────────────────────────────────────────────────────────
// FIX: title and button are now stacked vertically (flex-col) instead of side-by-side
const AttractionCard = memo(({ item }) => {
  const href = item.link || "#";
  const titleLines = String(item.title || "").split(",");
  const isExternal = /^https?:/i.test(href);

  return (
    <article className="trw-destination-attraction-card group/attraction relative h-full min-h-[360px] overflow-hidden rounded-[18px] bg-black shadow-[0_18px_44px_rgba(0,0,0,0.16)]">
      <div className="absolute inset-0">
        {item.imageUrl ? (
          <img
            src={resolveTourImageSrc(item.imageUrl)}
            alt={item.title}
            className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover/attraction:scale-[1.04]"
            loading="lazy"
          />
        ) : (
          <div className="h-full w-full bg-gradient-to-br from-slate-700 to-slate-900" />
        )}
        <div className="absolute inset-x-0 bottom-0 h-[60%] bg-gradient-to-t from-black via-black/60 to-transparent" />
      </div>

      <span className="absolute top-0 left-[16px] z-10 inline-flex rounded-b-[4px] bg-red-600 px-3 py-[8px] text-[14px] leading-none font-semibold uppercase tracking-[0.04em] text-white">
        {item.tag || "Attraction"}
      </span>

      <div className="absolute inset-0 flex flex-col justify-between p-[18px]">
        <div />
        {/* FIXED: Changed from flex items-end justify-between to flex-col so title is above the button */}
        <div className="mt-auto flex flex-col gap-4">
          <h3 className="w-full text-[25px] leading-[1.04] font-medium text-white [font-family:'TRW_Yeseva_One','Yeseva_One',Georgia,serif] [display:-webkit-box] overflow-hidden [-webkit-box-orient:vertical] [-webkit-line-clamp:3] min-[1440px]:text-[27px]">
            {titleLines.map((line, idx) => (
              <span key={`${item.id}-title-${idx}`}>
                {idx > 0 ? <br /> : null}
                {line.trim()}
                {idx < titleLines.length - 1 ? "," : ""}
              </span>
            ))}
          </h3>

          <div className="flex justify-end">
            <a
              href={href}
              target={isExternal ? "_blank" : "_self"}
              rel="noopener noreferrer"
              className="group/cta inline-flex h-[46px] w-[46px] items-center overflow-hidden rounded-full border border-[#ef2f2f] bg-[#ef2f2f] text-white no-underline transition-all duration-300 ease-out hover:w-[172px] hover:bg-[rgba(9,9,11,0.82)]"
            >
              <span className="inline-flex h-[46px] w-[46px] shrink-0 items-center justify-center">
                <IconArrow cls="h-[18px] w-[18px]" />
              </span>
              <span className="pr-5 text-[14px] font-[700] uppercase tracking-[0.05em] whitespace-nowrap opacity-0 translate-x-2 transition-all duration-200 ease-out group-hover/cta:translate-x-0 group-hover/cta:opacity-100">
                Read More
              </span>
            </a>
          </div>
        </div>
      </div>
    </article>
  );
});

const DestinationArticleCard = memo(({ item, isBookmarked, onToggleBookmark }) => {
  const titleParts = String(item.title || "").split(/:(.+)/);
  const hasColonSplit = titleParts.length > 1 && titleParts[0].trim() && titleParts[1].trim();
  const titleWords = String(item.title || "").trim().split(/\s+/).filter(Boolean);
  const fallbackSplit = !hasColonSplit && titleWords.length > 3;
  const eyebrow = hasColonSplit
    ? `${titleParts[0].trim()}:`
    : fallbackSplit
      ? titleWords.slice(0, 3).join(" ")
      : item.excerpt || item.dateText || item.category || "Travel Rethink Ways";
  const headline = hasColonSplit
    ? titleParts[1].trim()
    : fallbackSplit
      ? titleWords.slice(3).join(" ")
      : item.title;

  return (
    <article className="trw-destination-blog-card group/article relative h-full min-h-[360px] overflow-hidden rounded-[18px] bg-black shadow-[0_18px_44px_rgba(0,0,0,0.16)]">
      <div className="absolute inset-0">
        {item.imageUrl ? (
          <img
            src={item.imageUrl}
            alt={item.imageAlt || item.title}
            className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover/article:scale-[1.04]"
            loading="lazy"
          />
        ) : (
          <div className="h-full w-full bg-gradient-to-br from-slate-700 to-slate-900" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/48 to-transparent" />
      </div>

      {item.category && (
        <span className="absolute top-0 left-[16px] z-10 inline-flex rounded-b-[4px] bg-red-600 px-3 py-[8px] text-[14px] leading-none font-semibold uppercase tracking-[0.04em] text-white">
          {item.category}
        </span>
      )}

      <div className="absolute inset-0 flex flex-col justify-between p-[18px]">
        <div className="flex items-start justify-end gap-3">
          <button
            type="button"
            onClick={onToggleBookmark}
            className={`ml-auto inline-flex h-[38px] w-[38px] items-center justify-center rounded-full border shadow-[0_8px_18px_rgba(0,0,0,0.18)] ${isBookmarked ? "border-red-600 bg-red-600 text-white" : "border-[#e5e7eb] bg-white text-black"}`}
            aria-label={`${isBookmarked ? "Remove bookmark for" : "Bookmark"} ${item.title}`}
            aria-pressed={isBookmarked}
          >
            <IconBookmark filled={isBookmarked} />
          </button>
        </div>

        <div className="mt-auto">
          <p className="max-w-[78%] text-[16px] leading-[1.2] font-medium text-white/95 [display:-webkit-box] overflow-hidden [-webkit-box-orient:vertical] [-webkit-line-clamp:1]">
            {eyebrow}
          </p>
          <h3 className="mt-2 max-w-[82%] text-[28px] leading-[1.04] font-medium text-white [font-family:'TRW_Yeseva_One','Yeseva_One',Georgia,serif] [display:-webkit-box] overflow-hidden [-webkit-box-orient:vertical] [-webkit-line-clamp:2] min-[1440px]:text-[30px]">
            {headline}
          </h3>

          <div className="mt-4 flex justify-end">
            <a
              href={item.link || "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="group/cta inline-flex h-[46px] w-[46px] items-center overflow-hidden rounded-full border border-[#ef2f2f] bg-[#ef2f2f] text-white no-underline transition-all duration-300 ease-out hover:w-[172px] hover:bg-[rgba(9,9,11,0.82)]"
            >
              <span className="inline-flex h-[46px] w-[46px] shrink-0 items-center justify-center">
                <IconArrow cls="h-[18px] w-[18px]" />
              </span>
              <span className="pr-5 text-[14px] font-[700] uppercase tracking-[0.05em] whitespace-nowrap opacity-0 translate-x-2 transition-all duration-200 ease-out group-hover/cta:translate-x-0 group-hover/cta:opacity-100">
                Read More
              </span>
            </a>
          </div>
        </div>
      </div>
    </article>
  );
});

const SimilarDestinationCard = memo(({ item, isBookmarked, onToggleBookmark, onShare, darkMode = false }) => {
  const detailsHref = item.tourLink || "#";
  const canViewDetails = item.showViewDetails !== false;

  return (
    <article className={`flex h-full flex-col gap-[12px] rounded-[20px] border p-[15px] ${darkMode ? "border-[#2b2b2b] bg-[#121212] text-white" : "border-[#eeeeee] bg-white text-black"}`}>
      <div className={`relative h-[200px] overflow-hidden rounded-[12px] ${darkMode ? "bg-[#1f2937]" : "bg-[#e5e7eb]"}`}>
        {item.imageUrl ? (
          <img
            src={resolveTourImageSrc(item.imageUrl)}
            alt={item.destination}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-slate-400 text-sm">{item.destination}</div>
        )}

	        <div className={`absolute top-[10px] left-[10px] rounded-[50px] py-[6px] pr-[16px] pl-[45px] text-[14px] font-semibold ${darkMode ? "bg-[#1e1e1e] text-white" : "bg-white text-black"}`}>
	          <span className="absolute left-0 top-1/2 inline-flex h-[36px] w-[36px] -translate-y-1/2 items-center justify-center rounded-full bg-red-600 text-[14px] font-semibold text-white">
	            {formatPackageCountLabel(item.packageCount)}
	          </span>
	          Packages
	        </div>

        <div className="absolute top-[10px] right-[10px] flex items-center gap-2">
          <button
            type="button"
            onClick={onToggleBookmark}
            className={`inline-flex h-[36px] w-[36px] items-center justify-center rounded-full border p-0 ${isBookmarked ? "border-red-600 bg-red-600 text-white" : darkMode ? "border-[#2b2b2b] bg-[#121212] text-white" : "border-[#9ca3af] bg-white text-black"}`}
            aria-label={`${isBookmarked ? "Remove bookmark for" : "Bookmark"} ${item.destination}`}
            aria-pressed={isBookmarked}
          >
            <IconBookmark filled={isBookmarked} />
          </button>
          <button
            type="button"
            onClick={onShare}
            className="inline-flex h-[36px] w-[36px] cursor-pointer items-center justify-center rounded-full border border-[#25D366] bg-[#25D366] p-0 text-white"
            aria-label={`Share ${item.destination}`}
          >
            <IconShare />
          </button>
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-2">
        <div className="flex items-start justify-between gap-3 max-[360px]:flex-col">
          <div className="min-w-0 flex-1 pr-2">
            <h3 className={`text-[22px] leading-[1.2] font-semibold ${darkMode ? "text-white" : "text-black"}`}>{item.destination}</h3>
            <p className={`text-[14px] ${darkMode ? "text-white/70" : "text-[#555]"}`}>{item.tagline}</p>
          </div>
          <div className="min-w-[118px] shrink-0 text-right max-[360px]:w-full max-[360px]:text-left">
            <p className={`text-[18px] leading-none font-semibold ${darkMode ? "text-white" : "text-black"}`}>Starts</p>
            <p className={`mt-1 text-[22px] leading-none font-bold ${darkMode ? "text-white" : "text-black"}`}>INR {formatINR(item.startsFrom)}</p>
            <p className="mt-1 text-[10px] leading-none text-red-600">{item.priceUnit || "Per Adult"}</p>
          </div>
        </div>

        <div className="mt-auto flex gap-3 max-[380px]:flex-col">
          <a
            href={item.planLink || "#"}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex h-[44px] ${canViewDetails ? "w-1/2" : "w-full"} max-[380px]:w-full items-center justify-center gap-2 rounded-[12px] bg-[#25D366] px-3 text-[13px] min-[390px]:text-[14px] font-semibold text-white no-underline`}
          >
            <IconWA cls="h-[18px] w-[18px]" />
            Plan Or Book Now
          </a>

          {canViewDetails ? (
            <a
              href={detailsHref}
              target="_self"
              rel="noopener noreferrer"
              className="inline-flex h-[44px] w-1/2 max-[380px]:w-full items-center justify-center gap-2 rounded-[12px] bg-red-600 px-3 text-[13px] min-[390px]:text-[14px] font-[600] text-white no-underline"
            >
              View Details
              <IconArrow cls="h-[16px] w-[16px]" />
            </a>
          ) : null}
        </div>
      </div>
    </article>
  );
});

// ─── FAQ ITEM ─────────────────────────────────────────────────────────────────
const FAQItem = memo(({ faq, isOpen, onToggle, darkMode = false }) => (
  <div className={`border-b py-[25px] last:border-b-0 last:pb-0 ${darkMode ? "border-white/10" : "border-[#e5e5e5]"}`}>
    <button type="button" onClick={onToggle}
      className={`flex w-full cursor-pointer items-center justify-between text-left text-[18px] sm:text-[22px] leading-[1.25] font-medium [font-family:'Poppins',sans-serif] ${darkMode ? "text-white" : "text-black"}`}>
      {faq.q}
      <span className="ml-4 text-[26px] leading-none font-light">{isOpen ? "−" : "+"}</span>
    </button>
    {isOpen && (
      <div className={`pt-3 text-[17px] leading-[1.6] ${darkMode ? "text-white/75" : "text-[#555]"}`}>
        {Array.isArray(faq.points) && faq.points.length
          ? <ul className="list-[square] space-y-2 pl-9">{faq.points.map((p) => <li key={p}>{p}</li>)}</ul>
          : <p>{faq.answer}</p>
        }
      </div>
    )}
  </div>
));

// ─── CITY LINKS ───────────────────────────────────────────────────────────────
const CityLinks = memo(({ destTitle, destSlug, cities = DEFAULT_CITIES, darkMode = false }) => {
  if (!cities.length) return null;
  return (
    <section className="mt-[30px] md:mt-[50px]">
      <div className={`overflow-hidden rounded-[12px] border ${darkMode ? "border-white/10 bg-black" : "border-[#d9d9d9] bg-white"}`}>
        <div className={`border-b px-[18px] py-[16px] ${darkMode ? "border-white/10" : "border-[#e5e5e5]"}`}>
          <h2 className={`m-0 text-left text-[22px] leading-[1.25] font-medium [font-family:Poppins,sans-serif] ${darkMode ? "text-white" : "text-[#2b2b2b]"}`}>
            Explore {destTitle} Tour Packages from India's Top Cities
          </h2>
        </div>
        <ul className="m-0 grid list-none grid-cols-1 gap-x-6 gap-y-3 p-[18px] md:grid-cols-2 xl:grid-cols-3">
          {cities.map((city) => (
            <li key={city} className="min-w-0">
              <a href={tourLink(destSlug, city)}
                className={`block text-[18px] leading-[1.35] font-medium no-underline ${darkMode ? "text-white underline decoration-white/80 underline-offset-4 hover:decoration-white" : "text-red-600 hover:underline"}`}>
                {destTitle} Tour Packages from {city}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
});

const InlineAdCard = ({ adIndex }) => {
  const [idx, setIdx] = useState(adIndex % ADS_MOBILE.length);

  useEffect(() => {
    if (ADS_MOBILE.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % ADS_MOBILE.length), 3500);
    return () => clearInterval(t);
  }, []);

  const ad = ADS_MOBILE[idx];

  return (
    <article className="block w-full max-w-[350px] mx-auto border-0 bg-transparent p-0 shadow-none text-black">
      <div className="flex items-center justify-center gap-2">
        <span className="text-[8px] tracking-[2px] text-black [writing-mode:vertical-rl] rotate-180">
          SPONSORED AD
        </span>
        <a
          href={ad.href}
          target="_self"
          rel="noopener noreferrer"
          aria-label={ad.alt}
          className="block w-full overflow-hidden rounded-[12px] bg-gradient-to-br from-slate-700 to-slate-800"
        >
          <img
            src={ad.image}
            alt={ad.alt}
            className="h-full w-full object-cover rounded-[12px]"
          />
        </a>
      </div>
      <div className="mt-2 text-center">
        <a
          href="https://travel.rethinkways.com/advertise-with-us/"
          target="_self"
          rel="noopener noreferrer"
          className="text-xs font-bold text-black hover:text-red-600"
        >
          Advertise With Us ›
        </a>
      </div>
    </article>
  );
};

// ─── SECTION HEADING ──────────────────────────────────────────────────────────
const SectionH2 = ({ children, className = "", darkMode = false }) => (
  <h2 className={`trw-section-h2 mt-0 mb-6 border-l-[4px] border-[#d02525] py-0 pr-0 pl-[15px] [font-family:Poppins,sans-serif] text-[36px] leading-none font-medium ${darkMode ? "text-white" : "text-black"} ${className}`}>
    {children}
  </h2>
);

// ─── DESTINATION PAGE ─────────────────────────────────────────────────────────
function DestinationPage({ slug, fromCity = "", darkMode = false }) {
  const [tourBMs, toggleTourBM] = useBookmarks(STORAGE.TOUR_BOOKMARKS);
  const [blogBMs, setBlogBMs] = useState(() => new Set(readBookmarkedPostIds().map(String)));
  const [shareUrl, setShareUrl] = useState("");
  const [openFaq, setOpenFaq] = useState(null);
  const [articles, setArticles] = useState([]);
  const [articlesLoading, setArticlesLoading] = useState(false);
  const [dataVersion, setDataVersion] = useState(0);
  const loadedArticlesKeyRef = useRef("");

  useEffect(() => {
    injectYesevaFont();
  }, []);

  useEffect(() => {
    const refresh = () => setDataVersion((value) => value + 1);
    const syncArticleBookmarks = () => setBlogBMs(new Set(readBookmarkedPostIds().map(String)));
    window.addEventListener("storage", refresh);
    window.addEventListener("pageshow", refresh);
    window.addEventListener("focus", refresh);
    window.addEventListener("storage", syncArticleBookmarks);
    window.addEventListener("pageshow", syncArticleBookmarks);
    window.addEventListener("focus", syncArticleBookmarks);
    return () => {
      window.removeEventListener("storage", refresh);
      window.removeEventListener("pageshow", refresh);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("storage", syncArticleBookmarks);
      window.removeEventListener("pageshow", syncArticleBookmarks);
      window.removeEventListener("focus", syncArticleBookmarks);
    };
  }, []);

  const toggleArticleBookmark = useCallback((article) => {
    const bookmarkId = String(article?.id || article?.link || article?.title || "");
    if (!bookmarkId) return;

    setBlogBMs((prev) => {
      const next = new Set(prev);
      const isRemoving = next.has(bookmarkId);

      if (isRemoving) next.delete(bookmarkId);
      else next.add(bookmarkId);

      const nextIds = Array.from(next);
      writeBookmarkedPostIds(nextIds);

      if (isRemoving) {
        removeBookmarkedPost(bookmarkId);
      } else {
        upsertBookmarkedPost(createDestinationArticleBookmarkPost(article), {
          categoryLabel: article?.category || article?.tag || "Travel Story",
          categorySlug: "",
          categoryLink: "",
        });
      }

      return next;
    });
  }, []);

  const destinationData = useMemo(() => getDestinationViewData(slug), [slug, dataVersion]);
  const packages = destinationData.packages;
  const attractions = destinationData.attractions;
  const faqs = destinationData.faqs;
  const title = destinationData.title;
  const articleTags = destinationData.articleTags;
  const articleTagsKey = useMemo(
    () => (Array.isArray(articleTags) ? articleTags.map((tag) => String(tag).trim()).filter(Boolean).join("|") : ""),
    [articleTags]
  );
  const articleCacheKey = useMemo(
    () => `${slugify(title)}::${articleTagsKey.split("|").map((tag) => slugify(tag)).filter(Boolean).join("|")}`,
    [articleTagsKey, title]
  );
  const articleRequestKey = useMemo(() => `${slug}::${title}::${articleTagsKey}`, [articleTagsKey, slug, title]);
  const cachedArticles = useMemo(() => destinationArticlesDataCache.get(articleCacheKey) || null, [articleCacheKey]);
  const cityLinks = destinationData.cityLinks;
  const activeCity = useMemo(() => {
    const cityFromQuery = resolveCityNameFromSlug(fromCity, cityLinks);
    if (cityFromQuery) return cityFromQuery;
    const storedCity = resolveCityNameFromSlug(readOriginCity(), cityLinks);
    if (storedCity) return storedCity;
    return DEFAULT_ORIGIN_CITY;
  }, [fromCity, cityLinks]);
  const pageTitle = activeCity ? `${title} Tour Packages from ${activeCity}` : `${title} Packages`;
  const allMiddlePackages = useMemo(() => getAllMiddlePackages(), [dataVersion]);
  const destSeed = useMemo(
    () => resolveMiddleDestinationSeed(allMiddlePackages, slug, title),
    [allMiddlePackages, slug, title]
  );
  const similarDestinations = useMemo(() => {
    if (!destSeed?.sectionCode) return [];

    return allMiddlePackages
      .filter((pkg) => {
        if (pkg.sectionCode !== destSeed.sectionCode) return false;
        const pkgKey = normalizeDestinationMatchKey(pkg.destinationSlug || pkg.destination || "");
        const currentKey = normalizeDestinationMatchKey(slug || title || "");
        return pkgKey !== currentKey;
      })
      .map((pkg) => ({
        ...pkg,
        packageCount: getDestinationViewData(pkg.destinationSlug).packages.length || pkg.packageCount || 0,
        showViewDetails: getDestinationViewData(pkg.destinationSlug).showViewDetails !== false,
      }))
      .filter((pkg) => pkg.packageCount > 0)
      .slice(0, 3);
  }, [allMiddlePackages, destSeed, slug, title]);

  useEffect(() => {
    if (activeCity) writeOriginCity(activeCity);
  }, [activeCity]);

  const SEO_TITLE = activeCity
    ? `${title} Tour Packages from ${activeCity} | Travel Rethink Ways`
    : `${title} Tour Packages | Travel Rethink Ways`;
  useEffect(() => { document.title = SEO_TITLE; }, [SEO_TITLE]);

  useEffect(() => {
    let ignore = false;

    if (!articleTagsKey) {
      setArticles([]);
      setArticlesLoading(false);
      loadedArticlesKeyRef.current = articleRequestKey;
      return () => { ignore = true; };
    }

    if (loadedArticlesKeyRef.current === articleRequestKey) {
      return () => { ignore = true; };
    }

    if (cachedArticles) {
      setArticles((current) => (current === cachedArticles ? current : cachedArticles));
      setArticlesLoading(false);
      loadedArticlesKeyRef.current = articleRequestKey;
      return () => { ignore = true; };
    }

    const loadArticles = async () => {
      setArticles((current) => (loadedArticlesKeyRef.current === articleRequestKey ? current : []));
      setArticlesLoading(true);
      try {
        const nextArticles = await fetchDestinationArticles(title, articleTags);
        if (!ignore) {
          setArticles(nextArticles);
          loadedArticlesKeyRef.current = articleRequestKey;
        }
      } catch (error) {
        if (!ignore) setArticles([]);
      } finally {
        if (!ignore) setArticlesLoading(false);
      }
    };

    loadArticles();
    return () => { ignore = true; };
  }, [articleRequestKey, articleTags, articleTagsKey, cachedArticles, title]);

  return (
    <main className={`trw-destination-page ${darkMode ? "bg-black text-white" : "bg-white text-black"}`}>
      <div className="mx-auto max-w-[1500px] px-[15px]">
        {/* Back */}
        <div className="mt-[30px] mb-[30px] md:mb-[50px] flex items-start justify-between gap-3">
          <h1 className={`trw-section-h2 mt-0 mb-0 border-l-[4px] border-[#d02525] py-0 pr-0 pl-[15px] [font-family:Poppins,sans-serif] text-[36px] leading-none font-medium ${darkMode ? "text-white" : "text-black"}`}>
            {pageTitle}
          </h1>
        </div>

        {/* Packages */}
        {packages.length > 0 && (
          <section className="packages">
            <ul id="packages-list" className="grid list-none grid-cols-1 gap-[25px] p-0 md:grid-cols-2 xl:grid-cols-3">
              {packages.map((pkg) => (
                <li key={pkg.id}>
                  <DestPkgCard
                    pkg={pkg}
                    isBookmarked={tourBMs.has(pkg.id)}
                    onToggle={() => toggleTourBM(pkg.id)}
                    onShare={() => setShareUrl(pkg.tourLink || window.location.href)}
                    darkMode={darkMode}
                  />
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Attractions */}
        {attractions.length > 0 && (
          <section className="mt-[30px] md:mt-[50px]">
            <SectionH2 className="!mb-[50px]" darkMode={darkMode}>Top Attractions</SectionH2>
            <div className="grid grid-cols-1 gap-[24px] md:grid-cols-2 xl:grid-cols-4">
              {attractions.map((a) => <AttractionCard key={a.id} item={a} />)}
            </div>
          </section>
        )}

        {/* Articles */}
        {(articlesLoading || articles.length > 0) && (
          <section className="mt-[30px] md:mt-[50px]">
            <SectionH2 className="!mb-[50px]" darkMode={darkMode}>Related Articles</SectionH2>
            {articles.length > 0 ? (
              <>
                {articlesLoading && (
                  <p className="mb-4 rounded-[12px] border border-[#e5e7eb] bg-white px-4 py-3 text-[13px] text-[#6b7280]">
                    Refreshing articles...
                  </p>
                )}
                <div className="grid grid-cols-1 gap-[24px] md:grid-cols-2 xl:grid-cols-3">
                  {articles.map((article) => (
                    <DestinationArticleCard
                      key={article.id}
                      item={article}
                      isBookmarked={blogBMs.has(String(article.id))}
                      onToggleBookmark={() => toggleArticleBookmark(article)}
                    />
                  ))}
                </div>
              </>
            ) : articlesLoading ? (
              <p className="rounded-[12px] border border-[#e5e7eb] bg-white px-4 py-4 text-[14px] text-[#374151]">
                Loading articles...
              </p>
            ) : null}
          </section>
        )}

        {/* City Links */}
        <CityLinks destTitle={title} destSlug={slug} cities={cityLinks} darkMode={darkMode} />

        {/* FAQs */}
        {faqs.length > 0 && (
          <section className="mt-[30px] md:mt-[50px]">
            <SectionH2 className="!mb-[30px]" darkMode={darkMode}>Frequently Asked Questions</SectionH2>
            <div className="w-full">
              {faqs.map((faq) => (
                <FAQItem
                  key={faq.id}
                  faq={faq}
                  isOpen={openFaq === faq.id}
                  onToggle={() => setOpenFaq((p) => p === faq.id ? null : faq.id)}
                  darkMode={darkMode}
                />
              ))}
            </div>
          </section>
        )}

        {/* Similar Destinations */}
        {similarDestinations.length > 0 && (
          <section className="mt-[30px] md:mt-[50px]">
            <SectionH2 className="!mb-[50px]" darkMode={darkMode}>Similar Destinations</SectionH2>
            <div className="grid grid-cols-1 gap-[25px] md:grid-cols-2 xl:grid-cols-3">
              {similarDestinations.map((item) => (
                <SimilarDestinationCard
                  key={item.id}
                  item={item}
                  isBookmarked={tourBMs.has(item.id)}
                  onToggleBookmark={() => toggleTourBM(item.id)}
                  onShare={() => setShareUrl(item.tourLink || item.planLink || window.location.href)}
                  darkMode={darkMode}
                />
              ))}
            </div>
          </section>
        )}

        {/* Spacer */}
        <div className="h-[50px]" />
      </div>

      {shareUrl && <SharePopup shareUrl={shareUrl} onClose={() => setShareUrl("")} />}
    </main>
  );
}

// ─── PACKAGES LISTING PAGE ────────────────────────────────────────────────────
function PackagesListingPage({ sectionCode, darkMode = false }) {
  const [tourBMs, toggleTourBM] = useBookmarks(STORAGE.TOUR_BOOKMARKS);
  const [shareUrl, setShareUrl] = useState("");
  const [dataVersion, setDataVersion] = useState(0);

  useEffect(() => {
    const refresh = () => setDataVersion((value) => value + 1);
    window.addEventListener("storage", refresh);
    window.addEventListener("pageshow", refresh);
    window.addEventListener("focus", refresh);
    return () => {
      window.removeEventListener("storage", refresh);
      window.removeEventListener("pageshow", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, []);

  const filteredSections = useMemo(() => {
    const f = String(sectionCode || "").toLowerCase();
    return f && SECTIONS.find((s) => s.code === f)
      ? [SECTIONS.find((s) => s.code === f)]
      : SECTIONS;
  }, [sectionCode]);

  const allPackages = useMemo(() => getAllMiddlePackages(), [dataVersion]);
  const packagesBySection = useMemo(() =>
    SECTIONS.reduce((acc, sec) => {
      acc[sec.code] = allPackages.filter((p) => p.sectionCode === sec.code);
      return acc;
    }, {}),
  [allPackages]);

  const withAds = (items) => items.map((item) => ({ type: "card", key: item.id, item }));

  const orderedSectionsMobile = useMemo(() => {
    const list = [...SECTIONS];
    const idx = list.findIndex((s) => s.code === "international");
    if (idx <= 0) return list;
    const [international] = list.splice(idx, 1);
    list.splice(1, 0, international); // after "All"
    return list;
  }, []);

  return (
    <main className={`trw-tour-packages-page py-8 ${darkMode ? "bg-black text-white" : "bg-white text-black"}`}>
      <div className="trw-superdeals mx-auto max-w-[1500px] px-[15px]">
        <h1
          className={`mb-6 text-center text-[32px] leading-[36px] sm:text-[36px] sm:leading-[36px] ${darkMode ? "text-white" : "text-black"}`}
          style={{
            fontFamily: "Poppins, sans-serif",
            fontStyle: "normal",
            fontWeight: 500,
          }}
        >
          350+ Tour Packages
        </h1>

        {/* Section Tabs */}
        <div
          className="mb-8 flex flex-wrap items-center justify-center gap-3 text-center sm:hidden"
          style={{ fontFamily: "Poppins, sans-serif" }}
          aria-label="Tour packages filters"
        >
          <a
            href="?"
            aria-current={!sectionCode ? "page" : undefined}
            className={`no-underline rounded-[12px] border px-[10px] py-[8px] text-[14px] font-[400] leading-[20px] transition-colors sm:px-4 sm:py-[11px] ${
              !sectionCode
                ? "border-[#ff1f1f] text-[#ff1f1f]"
                : darkMode
                  ? "border-white text-white hover:border-[#ff1f1f] hover:text-[#ff1f1f]"
                  : "border-black text-black hover:border-[#ff1f1f] hover:text-[#ff1f1f]"
            }`}
          >
            All
          </a>
          {orderedSectionsMobile.map((s) => {
            const active = sectionCode === s.code;
            const label =
              s.code === "india"
                ? "India Tour Packages"
                : s.code === "international"
                ? "International Tour Packages"
                : s.code === "europe"
                ? "Europe Tour Packages"
                : `${s.code} Tour Packages`;
            return (
              <a
                key={s.code}
                href={`?section=${s.code}`}
                aria-current={active ? "page" : undefined}
                className={`no-underline rounded-[12px] border px-[10px] py-[8px] text-[14px] font-[400] leading-[20px] transition-colors sm:px-4 sm:py-[11px] ${
                  active
                    ? "border-[#ff1f1f] text-[#ff1f1f]"
                    : darkMode
                      ? "border-white text-white hover:border-[#ff1f1f] hover:text-[#ff1f1f]"
                      : "border-black text-black hover:border-[#ff1f1f] hover:text-[#ff1f1f]"
                }`}
              >
                {label}
              </a>
            );
          })}
        </div>

        <div
          className="mb-8 hidden flex-wrap items-center justify-center gap-3 text-center sm:flex"
          style={{ fontFamily: "Poppins, sans-serif" }}
          aria-label="Tour packages filters"
        >
          <a
            href="?"
            aria-current={!sectionCode ? "page" : undefined}
            className={`no-underline rounded-[12px] border px-[10px] py-[8px] text-[14px] font-[400] leading-[20px] transition-colors sm:px-4 sm:py-[11px] ${
              !sectionCode
                ? "border-[#ff1f1f] text-[#ff1f1f]"
                : darkMode
                  ? "border-white text-white hover:border-[#ff1f1f] hover:text-[#ff1f1f]"
                  : "border-black text-black hover:border-[#ff1f1f] hover:text-[#ff1f1f]"
            }`}
          >
            All
          </a>
          {SECTIONS.map((s) => {
            const active = sectionCode === s.code;
            const label =
              s.code === "india"
                ? "India Tour Packages"
                : s.code === "international"
                ? "International Tour Packages"
                : s.code === "europe"
                ? "Europe Tour Packages"
                : `${s.code} Tour Packages`;
            return (
              <a
                key={s.code}
                href={`?section=${s.code}`}
                aria-current={active ? "page" : undefined}
                className={`no-underline rounded-[12px] border px-[10px] py-[8px] text-[14px] font-[400] leading-[20px] transition-colors sm:px-4 sm:py-[11px] ${
                  active
                    ? "border-[#ff1f1f] text-[#ff1f1f]"
                    : darkMode
                      ? "border-white text-white hover:border-[#ff1f1f] hover:text-[#ff1f1f]"
                      : "border-black text-black hover:border-[#ff1f1f] hover:text-[#ff1f1f]"
                }`}
              >
                {label}
              </a>
            );
          })}
        </div>

        {/* Sections */}
        {filteredSections.map((sec) => {
          const items = packagesBySection[sec.code] || [];
	          const mixed = withAds(items);
          return (
            <section key={sec.code} className="mb-10">
              <SectionH2 darkMode={darkMode}>{sec.title}</SectionH2>
              <div className="mt-[10px] md:mt-[30px] grid grid-cols-1 items-start gap-[25px] md:grid-cols-2 lg:grid-cols-3">
	                {mixed.map((entry) => (
	                  <TourMiddleCard
	                    key={entry.key}
	                    item={entry.item}
	                    darkMode={darkMode}
	                    isBookmarked={tourBMs.has(entry.item.id)}
	                    onToggle={() => toggleTourBM(entry.item.id)}
	                    onShare={() => setShareUrl(typeof window !== "undefined" ? `${window.location.origin}${entry.item.tourLink}` : entry.item.tourLink)}
	                    onForm={() => {}}
	                  />
	                ))}
	              </div>
            </section>
          );
        })}
      </div>
      {shareUrl && <SharePopup shareUrl={shareUrl} onClose={() => setShareUrl("")} />}
    </main>
  );
}

// ─── BOOKMARKS PAGE ───────────────────────────────────────────────────────────
function BookmarksPage({ darkMode = false }) {
  const [tourBMs, toggleTourBM] = useBookmarks(STORAGE.TOUR_BOOKMARKS);
  const [shareUrl, setShareUrl] = useState("");
  const [dataVersion, setDataVersion] = useState(0);

  useEffect(() => {
    const refresh = () => setDataVersion((value) => value + 1);
    window.addEventListener("storage", refresh);
    window.addEventListener("pageshow", refresh);
    window.addEventListener("focus", refresh);
    return () => {
      window.removeEventListener("storage", refresh);
      window.removeEventListener("pageshow", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, []);

  const bookmarkedMiddle = useMemo(
    () => getAllMiddlePackages().filter((p) => tourBMs.has(p.id)),
    [tourBMs, dataVersion]
  );

  return (
    <main className={`trw-tour-packages-page py-8 ${darkMode ? "bg-black text-white" : "bg-white text-black"}`}>
      <div className="mx-auto max-w-[1500px] px-[15px]">
        <div className="mb-8 flex justify-end">
          <a href="?"
            className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-semibold uppercase hover:bg-black ${darkMode ? "border-white bg-transparent text-white hover:text-white" : "border-black bg-white text-black hover:text-white"}`}>
            <i className="fa-solid fa-arrow-left text-[11px]" aria-hidden="true" />Back
          </a>
        </div>

        <section className="mb-10">
          <SectionH2 darkMode={darkMode}>Middle Page Packages</SectionH2>
          {bookmarkedMiddle.length ? (
            <div className="mt-[30px] grid grid-cols-1 items-stretch gap-[25px] md:grid-cols-2 lg:grid-cols-3">
              {bookmarkedMiddle.map((item) => (
                <TourMiddleCard
                  key={item.id} item={item} darkMode={darkMode}
                  isBookmarked={tourBMs.has(item.id)}
                  onToggle={() => toggleTourBM(item.id)}
                  onShare={() => setShareUrl(item.tourLink || "")}
                  onForm={() => {}}
                />
              ))}
            </div>
          ) : (
            <p className={`rounded-[12px] border border-[#ef4444] px-4 py-4 text-[14px] ${darkMode ? "bg-transparent text-white" : "bg-white text-[#374151]"}`}>No bookmarked packages yet.</p>
          )}
        </section>
      </div>
      {shareUrl && <SharePopup shareUrl={shareUrl} onClose={() => setShareUrl("")} />}
    </main>
  );
}

// ─── TOUR PACKAGE FORM ────────────────────────────────────────────────────────
function TourPackageForm({ initialTab = "", initialDestSlug = "", darkMode = false }) {
  const packageBaseForm = {
    sectionCode: "india",
    destination: "",
    tagline: "",
    packageCount: "1",
    startsFrom: "",
    priceUnit: "Per Adult",
    imageUrl: "",
    imageFileName: "",
    planNumber: DEFAULT_WA_NUMBER,
    planMessage: "",
    showShare: true,
    showViewDetails: true,
    planLink: WA_BASE,
  };
  const detailPackageBaseForm = {
    title: "",
    subtitle: "",
    nights: "",
    days: "",
    startsFrom: "",
    perAdult: "Per Adult",
    imageUrl: "",
    imageFileName: "",
    includes: [...DEFAULT_INCLUDES],
    routes: "",
    highlightsText: "",
    planNumber: DEFAULT_WA_NUMBER,
    planMessage: "",
    planLink: WA_BASE,
    terms: DEFAULT_TERMS,
  };
  const attractionBaseForm = {
    title: "",
    description: "",
    tag: "Attraction",
    imageUrl: "",
    imageFileName: "",
    link: "#",
  };
  const faqBaseForm = {
    q: "",
    answer: "",
    pointsText: "",
  };
  const seoMetaBaseForm = {
    pageKey: "listing:all",
    metaTitle: "",
    metaDescription: "",
  };
  const [tab, setTab] = useState(
    initialTab === "destination" ? "destination" : initialTab === "superdeals" ? "superdeals" : initialTab === "seo" ? "seo" : "packages"
  );
  const [form, setForm] = useState(() => readTourFormDrafts().packageForm || packageBaseForm);
  const [savedPkgs, setSavedPkgs] = useState(() => getAllMiddlePackages());
  const [notice, setNotice] = useState(null);
  const [editId, setEditId] = useState(null);
  const [selectedDestSlug, setSelectedDestSlug] = useState(() => slugify(initialDestSlug || "andaman") || "andaman");
  const [destinationDataVersion, setDestinationDataVersion] = useState(0);
  const [newDestinationName, setNewDestinationName] = useState("");
  const [showNewDestinationForm, setShowNewDestinationForm] = useState(false);
  const [destForm, setDestForm] = useState({
    title: "",
    articleTags: "",
    cityLinks: "",
    showViewDetails: true,
  });
  const [pkgUploadKey, setPkgUploadKey] = useState(0);
  const [detailPkgUploadKey, setDetailPkgUploadKey] = useState(0);
  const [detailPkgForm, setDetailPkgForm] = useState(detailPackageBaseForm);
  const [detailPkgEditId, setDetailPkgEditId] = useState("");
	  const [attractionUploadKey, setAttractionUploadKey] = useState(0);
	  const [attractionForm, setAttractionForm] = useState(attractionBaseForm);
	  const [attractionEditId, setAttractionEditId] = useState("");
	  const [faqForm, setFaqForm] = useState(faqBaseForm);
	  const [faqEditId, setFaqEditId] = useState("");
	  const [seoMetaOverrides, setSeoMetaOverrides] = useState(() => readTourSeoMetaOverrides());
	  const [seoMetaForm, setSeoMetaForm] = useState(seoMetaBaseForm);
	  const [importingBackup, setImportingBackup] = useState(false);
	  const destinationEditorInitializedRef = useRef(false);
	  const [formDrafts, setFormDrafts] = useState(() => readTourFormDrafts());
	  const backupFileInputRef = useRef(null);

  const notify = useCallback((type, text) => {
    setNotice({ type, text });
  }, []);

  const handleDownloadTourPackagesBackup = useCallback(() => {
    const data = {
      generatedAt: new Date().toISOString(),
      source: "tour-packages-form",
      tourPackages: {
        customPackages: readCustomMiddlePackages(),
        destinationPages: readDestinationPageOverrides(),
        seedPackageOverrides: readSeedPackageOverrides(),
        deletedSeedPackageIds: readDeletedSeedPackageIds(),
        seoMetaOverrides: readTourSeoMetaOverrides(),
      },
    };
    downloadBackupJS("tour-packages-backup", "TRW_TOUR_PACKAGES_BACKUP", data);
    notify("success", "Backup JS downloaded for Tour Packages.");
  }, [notify]);

	  const handleDownloadLiveBackup = useCallback(async () => {
	    const liveData = await fetchTourPackagesRemoteData();
	    if (!liveData) {
	      notify("error", "Unable to fetch live backup data.");
	      return;
    }
    const data = {
      generatedAt: new Date().toISOString(),
      source: "live-data-sync",
      liveData,
    };
    downloadBackupJS("live-data-backup", "TRW_LIVE_DATA_BACKUP", data);
	    notify("success", "Live backup downloaded.");
	  }, [notify]);

	  const handleImportTourPackagesBackup = useCallback(async (event) => {
	    const file = event?.target?.files?.[0];
	    if (!file) return;
	    if (!window.confirm("This will overwrite your current Tour Packages data in this browser (localStorage). Continue?")) {
	      event.target.value = "";
	      return;
	    }
	    setImportingBackup(true);
	    try {
	      const text = await file.text();
	      const payload = parseTourPackagesBackupFromText(text);
	      const normalized = normalizeTourPackagesBackupPayload(payload);

	      writeCustomMiddlePackages(normalized.customPackages);
	      writeDestinationPageOverrides(normalized.destinationPages);
	      writeSeedPackageOverrides(normalized.seedPackageOverrides);
	      writeDeletedSeedPackageIds(normalized.deletedSeedPackageIds);
	      writeTourSeoMetaOverrides(normalized.seoMetaOverrides);
	      setSeoMetaOverrides(normalized.seoMetaOverrides);

	      notify("success", "Backup imported. Data restored in this browser.");
	    } catch (error) {
	      notify("error", error?.message || "Unable to import backup file.");
	    } finally {
	      setImportingBackup(false);
	      if (backupFileInputRef.current) backupFileInputRef.current.value = "";
	    }
	  }, [notify]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = window.setTimeout(() => setNotice(null), 2600);
    return () => window.clearTimeout(timer);
  }, [notice]);

  useEffect(() => {
    const sync = () => setSavedPkgs(getAllMiddlePackages());
    window.addEventListener("storage", sync);
    window.addEventListener("pageshow", sync);
    window.addEventListener("focus", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("pageshow", sync);
      window.removeEventListener("focus", sync);
    };
  }, []);

  useEffect(() => {
    const syncSeoMeta = () => setSeoMetaOverrides(readTourSeoMetaOverrides());
    window.addEventListener("storage", syncSeoMeta);
    window.addEventListener(SEO_META_UPDATED_EVENT, syncSeoMeta);
    return () => {
      window.removeEventListener("storage", syncSeoMeta);
      window.removeEventListener(SEO_META_UPDATED_EVENT, syncSeoMeta);
    };
  }, []);

  useEffect(() => {
    setFormDrafts((current) => {
      const next = {
        ...current,
        packageForm: form,
      };
      writeTourFormDrafts(next);
      return next;
    });
  }, [form]);

  useEffect(() => {
    const slug = slugify(selectedDestSlug || "");
    if (!slug) return;
    setFormDrafts((current) => {
      const next = {
        ...current,
        destinationForms: {
          ...(current.destinationForms || {}),
          [slug]: {
            destForm,
            detailPkgForm,
            detailPkgEditId,
            attractionForm,
            attractionEditId,
            faqForm,
            faqEditId,
          },
        },
      };
      writeTourFormDrafts(next);
      return next;
    });
  }, [selectedDestSlug, destForm, detailPkgForm, detailPkgEditId, attractionForm, attractionEditId, faqForm, faqEditId]);

  const destinationOptions = useMemo(() => {
    const seedSlugs = getAllMiddlePackages().map((pkg) => slugify(pkg.destinationSlug || pkg.destination));
    const overrideSlugs = Object.keys(readDestinationPageOverrides() || {});
    const destSlugs = Object.keys(DEST_PACKAGES || {});
    return Array.from(new Set([...seedSlugs, ...overrideSlugs, ...destSlugs]))
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
  }, [savedPkgs, destinationDataVersion]);

  const selectedDestinationData = useMemo(
    () => getDestinationViewData(selectedDestSlug || "andaman"),
    [selectedDestSlug, destinationDataVersion]
  );
  const seoMetaDefaultEntries = useMemo(
    () => getSeoEditorDefaultEntries(savedPkgs),
    [savedPkgs]
  );
  const seoMetaDefaultMap = useMemo(
    () => new Map(seoMetaDefaultEntries.map((entry) => [entry.key, entry])),
    [seoMetaDefaultEntries]
  );
  const seoMetaEntries = useMemo(
    () => seoMetaDefaultEntries.map((entry) => ({
      ...entry,
      ...(seoMetaOverrides?.[entry.key] || {}),
    })),
    [seoMetaDefaultEntries, seoMetaOverrides]
  );

  const loadDestinationEditor = useCallback((slug) => {
    const normalizedSlug = slugify(slug || "andaman") || "andaman";
    const data = getDestinationViewData(normalizedSlug);
    const draft = readTourFormDrafts()?.destinationForms?.[normalizedSlug] || null;
    setSelectedDestSlug(normalizedSlug);
    setShowNewDestinationForm(false);
    setNewDestinationName("");
    setDestForm({
      title: draft?.destForm?.title ?? data.title ?? "",
      articleTags: draft?.destForm?.articleTags ?? (data.articleTags || []).join(", "),
      cityLinks: draft?.destForm?.cityLinks ?? (data.cityLinks || []).join(", "),
      showViewDetails: draft?.destForm?.showViewDetails ?? (data.showViewDetails !== false),
    });
    setDetailPkgForm(draft?.detailPkgForm || detailPackageBaseForm);
    setDetailPkgEditId(draft?.detailPkgEditId || "");
    setAttractionForm(draft?.attractionForm || attractionBaseForm);
    setAttractionEditId(draft?.attractionEditId || "");
    setDetailPkgUploadKey((value) => value + 1);
    setAttractionUploadKey((value) => value + 1);
    setFaqForm(draft?.faqForm || faqBaseForm);
    setFaqEditId(draft?.faqEditId || "");
    setDestinationDataVersion((value) => value + 1);
  }, []);

  const createDestinationFromName = useCallback((name) => {
    const trimmedName = String(name || "").trim();
    if (!trimmedName) {
      notify("error", "Destination name is required.");
      return;
    }

    const slug = slugify(trimmedName);
    if (!slug) {
      notify("error", "Please enter a valid destination name.");
      return;
    }

    const overrides = readDestinationPageOverrides();
    if (overrides?.[slug]) {
      notify("info", "Destination already exists. Opening it now.");
      loadDestinationEditor(slug);
      return;
    }

    overrides[slug] = {
      title: trimmedName,
      showViewDetails: true,
      articleTags: [],
      cityLinks: [],
      packages: [],
      attractions: [],
      faqs: [],
    };
    writeDestinationPageOverrides(overrides);
    notify("success", `Destination created: ${trimmedName}`);
    loadDestinationEditor(slug);
  }, [loadDestinationEditor, notify]);

  useEffect(() => {
    const normalizedInitialSlug = slugify(initialDestSlug || "");
    const currentSlug = slugify(selectedDestSlug || "");
    const hasCurrentSelection = currentSlug && destinationOptions.includes(currentSlug);
    const preferredSlug = normalizedInitialSlug && destinationOptions.includes(normalizedInitialSlug)
      ? normalizedInitialSlug
      : (hasCurrentSelection ? currentSlug : (destinationOptions[0] || "andaman"));

    if (!destinationEditorInitializedRef.current) {
      destinationEditorInitializedRef.current = true;
      loadDestinationEditor(preferredSlug);
      return;
    }

    if (normalizedInitialSlug && normalizedInitialSlug !== currentSlug && destinationOptions.includes(normalizedInitialSlug)) {
      loadDestinationEditor(normalizedInitialSlug);
      return;
    }

    if (!hasCurrentSelection && preferredSlug) {
      loadDestinationEditor(preferredSlug);
    }
  }, [initialDestSlug, destinationOptions, loadDestinationEditor, selectedDestSlug]);

  useEffect(() => {
    const selectedKey = seoMetaDefaultMap.has(seoMetaForm.pageKey)
      ? seoMetaForm.pageKey
      : (seoMetaDefaultEntries[0]?.key || "listing:all");
    const activeEntry = seoMetaEntries.find((entry) => entry.key === selectedKey) || seoMetaEntries[0];
    if (!activeEntry) return;
    setSeoMetaForm((current) => (
      current.pageKey === activeEntry.key &&
      current.metaTitle === (activeEntry.title || "") &&
      current.metaDescription === (activeEntry.description || "")
        ? current
        : {
            pageKey: activeEntry.key,
            metaTitle: activeEntry.title || "",
            metaDescription: activeEntry.description || "",
          }
    ));
  }, [seoMetaDefaultEntries, seoMetaDefaultMap, seoMetaEntries, seoMetaForm.pageKey]);

  const packagePreview = useMemo(() => ({
    id: "preview-middle-package",
    sectionCode: form.sectionCode,
    destination: form.destination.trim() || "Destination Name",
    destinationSlug: slugify(form.destination || "destination-name"),
    tagline: form.tagline.trim() || "Destination subtitle appears here",
    packageCount: normalizePackageCount(form.packageCount),
    startsFrom: Number(form.startsFrom) || 0,
    priceUnit: form.priceUnit.trim() || "Per Adult",
    imageUrl: form.imageUrl.trim() || imgUrl(form.destination || "Andaman"),
    showShare: !!form.showShare,
    showViewDetails: form.showViewDetails !== false,
    planLink: buildWhatsAppLink(
      form.planMessage.trim() || getMiddlePackageWhatsappMessage(form.destination.trim() || "this"),
      form.planNumber
    ),
    tourLink: tourLink(slugify(form.destination || "destination-name")),
  }), [form]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.destination.trim()) { notify("error", "Destination is required."); return; }
    if (!form.tagline.trim()) { notify("error", "Tagline is required."); return; }
    if (!form.startsFrom) { notify("error", "Starts From (INR) is required."); return; }

    const destinationSlug = slugify(form.destination);
    const resolvedImageUrl = form.imageUrl.trim() || imgUrl(form.destination || "Andaman");
    const resolvedPlanMessage = form.planMessage.trim() || getMiddlePackageWhatsappMessage(form.destination.trim() || "this");
    const existingPkgSeo = getAllMiddlePackages().find((item) => String(item.id) === String(editId))
      || getAllMiddlePackages().find((item) => slugify(item.destinationSlug || item.destination) === destinationSlug);
    const pkg = {
      id: editId || `custom-${Date.now()}`,
      sectionCode: form.sectionCode,
      destination: form.destination.trim(),
      destinationSlug,
      tagline: form.tagline.trim(),
      packageCount: normalizePackageCount(form.packageCount),
      startsFrom: +form.startsFrom,
      priceUnit: form.priceUnit.trim() || "Per Adult",
      imageUrl: resolvedImageUrl,
      imageFileName: form.imageFileName.trim(),
      planMessage: resolvedPlanMessage,
      showShare: !!form.showShare,
      showViewDetails: form.showViewDetails !== false,
      planLink: buildWhatsAppLink(resolvedPlanMessage, form.planNumber),
      tourLink: tourLink(destinationSlug),
      seoTitle: existingPkgSeo?.seoTitle || "",
      seoDescription: existingPkgSeo?.seoDescription || "",
    };

    if (editId && !String(editId).startsWith("custom-")) {
      const nextOverrides = [
        ...readSeedPackageOverrides().filter((item) => item.id !== editId),
        normalizeMiddlePackage(pkg),
      ];
      writeSeedPackageOverrides(nextOverrides);
      writeDeletedSeedPackageIds(readDeletedSeedPackageIds().filter((id) => id !== editId));
    } else {
      const customPackages = readCustomMiddlePackages();
      const nextCustom = editId
        ? customPackages.map((p) => (p.id === editId ? pkg : p))
        : [...customPackages, pkg];
      writeCustomMiddlePackages(nextCustom);
    }
    setSavedPkgs(getAllMiddlePackages());
    setEditId(null);
    setForm(packageBaseForm);
    setPkgUploadKey((value) => value + 1);
    notify("success", editId ? "Middle-page package updated." : "Middle-page package saved.");
  };

  // FIX: Changed max file size from 100KB to 1MB
  const handlePackageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const isAvif = file.type === "image/avif" || /\.avif$/i.test(file.name);
    if (!isAvif) {
      notify("error", "Only AVIF files are allowed.");
      event.target.value = "";
      return;
    }
    if (file.size > MAX_AVIF_UPLOAD_SIZE) {
      notify("error", "Image size must be 1MB or less.");
      event.target.value = "";
      return;
    }
    const existingImageUrl = String(form.imageFileName ? form.imageUrl : "").trim();
    const remoteImageUrl = await uploadTourImageToRemote(file, existingImageUrl);
    if (remoteImageUrl) {
      setForm((current) => ({
        ...current,
        imageUrl: remoteImageUrl,
        imageFileName: file.name,
      }));
      notify("success", `Image uploaded to live folder: ${file.name}`);
      event.target.value = "";
      return;
    }

    if (!isLocalHostLike()) {
      notify("error", "Live image upload failed. Please try again or use the image URL/path field.");
      event.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setForm((current) => ({
        ...current,
        imageUrl: String(reader.result || ""),
        imageFileName: file.name,
      }));
      notify("success", `Image uploaded: ${file.name}`);
    };
    reader.onerror = () => notify("error", "Unable to read image file. Please try another AVIF image.");
    reader.readAsDataURL(file);
  };

  const handleAvifUpload = async (event, setImageState, existingImageUrl = "", existingImageFileName = "") => {
    const file = event.target.files?.[0];
    if (!file) return;
    const isAvif = file.type === "image/avif" || /\.avif$/i.test(file.name);
    if (!isAvif) {
      notify("error", "Only AVIF files are allowed.");
      event.target.value = "";
      return;
    }
    if (file.size > MAX_AVIF_UPLOAD_SIZE) {
      notify("error", "Image size must be 1MB or less.");
      event.target.value = "";
      return;
    }
    const remoteImageUrl = await uploadTourImageToRemote(
      file,
      String(existingImageFileName || "").trim() ? existingImageUrl : ""
    );
    if (remoteImageUrl) {
      setImageState((current) => ({
        ...current,
        imageUrl: remoteImageUrl,
        imageFileName: file.name,
      }));
      notify("success", `Image uploaded to live folder: ${file.name}`);
      event.target.value = "";
      return;
    }

    if (!isLocalHostLike()) {
      notify("error", "Live image upload failed. Please try again or use the image URL/path field.");
      event.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setImageState((current) => ({
        ...current,
        imageUrl: String(reader.result || ""),
        imageFileName: file.name,
      }));
      notify("success", `Image uploaded: ${file.name}`);
    };
    reader.onerror = () => notify("error", "Unable to read image file. Please try another AVIF image.");
    reader.readAsDataURL(file);
  };

  const handleImageUrlChange = (value, setImageState) => {
    setImageState((current) => ({
      ...current,
      imageUrl: value,
      imageFileName: current.imageFileName && value.trim() !== current.imageUrl.trim()
        ? ""
        : current.imageFileName,
    }));
  };

  const deleteCustom = (id) => {
    if (!window.confirm("Delete this package?")) return;
    if (String(id).startsWith("custom-")) {
      const next = readCustomMiddlePackages().filter((p) => p.id !== id);
      writeCustomMiddlePackages(next);
    } else {
      writeDeletedSeedPackageIds(Array.from(new Set([...readDeletedSeedPackageIds(), String(id)])));
      writeSeedPackageOverrides(readSeedPackageOverrides().filter((item) => item.id !== id));
    }
    setSavedPkgs(getAllMiddlePackages());
    if (editId === id) {
      setEditId(null);
      setForm(packageBaseForm);
    }
    notify("success", "Package deleted.");
  };

  const startEdit = (pkg) => {
    setEditId(pkg.id);
    setForm({
      sectionCode: pkg.sectionCode || "india",
      destination: pkg.destination || "",
      tagline: pkg.tagline || "",
      packageCount: String(normalizePackageCount(pkg.packageCount)),
      startsFrom: String(pkg.startsFrom ?? ""),
      priceUnit: pkg.priceUnit || "Per Adult",
      imageUrl: pkg.imageUrl || "",
      imageFileName: pkg.imageFileName || "",
      planNumber: extractWhatsAppNumber(pkg.planLink, DEFAULT_WA_NUMBER),
      planMessage: extractWhatsAppMessage(pkg.planLink, getMiddlePackageWhatsappMessage(pkg.destination || "this")),
      showShare: pkg.showShare !== false,
      showViewDetails: pkg.showViewDetails !== false,
      planLink: pkg.planLink || WA_BASE,
    });
    setPkgUploadKey((value) => value + 1);
    notify("info", `Editing: ${pkg.destination}`);
  };

  const saveDestinationSection = (updater, successText) => {
    const slug = slugify(selectedDestSlug || "");
    if (!slug) {
      notify("error", "Select a destination first.");
      return;
    }
    const currentData = getDestinationViewData(slug);
    const overrides = readDestinationPageOverrides();
    const existing = overrides[slug] || {};
      const nextPage = {
        title: String(existing.title || currentData.title).trim(),
        showViewDetails: typeof existing.showViewDetails === "boolean" ? existing.showViewDetails : currentData.showViewDetails !== false,
        articleTags: Array.isArray(existing.articleTags) ? existing.articleTags : currentData.articleTags,
        cityLinks: Array.isArray(existing.cityLinks) ? existing.cityLinks : currentData.cityLinks,
        packages: Array.isArray(existing.packages) ? existing.packages.map(normalizeDetailPackage) : currentData.packages.map(normalizeDetailPackage),
      attractions: Array.isArray(existing.attractions) ? existing.attractions.map(normalizeAttraction) : currentData.attractions.map(normalizeAttraction),
      faqs: Array.isArray(existing.faqs) ? existing.faqs.map(normalizeFaq) : currentData.faqs.map(normalizeFaq),
    };
    overrides[slug] = updater(nextPage);
    writeDestinationPageOverrides(overrides);
    notify("success", successText);
    loadDestinationEditor(slug);
  };

  const handleSaveDestination = () => {
    const slug = slugify(selectedDestSlug || "");
    if (!slug) {
      notify("error", "Select a destination first.");
      return;
    }
    const currentData = getDestinationViewData(slug);
    const overrides = readDestinationPageOverrides();
    const existing = overrides[slug] || {};
    overrides[slug] = {
      title: String(destForm.title || "").trim() || currentData.title,
      showViewDetails: destForm.showViewDetails !== false,
      articleTags: String(destForm.articleTags || "").split(",").map((item) => item.trim()).filter(Boolean),
      cityLinks: String(destForm.cityLinks || "").split(",").map((item) => item.trim()).filter(Boolean),
      packages: Array.isArray(existing.packages) ? existing.packages.map(normalizeDetailPackage) : currentData.packages.map(normalizeDetailPackage),
      attractions: Array.isArray(existing.attractions) ? existing.attractions.map(normalizeAttraction) : currentData.attractions.map(normalizeAttraction),
      faqs: Array.isArray(existing.faqs) ? existing.faqs.map(normalizeFaq) : currentData.faqs.map(normalizeFaq),
    };
    writeDestinationPageOverrides(overrides);
    notify("success", "Destination detail page data updated.");
    loadDestinationEditor(slug);
  };

  const toggleInclude = (include) => {
    setDetailPkgForm((current) => ({
      ...current,
      includes: current.includes.includes(include)
        ? current.includes.filter((item) => item !== include)
        : [...current.includes, include],
    }));
  };

  const saveDetailPackage = () => {
    if (!detailPkgForm.title.trim()) { notify("error", "Detail package title is required."); return; }
    const currentDestinationData = getDestinationViewData(selectedDestSlug || "andaman");
    const existingItem = detailPkgEditId
      ? currentDestinationData.packages.find((pkg) => pkg.id === detailPkgEditId)
      : null;
    const resolvedDetailImageUrl = detailPkgForm.imageUrl.trim() || existingItem?.imageUrl || "";
    const resolvedDetailImageFileName = detailPkgForm.imageFileName.trim() || existingItem?.imageFileName || "";

	    if (!resolvedDetailImageUrl) {
	      notify("error", "Please add an image URL/path or upload an AVIF image for the detail package.");
	      return;
	    }

    const item = normalizeDetailPackage({
      id: detailPkgEditId || undefined,
      title: detailPkgForm.title,
      subtitle: detailPkgForm.subtitle,
      nights: detailPkgForm.nights,
      days: detailPkgForm.days,
      startsFrom: detailPkgForm.startsFrom,
      perAdult: detailPkgForm.perAdult,
      imageUrl: resolvedDetailImageUrl,
      imageFileName: resolvedDetailImageFileName,
      includes: detailPkgForm.includes.length ? detailPkgForm.includes : [...DEFAULT_INCLUDES],
      routes: detailPkgForm.routes,
      highlights: detailPkgForm.highlightsText,
      planLink: buildWhatsAppLink(
        detailPkgForm.planMessage.trim() || getDetailPackageWhatsappMessage(detailPkgForm.title.trim()),
        detailPkgForm.planNumber
      ),
      tourLink: tourLink(selectedDestSlug),
      terms: detailPkgForm.terms,
    });
    saveDestinationSection((page) => ({
      ...page,
      packages: detailPkgEditId
        ? page.packages.map((pkg) => (pkg.id === detailPkgEditId ? item : pkg))
        : [...page.packages, item],
    }), detailPkgEditId ? "Detail package updated." : "Detail package added.");
    setDetailPkgForm(detailPackageBaseForm);
    setDetailPkgEditId("");
    setDetailPkgUploadKey((value) => value + 1);
  };

  const editDetailPackage = (item) => {
    setDetailPkgEditId(item.id);
    setDetailPkgForm({
      title: item.title || "",
      subtitle: item.subtitle || "",
      nights: String(item.nights ?? ""),
      days: String(item.days ?? ""),
      startsFrom: String(item.startsFrom ?? ""),
      perAdult: item.perAdult || "Per Adult",
      imageUrl: item.imageUrl || "",
      imageFileName: item.imageFileName || "",
      includes: Array.isArray(item.includes) && item.includes.length ? item.includes : [...DEFAULT_INCLUDES],
      routes: item.routes || "",
      highlightsText: Array.isArray(item.highlights) ? item.highlights.join(" | ") : "",
      planNumber: extractWhatsAppNumber(item.planLink, DEFAULT_WA_NUMBER),
      planMessage: extractWhatsAppMessage(item.planLink, getDetailPackageWhatsappMessage(item.title || "travel")),
      planLink: item.planLink || WA_BASE,
      terms: item.terms || DEFAULT_TERMS,
    });
    setDetailPkgUploadKey((value) => value + 1);
    notify("info", `Editing detail package: ${item.title}`);
  };

  const deleteDetailPackage = (id) => {
    if (!window.confirm("Delete this detail package?")) return;
    saveDestinationSection((page) => ({
      ...page,
      packages: page.packages.filter((item) => item.id !== id),
    }), "Detail package deleted.");
    if (detailPkgEditId === id) {
      setDetailPkgForm(detailPackageBaseForm);
      setDetailPkgEditId("");
      setDetailPkgUploadKey((value) => value + 1);
    }
  };

  const saveAttraction = () => {
    if (!attractionForm.title.trim()) { notify("error", "Attraction title is required."); return; }
    const currentDestinationData = getDestinationViewData(selectedDestSlug || "andaman");
    const existingAttraction = attractionEditId
      ? currentDestinationData.attractions.find((item) => item.id === attractionEditId)
      : null;
    const resolvedAttractionImageUrl = attractionForm.imageUrl.trim() || existingAttraction?.imageUrl || "";
    const resolvedAttractionImageFileName = attractionForm.imageFileName.trim() || existingAttraction?.imageFileName || "";

	    if (!resolvedAttractionImageUrl) {
	      notify("error", "Please add an image URL/path or upload an AVIF image for the attraction.");
	      return;
	    }

    const item = normalizeAttraction({
      id: attractionEditId || undefined,
      title: attractionForm.title,
      description: attractionForm.description,
      tag: attractionForm.tag,
      imageUrl: resolvedAttractionImageUrl,
      imageFileName: resolvedAttractionImageFileName,
      link: attractionForm.link,
    });
    saveDestinationSection((page) => ({
      ...page,
      attractions: attractionEditId
        ? page.attractions.map((entry) => (entry.id === attractionEditId ? item : entry))
        : [...page.attractions, item],
    }), attractionEditId ? "Attraction updated." : "Attraction added.");
    setAttractionForm(attractionBaseForm);
    setAttractionEditId("");
    setAttractionUploadKey((value) => value + 1);
  };

  const editAttraction = (item) => {
    setAttractionEditId(item.id);
    setAttractionForm({
      title: item.title || "",
      description: item.description || "",
      tag: item.tag || "Attraction",
      imageUrl: item.imageUrl || "",
      imageFileName: item.imageFileName || "",
      link: item.link || "#",
    });
    setAttractionUploadKey((value) => value + 1);
    notify("info", `Editing attraction: ${item.title}`);
  };

  const deleteAttraction = (id) => {
    if (!window.confirm("Delete this attraction?")) return;
    saveDestinationSection((page) => ({
      ...page,
      attractions: page.attractions.filter((item) => item.id !== id),
    }), "Attraction deleted.");
    if (attractionEditId === id) {
      setAttractionForm(attractionBaseForm);
      setAttractionEditId("");
      setAttractionUploadKey((value) => value + 1);
    }
  };

  const saveFaq = () => {
    if (!faqForm.q.trim()) { notify("error", "FAQ question is required."); return; }
    const item = normalizeFaq({
      id: faqEditId || undefined,
      q: faqForm.q,
      answer: faqForm.answer,
      points: faqForm.pointsText,
    });
    saveDestinationSection((page) => ({
      ...page,
      faqs: faqEditId
        ? page.faqs.map((entry) => (entry.id === faqEditId ? item : entry))
        : [...page.faqs, item],
    }), faqEditId ? "FAQ updated." : "FAQ added.");
    setFaqForm(faqBaseForm);
    setFaqEditId("");
  };

  const editFaq = (item) => {
    setFaqEditId(item.id);
    setFaqForm({
      q: item.q || "",
      answer: item.answer || "",
      pointsText: Array.isArray(item.points) ? item.points.join("\n") : "",
    });
    notify("info", `Editing FAQ: ${item.q}`);
  };

  const saveSeoMeta = () => {
    if (!seoMetaForm.metaTitle.trim()) { notify("error", "Meta title is required."); return; }
    if (!seoMetaForm.metaDescription.trim()) { notify("error", "Meta description is required."); return; }
    if (seoMetaForm.metaTitle.trim().length > SEO_TITLE_LIMIT) {
      notify("error", `Meta title must be ${SEO_TITLE_LIMIT} characters or less.`);
      return;
    }
    if (seoMetaForm.metaDescription.trim().length > SEO_DESCRIPTION_LIMIT) {
      notify("error", `Meta description must be ${SEO_DESCRIPTION_LIMIT} characters or less.`);
      return;
    }
    const nextOverrides = {
      ...seoMetaOverrides,
      [seoMetaForm.pageKey]: {
        title: seoMetaForm.metaTitle.trim(),
        description: seoMetaForm.metaDescription.trim(),
      },
    };
    writeTourSeoMetaOverrides(nextOverrides);
    setSeoMetaOverrides(nextOverrides);
    notify("success", "SEO meta updated.");
  };

  const deleteSeoMeta = () => {
    const defaultEntry = seoMetaDefaultMap.get(seoMetaForm.pageKey);
    if (!defaultEntry) return;
    const nextOverrides = { ...seoMetaOverrides };
    delete nextOverrides[seoMetaForm.pageKey];
    writeTourSeoMetaOverrides(nextOverrides);
    setSeoMetaOverrides(nextOverrides);
    setSeoMetaForm({
      pageKey: defaultEntry.key,
      metaTitle: defaultEntry.title || "",
      metaDescription: defaultEntry.description || "",
    });
    notify("success", "SEO meta reset to default.");
  };

  const deleteFaq = (id) => {
    if (!window.confirm("Delete this FAQ?")) return;
    saveDestinationSection((page) => ({
      ...page,
      faqs: page.faqs.filter((item) => item.id !== id),
    }), "FAQ deleted.");
    if (faqEditId === id) {
      setFaqForm(faqBaseForm);
      setFaqEditId("");
    }
  };

  const NOTICE_CLS = { success:"border-[#bbf7d0] bg-[#f0fdf4] text-[#166534]", error:"border-[#fecaca] bg-[#fff5f5] text-[#991b1b]", info:"border-[#bfdbfe] bg-[#eff6ff] text-[#1d4ed8]" };

  return (
    <main className="min-h-screen bg-white py-10">
      <div className="mx-auto max-w-[1500px] px-4">
		        <div className="mb-7 flex flex-wrap items-center justify-between gap-3">
		          <h2 className="text-[30px] font-bold tracking-[-0.4px] text-black">Tour Package Entry Form</h2>
		          <div className="flex flex-wrap items-center gap-3">
		            <button type="button" onClick={handleDownloadLiveBackup} className="rounded-full border border-[#e01919] px-5 py-2 text-xs font-semibold tracking-[0.4px] uppercase text-[#e01919] hover:bg-[#e01919] hover:text-white">Download Live Backup</button>
		            <button type="button" onClick={handleDownloadTourPackagesBackup} className="rounded-full border border-black px-5 py-2 text-xs font-semibold tracking-[0.4px] uppercase text-black hover:bg-black hover:text-white">Download Tour Backup</button>
		            <input
		              ref={backupFileInputRef}
		              type="file"
		              accept=".js,application/javascript,text/javascript"
		              onChange={handleImportTourPackagesBackup}
		              className="hidden"
		            />
		            <button
		              type="button"
		              disabled={importingBackup}
		              onClick={() => backupFileInputRef.current?.click()}
		              className={`rounded-full border border-[#111827] px-5 py-2 text-xs font-semibold tracking-[0.4px] uppercase hover:bg-[#111827] hover:text-white ${importingBackup ? "cursor-not-allowed opacity-60" : "text-[#111827]"}`}
		            >
		              {importingBackup ? "Importing..." : "Import Tour Backup"}
		            </button>
		            <a href="?" className="rounded-full bg-black px-5 py-2 text-xs font-semibold tracking-[0.4px] uppercase text-white hover:bg-[#e01919]">Back To Listing</a>
		          </div>
		        </div>

        <div className="grid grid-cols-1 gap-7 lg:grid-cols-[220px_minmax(0,1fr)]">
          {/* Sidebar tabs */}
          <aside className="self-start rounded-[20px] border border-[#d9d9d9] bg-white p-4 shadow-[0_20px_55px_rgba(0,0,0,0.07)]">
            <p className="mb-3 text-[11px] font-semibold tracking-[0.3px] uppercase text-[#6b7280]">Form Tabs</p>
            <div className="space-y-2">
              {[["packages","Tour Packages"],["destination","Destination Page"],["seo","SEO Meta"],["superdeals","Super Deals"],["homebanner","Home Banner"],["homelatest","Home Latest Updates"]].map(([t, label]) => (
                <button key={t} type="button" onClick={() => setTab(t)}
                  className={`w-full rounded-xl px-4 py-3 text-left text-xs font-semibold tracking-[0.35px] uppercase transition-colors ${tab === t ? "bg-black text-white" : "border border-[#d1d5db] bg-white text-black hover:bg-black hover:text-white"}`}>
                  {label}
                </button>
              ))}
            </div>
          </aside>

          {/* Main content */}
          <div>
            {tab === "packages" && (
              <div className="grid grid-cols-1 items-start gap-7 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
                <section className="rounded-[20px] border border-[#d9d9d9] bg-white p-6 shadow-[0_20px_55px_rgba(0,0,0,0.07)]">
                  <h2 className="mb-4 text-lg font-bold text-black">Tour Package Middle Page Manager</h2>
                  <p className="mb-5 text-sm text-[#4b5563]">This form is for non-developers. Enter the package details here and upload one AVIF image just like the Super Deals form. Package count is controlled here and shows as a `+` badge on the card.</p>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <label className="text-sm font-semibold text-[#1f2937]">Section
                        <select value={form.sectionCode} onChange={(event) => setForm((current) => ({ ...current, sectionCode: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]">
                          {SECTIONS.map((section) => <option key={section.code} value={section.code}>{section.title}</option>)}
                        </select>
                      </label>
                      <label className="text-sm font-semibold text-[#1f2937]">Destination
                        <input type="text" value={form.destination} onChange={(event) => setForm((current) => ({ ...current, destination: event.target.value }))} placeholder="Andaman" className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
                      </label>
                    </div>
                    <label className="block text-sm font-semibold text-[#1f2937]">Tagline
                      <input type="text" value={form.tagline} onChange={(event) => setForm((current) => ({ ...current, tagline: event.target.value }))} placeholder="Island Life Starts Here" className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
                    </label>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <label className="text-sm font-semibold text-[#1f2937]">Package Count
                        <input type="number" min="1" value={form.packageCount} onChange={(event) => setForm((current) => ({ ...current, packageCount: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
                        <p className="mt-1 text-xs font-normal text-[#6b7280]">Card badge will show this as {formatPackageCountLabel(form.packageCount)}.</p>
                      </label>
                      <label className="text-sm font-semibold text-[#1f2937]">Starts From (INR)
                        <input type="number" min="0" value={form.startsFrom} onChange={(event) => setForm((current) => ({ ...current, startsFrom: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
                      </label>
                    </div>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <label className="text-sm font-semibold text-[#1f2937]">Price Unit
                        <input type="text" value={form.priceUnit} onChange={(event) => setForm((current) => ({ ...current, priceUnit: event.target.value }))} placeholder="Per Adult" className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
                      </label>
                    </div>
                    <div className="rounded-2xl border border-[#d1d5db] p-4">
                      <p className="text-sm font-semibold text-[#1f2937]">Package Card Image</p>
                      <label className="mt-3 block text-sm font-semibold text-[#1f2937]">Upload AVIF File
                        <input key={pkgUploadKey} type="file" accept=".avif,image/avif" onChange={handlePackageUpload} className="mt-2 block w-full cursor-pointer rounded-xl border border-[#d1d5db] bg-white px-4 py-3 text-sm text-[#111827] file:mr-4 file:rounded-lg file:border-0 file:bg-[#111827] file:px-3 file:py-2 file:text-xs file:font-semibold file:text-white hover:file:bg-[#e01919]" />
                      </label>
                      {/* FIX: Updated helper text from 100KB to 1MB */}
                      <p className="mt-2 text-xs text-[#6b7280]">Allowed: <span className="font-semibold">AVIF only</span>, max size <span className="font-semibold">1MB</span>.</p>
                      <p className="mt-1 text-xs text-[#6b7280]">Recommended package card image size: <span className="font-semibold">400 x 200 px</span>.</p>
                      {editId && form.imageUrl && !form.imageFileName && (
                        <p className="mt-2 text-xs text-[#6b7280]">Current image will stay the same unless you upload a new AVIF file.</p>
                      )}
                      {form.imageFileName && <p className="mt-2 text-xs font-semibold text-[#065f46]">Selected file: {form.imageFileName}</p>}
                    </div>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <label className="block text-sm font-semibold text-[#1f2937]">WhatsApp Number
                        <input type="text" value={form.planNumber} onChange={(event) => setForm((current) => ({ ...current, planNumber: event.target.value }))} placeholder="918438506813" className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
                      </label>
                    </div>
                    <label className="block text-sm font-semibold text-[#1f2937]">WhatsApp Message
                      <textarea value={form.planMessage} onChange={(event) => setForm((current) => ({ ...current, planMessage: event.target.value }))} placeholder="Hi Travel Rethink Ways, I want to plan the Andaman package." rows={4} className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm font-semibold text-[#1f2937]">
                      <input type="checkbox" checked={form.showShare} onChange={(event) => setForm((current) => ({ ...current, showShare: event.target.checked }))} />
                      Show Share Button
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm font-semibold text-[#1f2937]">
                      <input type="checkbox" checked={form.showViewDetails !== false} onChange={(event) => setForm((current) => ({ ...current, showViewDetails: event.target.checked }))} />
                      Show View Details Button
                    </label>
                    <div className="flex flex-wrap gap-3">
                      <button type="submit" className="rounded-full bg-[#e01919] px-6 py-3 text-xs font-semibold tracking-[0.4px] uppercase text-white hover:bg-black">
                        {editId ? "Update Package" : "Save Package"}
                      </button>
                      {editId && (
                        <button type="button" onClick={() => { setEditId(null); setForm(packageBaseForm); setPkgUploadKey((value) => value + 1); }} className="rounded-full border border-black px-6 py-3 text-xs font-semibold uppercase hover:bg-black hover:text-white">
                          Cancel Edit
                        </button>
                      )}
                    </div>
                  </form>

                  {savedPkgs.length > 0 && (
                    <div className="mt-6">
                      <h3 className="mb-3 text-sm font-bold text-black">Custom Packages ({savedPkgs.length})</h3>
                      <ul className="space-y-2">
                        {savedPkgs.map((p) => (
                          <li key={p.id} className="flex items-center justify-between gap-3 rounded-xl border border-[#e5e7eb] bg-[#f9fafb] px-4 py-3">
                            <div>
                              <p className="text-sm font-semibold text-black">{p.destination}</p>
                              {p.imageFileName && <p className="text-xs text-[#6b7280]">{p.imageFileName}</p>}
                              <p className="text-xs text-[#6b7280]">{p.sectionCode} · INR {formatINR(p.startsFrom)}</p>
                            </div>
                            <div className="flex gap-2">
                              <button type="button" onClick={() => startEdit(p)} className="rounded-full border border-black px-3 py-1 text-[10px] font-semibold uppercase hover:bg-black hover:text-white">Edit</button>
                              <button type="button" onClick={() => deleteCustom(p.id)} className="rounded-full border border-[#dc2626] px-3 py-1 text-[10px] font-semibold uppercase text-[#dc2626] hover:bg-[#dc2626] hover:text-white">Delete</button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </section>

                <section className="self-start rounded-[20px] border border-[#d9d9d9] bg-white p-5 shadow-[0_20px_55px_rgba(0,0,0,0.07)]">
                  <h2 className="mb-4 text-lg font-bold text-black">Live Card Preview</h2>
                  <div className="pointer-events-none">
                    <TourMiddleCard item={packagePreview} darkMode={darkMode} isBookmarked={false} onToggle={() => {}} onShare={() => {}} onForm={() => {}} />
                  </div>
                </section>
              </div>
            )}

            {tab === "destination" && (
              <div className="space-y-6">
                <section className="rounded-[20px] border border-[#d9d9d9] bg-white p-6 shadow-[0_20px_55px_rgba(0,0,0,0.07)]">
                  <h2 className="mb-3 text-lg font-bold text-black">Destination Detail Page Manager</h2>
                  <p className="mb-5 text-sm text-[#6b7280]">This is a non-developer form. Update the title, article tags, city links, packages, attractions and FAQs directly from here.</p>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <label className="text-sm font-semibold text-[#1f2937]">Destination
                      <select
                        value={selectedDestSlug}
                        onChange={(event) => {
                          const value = event.target.value;
                          if (value === DESTINATION_DROPDOWN_ADD_VALUE) {
                            setShowNewDestinationForm(true);
                            return;
                          }
                          loadDestinationEditor(value);
                        }}
                        className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]"
                      >
                        <option value={DESTINATION_DROPDOWN_ADD_VALUE}>+ Add destination…</option>
                        {destinationOptions.map((slug) => (
                          <option key={slug} value={slug}>{getDestinationViewData(slug).title || slug}</option>
                        ))}
                      </select>
                    </label>
                    <label className="text-sm font-semibold text-[#1f2937]">Page Title
                      <input type="text" value={destForm.title} onChange={(event) => setDestForm((current) => ({ ...current, title: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
                    </label>
                  </div>

                  {showNewDestinationForm && (
                    <div className="mt-4 rounded-2xl border border-[#e5e7eb] bg-[#f9fafb] p-4">
                      <p className="text-sm font-semibold text-black">Create a new destination</p>
                      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_auto_auto] md:items-end">
                        <label className="text-sm font-semibold text-[#1f2937]">Destination Name
                          <input
                            type="text"
                            value={newDestinationName}
                            onChange={(event) => setNewDestinationName(event.target.value)}
                            placeholder="E.g., Goa"
                            className="mt-2 w-full rounded-xl border border-[#d1d5db] bg-white px-4 py-3 text-sm outline-none focus:border-[#111827]"
                          />
                        </label>
                        <button
                          type="button"
                          onClick={() => createDestinationFromName(newDestinationName)}
                          className="rounded-full bg-[#e01919] px-6 py-3 text-xs font-semibold tracking-[0.4px] uppercase text-white hover:bg-black"
                        >
                          Create
                        </button>
                        <button
                          type="button"
                          onClick={() => { setShowNewDestinationForm(false); setNewDestinationName(""); }}
                          className="rounded-full border border-black px-6 py-3 text-xs font-semibold uppercase hover:bg-black hover:text-white"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {!showNewDestinationForm && selectedDestinationData.packages.length === 0 && (
                    <div className="mt-4 rounded-2xl border border-[#fde68a] bg-[#fffbeb] p-4">
                      <p className="text-sm font-semibold text-black">No packages found for this destination.</p>
                      <p className="mt-1 text-xs text-[#6b7280]">Add packages below, or create a new destination from the dropdown.</p>
                    </div>
                  )}
                  <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                    <label className="text-sm font-semibold text-[#1f2937]">Article Tags
                      <input type="text" value={destForm.articleTags} onChange={(event) => setDestForm((current) => ({ ...current, articleTags: event.target.value }))} placeholder="andaman, island travel" className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
                    </label>
                    <label className="text-sm font-semibold text-[#1f2937]">City Links
                      <input type="text" value={destForm.cityLinks} onChange={(event) => setDestForm((current) => ({ ...current, cityLinks: event.target.value }))} placeholder="Bangalore, Mumbai, Delhi" className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
                    </label>
                  </div>
                  <label className="mt-4 inline-flex items-center gap-3 rounded-xl border border-[#e5e7eb] px-4 py-3 text-sm font-semibold text-[#1f2937]">
                    <input
                      type="checkbox"
                      checked={destForm.showViewDetails !== false}
                      onChange={(event) => setDestForm((current) => ({ ...current, showViewDetails: event.target.checked }))}
                      className="h-4 w-4 accent-[#e01919]"
                    />
                    Enable View Details button in Similar Destinations for this destination
                  </label>
                  <div className="mt-5 flex flex-wrap gap-3">
                    <button type="button" onClick={handleSaveDestination} className="rounded-full bg-[#e01919] px-6 py-3 text-xs font-semibold tracking-[0.4px] uppercase text-white hover:bg-black">Save Destination Page</button>
                    <a href={tourLink(selectedDestSlug)} className="rounded-full border border-black px-6 py-3 text-xs font-semibold uppercase text-black no-underline hover:bg-black hover:text-white">Open Detail Page</a>
                  </div>
                </section>

                <section className="rounded-[20px] border border-[#d9d9d9] bg-white p-6 shadow-[0_20px_55px_rgba(0,0,0,0.07)]">
                  <h3 className="mb-4 text-lg font-bold text-black">Detail Packages</h3>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <label className="text-sm font-semibold text-[#1f2937]">Package Title
                      <input type="text" value={detailPkgForm.title} onChange={(event) => setDetailPkgForm((current) => ({ ...current, title: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
                    </label>
                    <label className="text-sm font-semibold text-[#1f2937]">Subtitle
                      <input type="text" value={detailPkgForm.subtitle} onChange={(event) => setDetailPkgForm((current) => ({ ...current, subtitle: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
                    </label>
                    <label className="text-sm font-semibold text-[#1f2937]">Nights
                      <input type="number" min="0" value={detailPkgForm.nights} onChange={(event) => setDetailPkgForm((current) => ({ ...current, nights: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
                    </label>
                    <label className="text-sm font-semibold text-[#1f2937]">Days
                      <input type="number" min="0" value={detailPkgForm.days} onChange={(event) => setDetailPkgForm((current) => ({ ...current, days: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
                    </label>
                    <label className="text-sm font-semibold text-[#1f2937]">Starts From (INR)
                      <input type="number" min="0" value={detailPkgForm.startsFrom} onChange={(event) => setDetailPkgForm((current) => ({ ...current, startsFrom: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
                    </label>
                    <label className="text-sm font-semibold text-[#1f2937]">Price Unit
                      <input type="text" value={detailPkgForm.perAdult} onChange={(event) => setDetailPkgForm((current) => ({ ...current, perAdult: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
                    </label>
                    <label className="text-sm font-semibold text-[#1f2937]">Routes
                      <input type="text" value={detailPkgForm.routes} onChange={(event) => setDetailPkgForm((current) => ({ ...current, routes: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
                    </label>
                    <label className="text-sm font-semibold text-[#1f2937]">WhatsApp Number
                      <input type="text" value={detailPkgForm.planNumber} onChange={(event) => setDetailPkgForm((current) => ({ ...current, planNumber: event.target.value }))} placeholder="918438506813" className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
                    </label>
                    <label className="text-sm font-semibold text-[#1f2937] md:col-span-2">WhatsApp Message
                      <textarea value={detailPkgForm.planMessage} onChange={(event) => setDetailPkgForm((current) => ({ ...current, planMessage: event.target.value }))} rows={4} placeholder="Hi, I'm interested in the Andaman Starter package. Please share the full details." className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
                    </label>
                    <label className="text-sm font-semibold text-[#1f2937] md:col-span-2">Highlights
                      <input type="text" value={detailPkgForm.highlightsText} onChange={(event) => setDetailPkgForm((current) => ({ ...current, highlightsText: event.target.value }))} placeholder="Highlight 1 | Highlight 2 | Highlight 3" className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
                    </label>
                  </div>
	                  <div className="mt-4 rounded-2xl border border-[#d1d5db] p-4">
	                    <p className="text-sm font-semibold text-[#1f2937]">Package Image</p>
	                    <label className="mt-3 block text-sm font-semibold text-[#1f2937]">Image URL or Asset Path
	                      <input
	                        type="text"
	                        value={detailPkgForm.imageUrl}
	                        onChange={(event) => handleImageUrlChange(event.target.value, setDetailPkgForm)}
	                        placeholder="/assets/images/tour-packages/tour-packages/Travel-Rethinkways-Andaman.avif"
	                        className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]"
	                      />
	                    </label>
	                    <label className="mt-3 block text-sm font-semibold text-[#1f2937]">Upload AVIF File
		                      <input key={detailPkgUploadKey} type="file" accept=".avif,image/avif" onChange={(event) => handleAvifUpload(event, setDetailPkgForm, detailPkgForm.imageUrl, detailPkgForm.imageFileName)} className="mt-2 block w-full cursor-pointer rounded-xl border border-[#d1d5db] bg-white px-4 py-3 text-sm text-[#111827] file:mr-4 file:rounded-lg file:border-0 file:bg-[#111827] file:px-3 file:py-2 file:text-xs file:font-semibold file:text-white hover:file:bg-[#e01919]" />
	                    </label>
	                    <p className="mt-2 text-xs text-[#6b7280]">Allowed: <span className="font-semibold">AVIF only</span>, max size <span className="font-semibold">1MB</span>.</p>
	                    <p className="mt-1 text-xs text-[#6b7280]">For live updates, using an existing image URL or `/assets/...` path is the most reliable option.</p>
	                    {detailPkgEditId ? (
	                      <p className="mt-2 text-xs text-[#6b7280]">Current image will stay the same unless you change the URL/path or upload a new AVIF file.</p>
	                    ) : (
	                      <p className="mt-2 text-xs text-[#6b7280]">Add an image URL/path or upload an AVIF file when creating a detail package.</p>
	                    )}
	                    {detailPkgForm.imageFileName && <p className="mt-2 text-xs font-semibold text-[#065f46]">Selected file: {detailPkgForm.imageFileName}</p>}
	                  </div>
                  <div className="mt-4">
                    <p className="text-sm font-semibold text-[#1f2937]">Package Includes</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {DEFAULT_INCLUDES.map((include) => (
                        <label key={include} className="inline-flex items-center gap-2 rounded-full border border-[#d1d5db] px-3 py-2 text-xs font-semibold text-[#1f2937]">
                          <input type="checkbox" checked={detailPkgForm.includes.includes(include)} onChange={() => toggleInclude(include)} />
                          {include}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="mt-5 flex flex-wrap gap-3">
                    <button type="button" onClick={saveDetailPackage} className="rounded-full bg-[#e01919] px-6 py-3 text-xs font-semibold tracking-[0.4px] uppercase text-white hover:bg-black">{detailPkgEditId ? "Update Detail Package" : "Add Detail Package"}</button>
                    {detailPkgEditId && <button type="button" onClick={() => { setDetailPkgForm(detailPackageBaseForm); setDetailPkgEditId(""); setDetailPkgUploadKey((value) => value + 1); }} className="rounded-full border border-black px-6 py-3 text-xs font-semibold uppercase hover:bg-black hover:text-white">Cancel Edit</button>}
                  </div>
                  <ul className="mt-6 space-y-2">
                    {selectedDestinationData.packages.map((item) => (
                      <li key={item.id} className="flex items-center justify-between gap-3 rounded-xl border border-[#e5e7eb] bg-[#f9fafb] px-4 py-3">
                        <div>
                          <p className="text-sm font-semibold text-black">{item.title}</p>
                          <p className="text-xs text-[#6b7280]">{item.nights}N / {item.days}D · INR {formatINR(item.startsFrom)}</p>
                        </div>
                        <div className="flex gap-2">
                          <button type="button" onClick={() => editDetailPackage(item)} className="rounded-full border border-black px-3 py-1 text-[10px] font-semibold uppercase hover:bg-black hover:text-white">Edit</button>
                          <button type="button" onClick={() => deleteDetailPackage(item.id)} className="rounded-full border border-[#dc2626] px-3 py-1 text-[10px] font-semibold uppercase text-[#dc2626] hover:bg-[#dc2626] hover:text-white">Delete</button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>

                <section className="rounded-[20px] border border-[#d9d9d9] bg-white p-6 shadow-[0_20px_55px_rgba(0,0,0,0.07)]">
                  <h3 className="mb-4 text-lg font-bold text-black">Attractions</h3>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <label className="text-sm font-semibold text-[#1f2937]">Title
                      <input type="text" value={attractionForm.title} onChange={(event) => setAttractionForm((current) => ({ ...current, title: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
                    </label>
                    <label className="text-sm font-semibold text-[#1f2937]">Tag
                      <input type="text" value={attractionForm.tag} onChange={(event) => setAttractionForm((current) => ({ ...current, tag: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
                    </label>
                    <label className="text-sm font-semibold text-[#1f2937] md:col-span-2">Description
                      <textarea rows={3} value={attractionForm.description} onChange={(event) => setAttractionForm((current) => ({ ...current, description: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
                    </label>
                    <label className="text-sm font-semibold text-[#1f2937]">Link
                      <input type="text" value={attractionForm.link} onChange={(event) => setAttractionForm((current) => ({ ...current, link: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
                    </label>
                  </div>
	                  <div className="mt-4 rounded-2xl border border-[#d1d5db] p-4">
	                    <p className="text-sm font-semibold text-[#1f2937]">Attraction Image</p>
	                    <label className="mt-3 block text-sm font-semibold text-[#1f2937]">Image URL or Asset Path
	                      <input
	                        type="text"
	                        value={attractionForm.imageUrl}
	                        onChange={(event) => handleImageUrlChange(event.target.value, setAttractionForm)}
	                        placeholder="/assets/images/tour-packages/tour-packages/Travel-Rethinkways-Andaman-Ross-Island.avif"
	                        className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]"
	                      />
	                    </label>
	                    <label className="mt-3 block text-sm font-semibold text-[#1f2937]">Upload AVIF File
		                      <input key={attractionUploadKey} type="file" accept=".avif,image/avif" onChange={(event) => handleAvifUpload(event, setAttractionForm, attractionForm.imageUrl, attractionForm.imageFileName)} className="mt-2 block w-full cursor-pointer rounded-xl border border-[#d1d5db] bg-white px-4 py-3 text-sm text-[#111827] file:mr-4 file:rounded-lg file:border-0 file:bg-[#111827] file:px-3 file:py-2 file:text-xs file:font-semibold file:text-white hover:file:bg-[#e01919]" />
	                    </label>
	                    <p className="mt-2 text-xs text-[#6b7280]">Allowed: <span className="font-semibold">AVIF only</span>, max size <span className="font-semibold">1MB</span>.</p>
	                    <p className="mt-1 text-xs text-[#6b7280]">For live updates, using an existing image URL or `/assets/...` path is the most reliable option.</p>
	                    {attractionEditId ? (
	                      <p className="mt-2 text-xs text-[#6b7280]">Current image will stay the same unless you change the URL/path or upload a new AVIF file.</p>
	                    ) : (
	                      <p className="mt-2 text-xs text-[#6b7280]">Add an image URL/path or upload an AVIF file when creating a new attraction.</p>
	                    )}
	                    {attractionForm.imageFileName && <p className="mt-2 text-xs font-semibold text-[#065f46]">Selected file: {attractionForm.imageFileName}</p>}
	                  </div>
                  <div className="mt-5 flex flex-wrap gap-3">
                    <button type="button" onClick={saveAttraction} className="rounded-full bg-[#e01919] px-6 py-3 text-xs font-semibold tracking-[0.4px] uppercase text-white hover:bg-black">{attractionEditId ? "Update Attraction" : "Add Attraction"}</button>
                    {attractionEditId && <button type="button" onClick={() => { setAttractionForm(attractionBaseForm); setAttractionEditId(""); setAttractionUploadKey((value) => value + 1); }} className="rounded-full border border-black px-6 py-3 text-xs font-semibold uppercase hover:bg-black hover:text-white">Cancel Edit</button>}
                  </div>
                  <ul className="mt-6 space-y-2">
                    {selectedDestinationData.attractions.map((item) => (
                      <li key={item.id} className="flex items-center justify-between gap-3 rounded-xl border border-[#e5e7eb] bg-[#f9fafb] px-4 py-3">
                        <div>
                          <p className="text-sm font-semibold text-black">{item.title}</p>
                          <p className="text-xs text-[#6b7280]">{item.tag}</p>
                        </div>
                        <div className="flex gap-2">
                          <button type="button" onClick={() => editAttraction(item)} className="rounded-full border border-black px-3 py-1 text-[10px] font-semibold uppercase hover:bg-black hover:text-white">Edit</button>
                          <button type="button" onClick={() => deleteAttraction(item.id)} className="rounded-full border border-[#dc2626] px-3 py-1 text-[10px] font-semibold uppercase text-[#dc2626] hover:bg-[#dc2626] hover:text-white">Delete</button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>

                <section className="rounded-[20px] border border-[#d9d9d9] bg-white p-6 shadow-[0_20px_55px_rgba(0,0,0,0.07)]">
                  <h3 className="mb-4 text-lg font-bold text-black">FAQs</h3>
                  <div className="grid grid-cols-1 gap-4">
                    <label className="text-sm font-semibold text-[#1f2937]">Question
                      <input type="text" value={faqForm.q} onChange={(event) => setFaqForm((current) => ({ ...current, q: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
                    </label>
                    <label className="text-sm font-semibold text-[#1f2937]">Answer
                      <textarea rows={3} value={faqForm.answer} onChange={(event) => setFaqForm((current) => ({ ...current, answer: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
                    </label>
                    <label className="text-sm font-semibold text-[#1f2937]">Bullet Points
                      <textarea rows={5} value={faqForm.pointsText} onChange={(event) => setFaqForm((current) => ({ ...current, pointsText: event.target.value }))} placeholder="One point per line" className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]" />
                    </label>
                  </div>
                  <div className="mt-5 flex flex-wrap gap-3">
                    <button type="button" onClick={saveFaq} className="rounded-full bg-[#e01919] px-6 py-3 text-xs font-semibold tracking-[0.4px] uppercase text-white hover:bg-black">{faqEditId ? "Update FAQ" : "Add FAQ"}</button>
                    {faqEditId && <button type="button" onClick={() => { setFaqForm(faqBaseForm); setFaqEditId(""); }} className="rounded-full border border-black px-6 py-3 text-xs font-semibold uppercase hover:bg-black hover:text-white">Cancel Edit</button>}
                  </div>
                  <ul className="mt-6 space-y-2">
                    {selectedDestinationData.faqs.map((item) => (
                      <li key={item.id} className="flex items-center justify-between gap-3 rounded-xl border border-[#e5e7eb] bg-[#f9fafb] px-4 py-3">
                        <div>
                          <p className="text-sm font-semibold text-black">{item.q}</p>
                          <p className="text-xs text-[#6b7280]">{item.points?.length || 0} points</p>
                        </div>
                        <div className="flex gap-2">
                          <button type="button" onClick={() => editFaq(item)} className="rounded-full border border-black px-3 py-1 text-[10px] font-semibold uppercase hover:bg-black hover:text-white">Edit</button>
                          <button type="button" onClick={() => deleteFaq(item.id)} className="rounded-full border border-[#dc2626] px-3 py-1 text-[10px] font-semibold uppercase text-[#dc2626] hover:bg-[#dc2626] hover:text-white">Delete</button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              </div>
            )}

            {tab === "seo" && (
              <div className="grid grid-cols-1 items-start gap-7 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)]">
                <section className="rounded-[20px] border border-[#d9d9d9] bg-white p-6 shadow-[0_20px_55px_rgba(0,0,0,0.07)]">
                  <h2 className="mb-3 text-lg font-bold text-black">SEO Meta Manager</h2>
                  <p className="mb-5 text-sm text-[#6b7280]">Update the meta title and description for tour package listing pages and destination detail pages from here. Delete will reset the selected page back to its default SEO.</p>
                  <div className="space-y-4">
                    <label className="block text-sm font-semibold text-[#1f2937]">Page
                      <select
                        value={seoMetaForm.pageKey}
                        onChange={(event) => {
                          const selectedEntry = seoMetaEntries.find((entry) => entry.key === event.target.value);
                          setSeoMetaForm({
                            pageKey: event.target.value,
                            metaTitle: selectedEntry?.title || "",
                            metaDescription: selectedEntry?.description || "",
                          });
                        }}
                        className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]"
                      >
                        {seoMetaEntries.map((entry) => (
                          <option key={entry.key} value={entry.key}>{entry.label}</option>
                        ))}
                      </select>
                    </label>
	                    <label className="block text-sm font-semibold text-[#1f2937]">Meta Title
	                      <input
	                        type="text"
	                        maxLength={SEO_TITLE_LIMIT}
	                        value={seoMetaForm.metaTitle}
	                        onChange={(event) => setSeoMetaForm((current) => ({ ...current, metaTitle: event.target.value.slice(0, SEO_TITLE_LIMIT) }))}
	                        className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]"
	                      />
	                      <p className="mt-1 text-xs font-normal text-[#6b7280]">{seoMetaForm.metaTitle.length}/{SEO_TITLE_LIMIT}</p>
	                    </label>
	                    <label className="block text-sm font-semibold text-[#1f2937]">Meta Description
	                      <textarea
	                        rows={5}
	                        maxLength={SEO_DESCRIPTION_LIMIT}
	                        value={seoMetaForm.metaDescription}
	                        onChange={(event) => setSeoMetaForm((current) => ({ ...current, metaDescription: event.target.value.slice(0, SEO_DESCRIPTION_LIMIT) }))}
	                        className="mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]"
	                      />
	                      <p className="mt-1 text-xs font-normal text-[#6b7280]">{seoMetaForm.metaDescription.length}/{SEO_DESCRIPTION_LIMIT}</p>
	                    </label>
                    <div className="flex flex-wrap gap-3">
                      <button type="button" onClick={saveSeoMeta} className="rounded-full bg-[#e01919] px-6 py-3 text-xs font-semibold tracking-[0.4px] uppercase text-white hover:bg-black">Save SEO Meta</button>
                      <button type="button" onClick={deleteSeoMeta} className="rounded-full border border-[#dc2626] px-6 py-3 text-xs font-semibold uppercase text-[#dc2626] hover:bg-[#dc2626] hover:text-white">Delete Override</button>
                    </div>
                  </div>
                </section>

                <section className="rounded-[20px] border border-[#d9d9d9] bg-white p-6 shadow-[0_20px_55px_rgba(0,0,0,0.07)]">
                  <h3 className="mb-4 text-lg font-bold text-black">SEO Preview</h3>
                  <div className="space-y-4 rounded-2xl border border-[#e5e7eb] bg-[#f9fafb] p-5">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.35px] text-[#6b7280]">Meta Title</p>
                      <p className="mt-2 text-base font-semibold text-black">{seoMetaForm.metaTitle || "No title set"}</p>
                    </div>
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.35px] text-[#6b7280]">Meta Description</p>
                      <p className="mt-2 text-sm leading-[1.6] text-[#374151]">{seoMetaForm.metaDescription || "No description set"}</p>
                    </div>
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.35px] text-[#6b7280]">Canonical</p>
                      <p className="mt-2 break-all text-sm text-[#374151]">{seoMetaDefaultMap.get(seoMetaForm.pageKey)?.canonical || TOUR_PACKAGES_BASE_CANONICAL}</p>
                    </div>
                  </div>
                </section>
              </div>
            )}

            {tab === "superdeals" && <SuperDealsManager />}
            {tab === "homelatest" && <LatestUpdatesManager />}
            {tab === "homebanner" && <HomeBannerManager />}
          </div>
        </div>

        {/* Global notice */}
        {notice && (
          <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/45 p-4">
            <div role="alert" aria-live="assertive"
              className={`w-full max-w-[420px] rounded-2xl border px-5 py-4 shadow-[0_24px_60px_rgba(0,0,0,0.3)] ${NOTICE_CLS[notice.type] || NOTICE_CLS.info}`}>
              <p className="text-[11px] font-semibold uppercase">{notice.type}</p>
              <p className="mt-2 text-sm font-semibold">{notice.text}</p>
              <div className="mt-4 flex justify-end">
                <button type="button" onClick={() => setNotice(null)} className="rounded-full border border-current px-4 py-1.5 text-[11px] font-semibold uppercase">OK</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}

// ─── APP LAYOUT ───────────────────────────────────────────────────────────────
function AppLayout({ children, darkMode = false }) {
  return (
    <div className={`trw-app ${darkMode ? "bg-black text-white" : "bg-white text-black"}`}>
      <div id="trw-header-placeholder" />
      {children}
      <div id="trw-footer-placeholder" />
    </div>
  );
}

// ─── ROOT ROUTER ──────────────────────────────────────────────────────────────
export default function TourPackagesApp({ darkMode = false }) {
  const readRouteParams = () => {
    if (typeof window === "undefined") return {};
    const nextParams = Object.fromEntries(new URLSearchParams(window.location.search));
    const pathnameDestination = getDestinationSlugFromPathname(window.location.pathname);
    if (pathnameDestination) {
      nextParams.view = "destination";
      nextParams.destination = pathnameDestination;
    }
    return nextParams;
  };
  const [params, setParams] = useState(readRouteParams);
  const [seoMetaVersion, setSeoMetaVersion] = useState(0);
  const [dataVersion, setDataVersion] = useState(0);

  useEffect(() => {
    const update = () => setParams(readRouteParams());
    window.addEventListener("popstate", update);
    return () => window.removeEventListener("popstate", update);
  }, []);

  useEffect(() => {
    const refreshSeoMeta = () => setSeoMetaVersion((value) => value + 1);
    window.addEventListener("storage", refreshSeoMeta);
    window.addEventListener(SEO_META_UPDATED_EVENT, refreshSeoMeta);
    return () => {
      window.removeEventListener("storage", refreshSeoMeta);
      window.removeEventListener(SEO_META_UPDATED_EVENT, refreshSeoMeta);
    };
  }, []);

  useEffect(() => {
    hydrateTourPackagesFromRemote();
    startTourPackagesRemotePolling();
    const refresh = () => setDataVersion((value) => value + 1);
    window.addEventListener("storage", refresh);
    window.addEventListener("focus", refresh);
    window.addEventListener("pageshow", refresh);
    window.addEventListener(TOUR_PACKAGES_UPDATED_EVENT, refresh);
    return () => {
      window.removeEventListener("storage", refresh);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("pageshow", refresh);
      window.removeEventListener(TOUR_PACKAGES_UPDATED_EVENT, refresh);
    };
  }, []);

  const view    = params.view    || "";
  const section = params.section || "";
  const tab     = params.tab     || "";
  const dest    = params.destination || "";
  const from    = params.from || "";
  const formTab = params.formTab || "";

  const isForm      = view === "form";
  const isDest      = view === "destination";
  const isBookmarks = tab === "bookmarks";
  const resolvedSeo = useMemo(
    () => resolveTourPackagesSeo({
      view,
      section,
      destination: dest,
      packages: getAllMiddlePackages(),
      overrides: readTourSeoMetaOverrides(),
    }),
    [view, section, dest, seoMetaVersion]
  );

  return (
    <AppLayout darkMode={darkMode}>
      {!isForm && !isBookmarks && resolvedSeo && (
        <SEO
          title={resolvedSeo.title}
          description={resolvedSeo.description}
          canonical={resolvedSeo.canonical}
          image={resolvedSeo.image}
        />
      )}
      {isForm
        ? <TourPackageForm initialTab={formTab} initialDestSlug={dest} darkMode={darkMode} />
        : isDest
          ? <DestinationPage key={`dest-${dest}-${dataVersion}`} slug={dest || "andaman"} fromCity={from} darkMode={darkMode} />
          : isBookmarks
            ? <BookmarksPage key={`bookmarks-${dataVersion}`} darkMode={darkMode} />
            : <PackagesListingPage key={`listing-${section}-${dataVersion}`} sectionCode={section} darkMode={darkMode} />
      }
    </AppLayout>
  );
}
